import ResponsiveContainer from "../components/DensityResponsiveContainer";
import CsvExportButton from "../components/CsvExportButton";
import { MindshareChartSkeleton } from "./mindSHARE";
import { DashboardContent } from "../components/DashboardLoader";
import { layoutRect, layoutCoordinate, viewportHeight as layoutViewportHeight, viewportScrollY, viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import * as d3 from "d3";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Papa from "papaparse";
import { Search as SearchIcon, SlidersHorizontal, GalleryHorizontalEnd, Users, Grip, RotateCcw } from "lucide-react";
import "../components/mindshareHeaderControls.css";
import "../components/gameArtCards.css";
import "../components/dashboardTypography.css";
import MindshareHeaderControls, { MarketMindIcon } from "../components/MindshareHeaderControls";
import TwitchMarketMind from "../components/TwitchMarketMind";
import MGSignalSkeleton, { signalLoaderPreview, signalComparisonEnabled } from "../components/MGSignalSkeleton";
import { MGHeadingToggle, MGSectionHeader } from "../components/editorial/MGEditorial";
import { twitchMarketMetricLabel } from "../services/twitchMarket";
import DateSlider from "../components/DateSlider";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import SavedViewsManager from "../components/SavedViewsManager";
import { loadTwitchHistoryByGameId as loadSharedTwitchHistory } from "../services/twitchHistory";
import { loadTwitchStreamerDetails } from "../services/twitchStreamers";
import { loadTwitchCards, loadTwitchLiveStreamers, loadTwitchTablePages } from "../services/twitchCompact";
import TextInputField from "../components/Fields/TextInputField";
import { toPng } from "html-to-image";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceDot,

  Tooltip,
  XAxis,
  YAxis,
} from "recharts";


/** =========================
 *   Data sources (daily + weekly)
 *  ========================= */
// CloudFront base for Twitch data
const CF_TWITCH = "https://d1dlav2y8bte65.cloudfront.net"; // <-- your CF domain
const FILTER_BUTTON_ICON = `${process.env.PUBLIC_URL}/Filter Button.png`;

// Period-specific filter/metadata exports built from the Twitch export pipeline.
// This replaces the old browser-side full IGDB CSV load.
const FILTER_METADATA_PREFIX = `${CF_TWITCH}/Twitch_Filter_Metadata_JSON`;
const FILTER_METADATA_URLS = {
  now: `${FILTER_METADATA_PREFIX}/now/filter_metadata.json.gz`,
  "1d": `${FILTER_METADATA_PREFIX}/1d/filter_metadata.json.gz`,
  "7d": `${FILTER_METADATA_PREFIX}/7d/filter_metadata.json.gz`,
};
const filterMetadataCache = new Map();

// Period-specific precomputed language exports.
// Now is refreshed by mindgame-build-twitch-live-json; 1D/7D by twitch_daily_export.
const LANGUAGE_SUMMARY_PREFIX = `${CF_TWITCH}/Twitch_Language_JSON`;
const LANGUAGE_SUMMARY_URLS = {
  now: `${LANGUAGE_SUMMARY_PREFIX}/now/language_summary.json.gz`,
  "1d": `${LANGUAGE_SUMMARY_PREFIX}/1d/language_summary.json.gz`,
  "7d": `${LANGUAGE_SUMMARY_PREFIX}/7d/language_summary.json.gz`,
};
const languageSummaryCache = new Map();

const URLS = {
  daily: {
    game: `${CF_TWITCH}/daily_game_rollup.csv.gz`,
    streamer: "", // ❌ no longer used, 1d uses JSON only
    language: `${CF_TWITCH}/daily_language_rollup.csv.gz`,
  },
  weekly: {
    game: `${CF_TWITCH}/weekly_game_rollup.csv.gz`,
    streamer: "", // ❌ no longer used, 7d uses JSON only
    language: `${CF_TWITCH}/weekly_language_rollup.csv.gz`,
  },
};
const LIVE_LANGUAGES_URL = `${CF_TWITCH}/Twitch_Live_JSON/latest_languages.json`;

// ---------------- Twitch API base (same backend as Steam) ---------------
import { API_BASE as TWITCH_API_BASE } from "../config/api";

async function fetchJSON(pathOrUrl) {
  const isFull = /^https?:\/\//i.test(pathOrUrl);
  const url = isFull
    ? pathOrUrl
    : `${TWITCH_API_BASE}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;

  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`fetchJSON ${url} → ${res.status} ${txt}`);
  }
  return res.json();
}

async function fetchJsonMaybeGz(url) {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`fetchJsonMaybeGz ${url} → ${res.status} ${txt}`);
  }

  // If S3/CloudFront sends Content-Encoding:gzip, json() works directly.
  // If it serves the .gz as bytes, DecompressionStream handles it in modern browsers.
  try {
    return await res.clone().json();
  } catch (jsonErr) {
    if (typeof DecompressionStream !== "undefined") {
      const ds = new DecompressionStream("gzip");
      const decompressed = res.body.pipeThrough(ds);
      const text = await new Response(decompressed).text();
      return JSON.parse(text.replace(/^\uFEFF/, ""));
    }
    const text = await res.text();
    return JSON.parse(text.replace(/^\uFEFF/, ""));
  }
}

async function loadFilterMetadata(periodKey) {
  const key = periodKey === "7d" ? "7d" : periodKey === "1d" ? "1d" : "now";
  const previous = filterMetadataCache.get(key);
  if (previous && previous.until > Date.now()) return previous.data;

  const url = FILTER_METADATA_URLS[key];
  const data = await fetchJsonMaybeGz(url);
  filterMetadataCache.set(key, { data, until: Date.now() + 60000 });
  return data || {};
}

async function loadLanguageSummary(periodKey, { forceFresh = false } = {}) {
  const key = periodKey === "7d" ? "7d" : periodKey === "1d" ? "1d" : "now";

  // 1D/7D can be cached for the session. Now should be refetched whenever
  // liveRefreshNonce advances because the live Lambda rebuilds this file.
  if (key !== "now" && !forceFresh && languageSummaryCache.has(key)) {
    const previous = languageSummaryCache.get(key);
    if (previous.until > Date.now()) return previous.data;
  }

  const url = LANGUAGE_SUMMARY_URLS[key];
  const data = await fetchJsonMaybeGz(url);

  if (key !== "now") {
    languageSummaryCache.set(key, { data, until: Date.now() + 60000 });
  }

  return data || {};
}

function normalizeLanguageSummaryPayload(payload) {
  const byGameRaw = payload?.by_game || payload?.byGame || {};
  const byGameMap = new Map();

  if (byGameRaw && typeof byGameRaw === "object") {
    Object.entries(byGameRaw).forEach(([gameName, rows]) => {
      const cleanName = String(gameName || "").trim();
      if (!cleanName) return;

      const arr = (Array.isArray(rows) ? rows : [])
        .map((row) => {
          const language = canonicalLang(row?.language) || "unknown";
          const currentViewers = Number(row?.currentViewers ?? row?.current_viewers ?? row?.minutes ?? 0) || 0;
          const activeStreamers = Number(row?.activeStreamers ?? row?.active_streamers ?? 0) || 0;
          const viewMinutes = Number(row?.viewMinutes ?? row?.viewtime_minutes ?? row?.minutes ?? 0) || 0;
          const peak = Number(row?.peak ?? row?.peak_viewer_count ?? currentViewers ?? 0) || 0;

          return {
            ...row,
            language,
            currentViewers,
            activeStreamers,
            viewMinutes,
            peak,
          };
        })
        .sort((a, b) =>
          Number(b.viewMinutes || b.currentViewers || 0) -
            Number(a.viewMinutes || a.currentViewers || 0) ||
          Number(b.peak || 0) - Number(a.peak || 0)
        );

      byGameMap.set(cleanName, arr);
    });
  }

  const totalsArr = (Array.isArray(payload?.totals) ? payload.totals : [])
    .map((row) => {
      const language = canonicalLang(row?.language) || "unknown";
      const minutes = Number(row?.minutes ?? row?.viewMinutes ?? row?.currentViewers ?? 0) || 0;
      const currentViewers = Number(row?.currentViewers ?? row?.current_viewers ?? minutes ?? 0) || 0;
      const activeStreamers = Number(row?.activeStreamers ?? row?.active_streamers ?? 0) || 0;
      const peak = Number(row?.peak ?? row?.peak_viewer_count ?? currentViewers ?? 0) || 0;

      return {
        ...row,
        language,
        minutes,
        currentViewers,
        activeStreamers,
        peak,
      };
    })
    .sort((a, b) =>
      Number(b.minutes || b.currentViewers || 0) -
        Number(a.minutes || a.currentViewers || 0) ||
      Number(b.peak || 0) - Number(a.peak || 0)
    );

  return { byGameMap, totalsArr };
}


function getHistoryPointKey(pt) {
  return String(
    pt?.dt ||
      pt?.date ||
      pt?.month ||
      pt?.month_start ||
      pt?.period ||
      ""
  ).trim();
}

function toMonthKeyFromDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function normalizeMonthKey(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : "";
}

function parseHistoryDate(raw) {
  const s = String(raw || "").trim();
  const day = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  const month = s.match(/^(\d{4})-(\d{2})$/);
  if (month) return new Date(Number(month[1]), Number(month[2]) - 1, 1);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getHistoryMinutes(pt) {
  return Number(
    pt?.total_minutes ??
      pt?.viewtime_minutes ??
      pt?.minutes ??
      (pt?.hours_watched != null ? Number(pt.hours_watched) * 60 : 0)
  ) || 0;
}

function daysInHistoryMonth(dateObj) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return 30;
  return new Date(dateObj.getFullYear(), dateObj.getMonth() + 1, 0).getDate();
}

function previousMonthKey(monthKey) {
  const m = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function appendCurrentMonthEstimateToMonthlyHistory(monthlyHistory, dailyHistory) {
  const monthly = Array.isArray(monthlyHistory) ? monthlyHistory.slice() : [];
  const daily = Array.isArray(dailyHistory) ? dailyHistory.slice() : [];
  if (!daily.length) return monthly;

  const latestDaily = daily
    .map((pt) => ({ pt, d: parseHistoryDate(getHistoryPointKey(pt)) }))
    .filter((x) => x.d)
    .sort((a, b) => a.d - b.d)
    .pop();

  if (!latestDaily?.d) return monthly;

  const currentMonthKey = toMonthKeyFromDate(latestDaily.d);
  if (!currentMonthKey) return monthly;

  // Only estimate the active calendar month. If the newest daily data is from a
  // closed prior month, leave the monthly history alone.
  const now = new Date();
  if (currentMonthKey !== toMonthKeyFromDate(now)) return monthly;

  const currentMonthDaily = daily.filter((pt) => {
    const d = parseHistoryDate(getHistoryPointKey(pt));
    return d && toMonthKeyFromDate(d) === currentMonthKey;
  });

  const elapsedDays = new Set(
    currentMonthDaily
      .map((pt) => {
        const d = parseHistoryDate(getHistoryPointKey(pt));
        return d ? `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}` : "";
      })
      .filter(Boolean)
  ).size;

  if (!elapsedDays) return monthly;

  const actualMinutesSoFar = currentMonthDaily.reduce(
    (sum, pt) => sum + getHistoryMinutes(pt),
    0
  );

  if (!actualMinutesSoFar) return monthly;

  const monthDays = daysInHistoryMonth(latestDaily.d);

  // If the month is already complete in the daily data, use the final actual
  // value as the month instead of drawing an estimate segment.
  if (elapsedDays >= monthDays) {
    const closedMonthly = monthly.filter(
      (pt) => normalizeMonthKey(getHistoryPointKey(pt)) !== currentMonthKey
    );
    return [
      ...closedMonthly,
      {
        dt: currentMonthKey,
        month: currentMonthKey,
        total_minutes: actualMinutesSoFar,
        viewtime_minutes: actualMinutesSoFar,
        minutes: actualMinutesSoFar,
        is_current_month_actual: true,
      },
    ].sort((a, b) => {
      const ad = parseHistoryDate(getHistoryPointKey(a));
      const bd = parseHistoryDate(getHistoryPointKey(b));
      return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
    });
  }

  const estimatedMinutes = (actualMinutesSoFar / elapsedDays) * monthDays;

  const closedMonthly = monthly.filter(
    (pt) => normalizeMonthKey(getHistoryPointKey(pt)) !== currentMonthKey
  );

  // Brand-new games can have only a current-month estimate and no prior closed
  // monthly point. Add a zero anchor in the previous month so the estimate does
  // not render as a floating dot. Existing games keep their prior actual month.
  const hasPriorActual = closedMonthly.some((pt) => {
    const key = normalizeMonthKey(getHistoryPointKey(pt));
    return key && key < currentMonthKey && getHistoryMinutes(pt) > 0;
  });
  const zeroAnchor = hasPriorActual
    ? []
    : [{
        dt: previousMonthKey(currentMonthKey),
        month: previousMonthKey(currentMonthKey),
        total_minutes: 0,
        viewtime_minutes: 0,
        minutes: 0,
        is_zero_anchor: true,
      }];

  return [
    ...closedMonthly,
    ...zeroAnchor,
    {
      dt: currentMonthKey,
      month: currentMonthKey,
      total_minutes: estimatedMinutes,
      viewtime_minutes: estimatedMinutes,
      minutes: estimatedMinutes,
      estimated_minutes: estimatedMinutes,
      actual_minutes_so_far: actualMinutesSoFar,
      elapsed_days: elapsedDays,
      days_in_month: monthDays,
      estimate_is_low_confidence: elapsedDays < 10,
      is_current_month_estimate: true,
    },
  ].sort((a, b) => {
    const ad = parseHistoryDate(getHistoryPointKey(a));
    const bd = parseHistoryDate(getHistoryPointKey(b));
    return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
  });
}

const arrify = (v) => (Array.isArray(v) ? v : []);

function optionName(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v).trim();
  return String(v.value ?? v.name ?? v.label ?? v.key ?? "").trim();
}

function optionCount(v) {
  if (!v || typeof v !== "object") return 0;
  return Number(v.count ?? v.games ?? v.game_count ?? 0) || 0;
}

function optionMinutes(v) {
  if (!v || typeof v !== "object") return 0;
  return Number(v.minutes ?? v.viewtime_minutes ?? v.total_minutes ?? 0) || 0;
}

function normalizeFilterOptionArray(raw, withCounts = false) {
  const seen = new Map();

  arrify(raw).forEach((item) => {
    const name = optionName(item);
    if (!name) return;

    const prev = seen.get(name) || { name, count: 0, minutes: 0 };
    prev.count += optionCount(item);
    prev.minutes += optionMinutes(item);
    if (!prev.count && withCounts && typeof item === "string") prev.count += 1;
    seen.set(name, prev);
  });

  return Array.from(seen.values()).sort((a, b) => {
    if ((b.minutes || 0) !== (a.minutes || 0)) return (b.minutes || 0) - (a.minutes || 0);
    if ((b.count || 0) !== (a.count || 0)) return (b.count || 0) - (a.count || 0);
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

function getFilterBucket(filters, ...keys) {
  for (const k of keys) {
    if (Array.isArray(filters?.[k])) return filters[k];
  }
  return [];
}

function addMetaToMaps(metaMap, twitchMetaMap, key, meta) {
  if (!meta || typeof meta !== "object") return;

  const igdbId = normalizeIgdbId(
    meta.igdb_id ?? meta.igdbId ?? meta.IGDB_ID ?? meta.id_igdb ?? ""
  );
  const twitchId = String(
    meta.twitch_game_id ??
      meta.twitch_id ??
      meta.game_id ??
      meta.id ??
      key ??
      ""
  ).trim();

  if (igdbId && !metaMap.has(igdbId)) metaMap.set(igdbId, meta);
  if (twitchId && !twitchMetaMap.has(twitchId)) twitchMetaMap.set(twitchId, meta);
}

function normalizeFilterMetadataPayload(payload) {
  const filters = payload?.filters || payload?.filter_options || payload || {};
  const metaMap = new Map();        // igdb_id -> meta
  const twitchMetaMap = new Map();  // twitch game_id -> meta

  const gamesPayload =
    payload?.games ||
    payload?.game_metadata ||
    payload?.metadata ||
    payload?.games_by_twitch_id ||
    payload?.gamesByTwitchId ||
    [];

  if (Array.isArray(gamesPayload)) {
    gamesPayload.forEach((meta, idx) => addMetaToMaps(metaMap, twitchMetaMap, idx, meta));
  } else if (gamesPayload && typeof gamesPayload === "object") {
    Object.entries(gamesPayload).forEach(([key, meta]) => {
      if (meta && typeof meta === "object") {
        addMetaToMaps(metaMap, twitchMetaMap, key, { ...meta, twitch_game_id: meta.twitch_game_id ?? meta.game_id ?? key });
      }
    });
  }

  // Also support compact maps like { meta_by_igdb_id: { "123": {...} } }
  const byIgdb = payload?.meta_by_igdb_id || payload?.metadata_by_igdb_id || payload?.by_igdb_id;
  if (byIgdb && typeof byIgdb === "object") {
    Object.entries(byIgdb).forEach(([key, meta]) => {
      if (meta && typeof meta === "object") addMetaToMaps(metaMap, twitchMetaMap, key, { ...meta, igdb_id: meta.igdb_id ?? key });
    });
  }

  const byTwitch = payload?.meta_by_twitch_id || payload?.metadata_by_twitch_id || payload?.by_twitch_id;
  if (byTwitch && typeof byTwitch === "object") {
    Object.entries(byTwitch).forEach(([key, meta]) => {
      if (meta && typeof meta === "object") addMetaToMaps(metaMap, twitchMetaMap, key, { ...meta, twitch_game_id: meta.twitch_game_id ?? meta.game_id ?? key });
    });
  }

  const publisherStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "publishers", "publisher", "publisher_options")
  );
  const developerStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "developers", "developer", "developer_options")
  );
  const genreStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "genres", "genre", "genre_options")
  );
  const platformStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "platforms", "platform", "platform_options")
  );
  const keywordStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "keywords", "keyword", "keyword_options")
  );
  const franchiseStats = normalizeFilterOptionArray(
    getFilterBucket(filters, "franchises", "franchise", "franchise_options"),
    true
  );

  const range = filters.release_date_range || filters.date_range || payload?.release_date_range || {};
  const minDate = safeDate(
    range.min ||
      range.start ||
      range.from ||
      filters.release_date_min ||
      filters.min_release_date ||
      payload?.release_date_min ||
      payload?.min_release_date
  );
  const maxDate = safeDate(
    range.max ||
      range.end ||
      range.to ||
      filters.release_date_max ||
      filters.max_release_date ||
      payload?.release_date_max ||
      payload?.max_release_date
  );

  return {
    metaMap,
    twitchMetaMap,
    publisherStats,
    developerStats,
    genreStats,
    platformStats,
    keywordStats,
    franchiseStats,
    minDate,
    maxDate,
  };
}

function getGameMeta(game, metaById, metaByTwitchId) {
  if (!game) return null;
  const twitchId = String(game.id || game.gameId || game.game_id || "").trim();
  if (twitchId && metaByTwitchId?.has(twitchId)) return metaByTwitchId.get(twitchId);

  const igdbId = normalizeIgdbId(game.igdbId || game.igdb_id || "");
  if (igdbId && metaById?.has(igdbId)) return metaById.get(igdbId);

  return game.releaseDate ? { first_release_date: game.releaseDate } : null;
}

async function loadTwitchChartHistoryByGameId(gameId, granularity = "daily") {
  if (!gameId) return [];

  const isMonthly = granularity === "monthly";
  const history = await loadSharedTwitchHistory(gameId, granularity);
  if (!history.length) {
    // Brand-new releases may not exist in the monthly export yet because they do
    // not have a closed month. Still show them in Monthly History by estimating
    // the current month from the daily JSON.
    if (isMonthly) {
      try {
        const dailyHistory = await loadTwitchChartHistoryByGameId(gameId, "daily");
        const estimatedOnly = appendCurrentMonthEstimateToMonthlyHistory([], dailyHistory);
        if (estimatedOnly.length) return estimatedOnly;
      } catch (e) {
        console.warn("Twitch monthly new-release estimate fallback failed", e);
      }
    }

    console.warn(`No Twitch ${isMonthly ? "monthly" : "daily"} history found for game_id`, gameId);
    return [];
  }

  if (isMonthly) {
    try {
      const dailyHistory = await loadTwitchChartHistoryByGameId(gameId, "daily");
      return appendCurrentMonthEstimateToMonthlyHistory(history, dailyHistory);
    } catch (e) {
      console.warn("Twitch monthly current-month estimate failed", e);
    }
  }

  return history;
}

async function loadTwitchHistoryByGameId(gameId) {
  return loadTwitchChartHistoryByGameId(gameId, "daily");
}

/* ---------- helpers ---------- */

function useViewportSize() {
  const getSize = () => ({
    width: typeof window !== "undefined" ? layoutViewportWidth() : 1440,
    height: typeof window !== "undefined" ? layoutViewportHeight() : 900,
  });

  const [size, setSize] = useState(getSize);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let frame = null;
    const onResize = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => setSize(getSize()));
    };

    window.addEventListener("resize", onResize);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return size;
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}


const norm = (s = "") =>
  s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").trim();

function normalizeGameName(raw = "") {
  return String(raw || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // accents
    .replace(/[\u2122\u00ae]/g, "") // ™, ®
    .replace(/[^a-z0-9]+/g, " ") // punctuation → space
    .replace(/\s+/g, " ")
    .trim();
}


function getStreamerTopGames(s) {
  if (Array.isArray(s?.top_games)) return s.top_games;
  return [];
}

function getStreamerAvatarUrl(row) {
  return String(
    row?.profile_image_url ||
      row?.profileImageUrl ||
      row?.profile_image ||
      row?.user_profile_image_url ||
      row?.avatar_url ||
      row?.thumbnail_url ||
      ""
  ).trim();
}

function buildStreamerAvatarLookup(...rowGroups) {
  const byId = new Map();
  const byName = new Map();

  const addRow = (row) => {
    if (!row) return;
    const url = getStreamerAvatarUrl(row);
    if (!url) return;

    const id = String(row.user_id || row.id || "").trim();
    const name = String(row.user_name || row.display_name || row.login || "").trim().toLowerCase();

    if (id && !byId.has(id)) byId.set(id, url);
    if (name && !byName.has(name)) byName.set(name, url);
  };

  rowGroups.forEach((rows) => {
    if (Array.isArray(rows)) rows.forEach(addRow);
  });

  return { byId, byName };
}

function hydrateStreamerAvatar(row, avatarLookup) {
  const direct = getStreamerAvatarUrl(row);
  if (direct) return { ...row, profile_image_url: direct };

  const id = String(row?.user_id || row?.id || "").trim();
  const name = String(row?.user_name || row?.display_name || row?.login || "").trim().toLowerCase();
  const fallback =
    (id && avatarLookup?.byId?.get(id)) ||
    (name && avatarLookup?.byName?.get(name)) ||
    "";

  return fallback ? { ...row, profile_image_url: fallback } : row;
}

function getTopGameName(s) {
  return (
    s?.top_game_name ||
    s?.topGameName ||
    getStreamerTopGames(s)?.[0]?.game_name ||
    getStreamerTopGames(s)?.[0]?.name ||
    ""
  );
}

function getStreamerRowHours(row) {
  const direct =
    row?.hours_watched ??
    row?.hours ??
    row?.total_hours ??
    row?.viewtime_hours;
  if (direct != null && direct !== "") return Number(direct) || 0;

  const minutes =
    row?.viewtime_minutes ??
    row?.view_minutes ??
    row?.viewMinutes ??
    row?.total_minutes;
  if (minutes != null && minutes !== "") return (Number(minutes) || 0) / 60;

  return 0;
}

function getStreamerRowPeak(row) {
  return Number(
    row?.peak_viewers ??
      row?.peak ??
      row?.peak_viewer_count ??
      row?.viewer_count ??
      0
  ) || 0;
}

function getStreamerGameName(row) {
  return String(
    row?.game_name ||
      row?.top_game_name ||
      row?.name ||
      ""
  ).trim();
}

function getStreamerGameArt(row, gameArtByName) {
  const direct =
    row?.box_art_url ||
    row?.game_box_art_url ||
    row?.top_game_box_art_url ||
    row?.image_url ||
    "";
  if (direct) return normalizeBoxArt(direct);

  const gameName = getStreamerGameName(row);
  if (gameName && gameArtByName?.get) {
    return gameArtByName.get(normalizeGameName(gameName)) || "";
  }
  return "";
}

function getTopGameArt(s, gameArtByName) {
  const topName = getTopGameName(s);
  const direct =
    s?.top_game_box_art_url ||
    s?.top_game_image_url ||
    s?.top_game_art ||
    getStreamerTopGames(s)?.[0]?.box_art_url ||
    getStreamerTopGames(s)?.[0]?.image_url ||
    "";

  if (direct) return normalizeBoxArt(direct);
  if (topName && gameArtByName?.get) {
    return gameArtByName.get(normalizeGameName(topName)) || "";
  }
  return "";
}

// 🔹 filename-safe text (Windows/macOS safe)
function safeFilePart(s, fallback = "Unknown") {
  const t = String(s || "").trim();
  if (!t) return fallback;
  return t
    .replace(/[\\/:*?"<>|]/g, "")  // illegal filename chars
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}


// plain text of .csv(.gz)
async function fetchCsvText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    const msg = `[fetchCsvText] ${url} → HTTP ${res.status}`;
    console.error(msg);
    throw new Error(msg);
  }
  const text = await res.text(); // browser auto-decompresses gzip
  const clean = text.replace(/^\uFEFF/, "");

  // loud debug only for streamer CSVs (kept for history)
  if (url.includes("weekly_streamer_rollup")) {
    console.log(
      "[fetchCsvText weekly_streamer]",
      "url:",
      url,
      "length:",
      clean.length,
      "sample:",
      clean.slice(0, 200)
    );
  } else if (url.includes("daily_streamer_rollup")) {
    console.log("[fetchCsvText daily_streamer]", "length:", clean.length);
  }

  return clean;
}

// iterate CSV lines
async function* iterateCsvLinesGz(url) {
  const text = await fetchCsvText(url);
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (line) yield line;
  }
}

// CSV row → object using header
function rowToObj(header, row) {
  const cells = [];
  let inQ = false,
    cur = "";
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '"') {
      if (row[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQ = !inQ;
      }
      continue;
    }
    if (ch === "," && !inQ) {
      cells.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  cells.push(cur);
  const out = {};
  for (let k = 0; k < header.length; k++)
    out[header[k]] = (cells[k] ?? "").trim();
  return out;
}

function parseCSV(text) {
  const rows = [];
  let i = 0,
    field = "",
    row = [],
    inQuotes = false;
  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        } else {
          inQuotes = false;
          i++;
          continue;
        }
      } else {
        field += c;
        i++;
        continue;
      }
    } else {
      if (c === '"') {
        inQuotes = true;
        i++;
        continue;
      }
      if (c === ",") {
        pushField();
        i++;
        continue;
      }
      if (c === "\r") {
        i++;
        continue;
      }
      if (c === "\n") {
        pushField();
        pushRow();
        i++;
        continue;
      }
      field += c;
      i++;
    }
  }
  pushField();
  if (row.length > 1 || (row.length === 1 && row[0] !== "")) pushRow();

  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const o = {};
    header.forEach((h, idx) => (o[h] = (r[idx] ?? "").trim()));
    return o;
  });
}

function normalizeBoxArt(url) {
  if (!url) return "";
  return url.replace(/-\{width\}x\{height\}/g, "");
}

/** minutes → hours **/
const toHours = (m) => (m || 0) / 60;
const fmtHours = (h) =>
  (h >= 10 ? Math.round(h) : Math.round(h * 10) / 10).toLocaleString();
const fmtInt = (v) =>
  new Intl.NumberFormat("en-US").format(Math.round(Number(v || 0)));


/** language names **/
const LANG_NAMES = {
  en: "English",
  da: "Danish",
  ru: "Russian",
  sv: "Swedish",
  fi: "Finnish",
  tr: "Turkish",
  pl: "Polish",
  no: "Norwegian",
  es: "Spanish",
  uk: "Ukrainian",
  other: "Other",
  th: "Thai",
  it: "Italian",
  fr: "French",
  el: "Greek",
  pt: "Portuguese",
  de: "German",
  ja: "Japanese",
  nl: "Dutch",
  ar: "Arabic",
  hi: "Hindi",
  hu: "Hungarian",
  asl: "ASL",
  ko: "Korean",
  ro: "Romanian",
  zh: "Chinese",
  bg: "Bulgarian",
  cs: "Czech",
  tl: "Tagalog",
  ms: "Malay",
  sk: "Slovak",
  vi: "Vietnamese",
  ca: "Catalan",
  id: "Indonesian",
  "zh-cn": "Chinese (Simplified)",
  "zh-tw": "Chinese (Traditional)",
  "zh-hk": "Chinese (HK)",
  unknown: "Unknown",
};
const langLabel = (code) => {
  const c = (code || "").toLowerCase();
  return (
    LANG_NAMES[c] || LANG_NAMES[c.split("-")[0]] || (code ? code.toUpperCase() : "UNKNOWN")
  );
};

/** canonicalize variants **/
function canonicalLang(raw = "") {
  const c = String(raw || "").trim().toLowerCase();
  if (!c) return "";
  const base = c.split(/[-_]/)[0];
  const synonyms = { "zh-hk": "zh", fil: "tl" };
  return synonyms[c] || synonyms[base] || base;
}

// 🔹 simple CSV splitter for multi-value IGDB fields
function splitMulti(raw = "") {
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// 🔹 safe Date helper
function safeDate(dLike) {
  if (!dLike) return null;
  const d = new Date(dLike);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Released today or in the previous 6 days, based on local calendar date.
function isReleasedInLast7Days(dLike) {
  const d = safeDate(dLike);
  if (!d) return false;

  const releaseDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const todayDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const startDay = new Date(todayDay);
  startDay.setDate(todayDay.getDate() - 6);

  return releaseDay >= startDay && releaseDay <= todayDay;
}


// 🔹 Cache language aggregates per language CSV URL (daily / weekly)
const languageIndexCache = new Map(); // key: language CSV URL, value: { byGameMap, totalsArr }


// normalize igdb_id from either CSV (handles "52689.0" vs "52689")
function normalizeIgdbId(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  // If it's numeric-like, coerce to integer string
  const num = Number(s);
  if (Number.isFinite(num)) {
    return String(Math.trunc(num));
  }
  return s;
}


/* ---------- Cards ---------- */

const TwitchStatIconWrap = ({ children }) => (
  <span
    style={{
      width: 19,
      minWidth: 19,
      height: 19,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9fb7c9",
      transform: "translateY(0.5px)",
    }}
  >
    {children}
  </span>
);

const EyeIcon = ({ size = 17, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M2.8 12s3.45-5.7 9.2-5.7 9.2 5.7 9.2 5.7-3.45 5.7-9.2 5.7S2.8 12 2.8 12Z"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.05" stroke={color} strokeWidth="1.9" />
  </svg>
);

const StreamerIcon = ({ size = 17, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="4.25"
      y="7.25"
      width="10.5"
      height="9.5"
      rx="2"
      stroke={color}
      strokeWidth="1.9"
    />
    <path
      d="M14.75 10.25 20 7.85v8.3l-5.25-2.4v-3.5Z"
      stroke={color}
      strokeWidth="1.9"
      strokeLinejoin="round"
    />
  </svg>
);

const HoursIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.9" />
    <path
      d="M12 7.4V12l3.35 2.05"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PeakIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 17.5 9.1 12.4l3.2 3.2L20 7.9"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.3 7.9H20v4.7"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TwitchStatLine = ({ icon, children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      minHeight: 18,
      lineHeight: "18px",
    }}
  >
    <TwitchStatIconWrap>{icon}</TwitchStatIconWrap>
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 4,
        minWidth: 0,
      }}
    >
      {children}
    </span>
  </div>
);


// --- Steam-style packed bubble skeleton (Market Mind) ---
const SteamBubbleSkeleton = ({ width = 800, height = 800, count = 220 }) => {
  // Make sure we get a square area so it looks like a disk
  const size = Math.max(200, Math.min(width, height));
  const SAFE_INSET = size * 0.06;
  const S = size - SAFE_INSET * 2;
  const offsetX = (width - S) / 2;
  const offsetY = (height - S) / 2;

  // Build nodes once for this width/height/count
  const nodes = useMemo(() => {
    // 1) Generate “weights” with a wide distribution so radii vary a lot
    const children = d3.range(count).map(() => {
      const r = Math.random();
      let exp;

      // a few giants in the middle
      if (r < 0.04) {
        exp = 8 + Math.random() * 2; // biggest
      }
      // some big mid-tier bubbles
      else if (r < 0.18) {
        exp = 5 + Math.random() * 2;
      }
      // chunky medium group
      else if (r < 0.45) {
        exp = 3 + Math.random() * 2;
      }
      // lots of small ones
      else {
        exp = 0 + Math.random() * 2;
      }

      return { value: Math.pow(1.5, exp) };
    });

    const root = d3.hierarchy({ children }).sum((d) => d.value);
    d3.pack().size([S, S]).padding(2.5)(root);

    // 2) Turn leaves into renderable nodes with per-circle animation
    return root.leaves().map((leaf, i) => {
      // tiny random radius jitter so the disk edge isn’t perfectly smooth
      const jitter = 0.92 + Math.random() * 0.22;
      const cx = leaf.x + offsetX;
      const cy = leaf.y + offsetY;

      // random pulse timing per bubble
      const delay = Math.random() * 1.8;          // 0–1.8s
      const duration = 1.2 + Math.random() * 0.9; // 1.2–2.1s
      const opacity = 0.30 + Math.random() * 0.28; // 0.30–0.58

      return {
        id: i,
        cx,
        cy,
        r: leaf.r * jitter,
        opacity,
        animation: `ms-skeleton-pulse ${duration.toFixed(
          2
        )}s ease-in-out ${delay.toFixed(2)}s infinite`,
      };
    });
  }, [S, offsetX, offsetY, count]);

  return (
    <svg
      className="ms-bubble-skeleton"
      width={width}
      height={height}
      aria-label="Loading"
    >
      {nodes.map((n) => (
        <circle
          key={n.id}
          cx={n.cx}
          cy={n.cy}
          r={n.r}
          fill="#1d2730"
          opacity={n.opacity}
          style={{
            // per-bubble pulse: same keyframes, different timing
            animation: n.animation,
            transformOrigin: `${n.cx}px ${n.cy}px`,
          }}
        />
      ))}
    </svg>
  );
};




function TwitchCard({
  game,
  index,
  onClick,
  onRemove,
  selectedLang,
  inChart,
  onToggleChart,
  isNow = false,
  isNewRelease = false,
  loaderMode = "baseline",
}) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [settledImage, setSettledImage] = useState(null);
  const [failedImage, setFailedImage] = useState(null);
  const signalPending = loaderMode !== "baseline" && !!game.imageUrl && settledImage !== game.imageUrl;
  const footerFontSize = 12;
  const badgeFontSize = 12;
  const compareButtonSize = 22;

  const hours = toHours(game.displayMinutes);
  const langSuffix = selectedLang ? ` (${langLabel(selectedLang)})` : "";
  const currentViewers = Number(game.displayCurrentViewers || 0);
  const activeStreamers = Number(game.displayStreamers || 0);
  const showNowLabels = index === 0;
  const showCardLabels = index === 0;

  const handleMouseEnter = () => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

 return (
    <div
      style={{ position: "relative", width: "100%", minWidth: 0, margin: 0 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick?.(game)}
    >
      <style>{`
        /* Twitch-purple hover accent, scoped to this page only. Overrides the
           shared teal/coral card accent; new-release gold glow is untouched.
           The corner bracket stays the neutral border color at rest and only
           turns purple on hover. */
        .mg-twitch-page .mg-game-art-card {
          --mg-card-accent: 145, 70, 255;
          --mg-card-bracket: rgba(207, 216, 227, 0.45);
        }
        [data-card].twitch-new-release-card {
          box-shadow:
            0 0 0 2px rgba(255,199,44,0.82),
            0 0 30px rgba(255,199,44,0.55),
            0 8px 24px rgba(0,0,0,0.75);
        }
        [data-card].twitch-new-release-card:hover {
          box-shadow:
            0 0 0 2px rgba(255,218,82,0.98),
            0 0 36px rgba(255,199,44,0.68),
            0 18px 34px rgba(0,0,0,0.75);
        }

      `}</style>

      <div
        data-card
        className={[
          "mg-game-art-card",
          isNewRelease ? "twitch-new-release-card" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        title={isNewRelease ? "New Release" : undefined}
        style={{
          position: "relative",
          background: "#111",
          overflow: "hidden",
          boxShadow: isNewRelease
            ? "0 0 0 2px rgba(255,199,44,0.82), 0 0 30px rgba(255,199,44,0.55), 0 8px 24px rgba(0,0,0,0.75)"
            : undefined,
        }}
      >

        {/* IMAGE BLOCK */}
        <div className="mg-game-art-media">
          {signalPending && <MGSignalSkeleton animate={index < 4} />}
          {loaderMode !== "baseline" && (!game.imageUrl || failedImage === game.imageUrl) &&
            <div className="mg-signal-missing">Cover unavailable</div>}
          {game.imageUrl ? (
            <img
              key={game.imageUrl || game.key || game.id || game.name}
              src={game.imageUrl}
              alt={game.name}
              className={loaderMode !== "baseline" ? "mg-signal-cover-image" : undefined}
              data-signal-ready={loaderMode !== "baseline" ? !signalPending : undefined}
              ref={image => {
                if (loaderMode !== "baseline" && image?.complete && image.naturalWidth > 0 && settledImage !== game.imageUrl) setSettledImage(game.imageUrl);
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                filter:
                  hoveredIndex === index ? "brightness(1.10)" : "brightness(1)",
                willChange: "filter",
              }}
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              onLoad={loaderMode !== "baseline" ? () => setSettledImage(game.imageUrl) : undefined}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                if (loaderMode !== "baseline") {
                  setFailedImage(game.imageUrl);
                  setSettledImage(game.imageUrl);
                }
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(135deg, #1a1a1a 0%, #222 50%, #1a1a1a 100%)",
              }}
            />
          )}

          {/* Rank badge – small rounded rectangle like mindSHARE */}
          <div
            className="mg-game-art-rank"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "auto",
              minWidth: 24,
              boxSizing: "border-box",
              whiteSpace: "nowrap",
              height: 22,
              color: "#fff",
              fontSize: badgeFontSize,
              padding: "0 5px",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              lineHeight: "22px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            #{index + 1}
          </div>

          {/* Remove button – small rounded rect like mindSHARE */}
          <button
            className="mg-game-art-remove"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove?.(game.name);
            }}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              color: "#fff",
              fontSize: badgeFontSize,
              padding: 0,
              border: "none",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              cursor: "pointer",
              zIndex: 11,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Remove game"
          >
            ×
          </button>
        </div>

          {/* FOOTER: overlay stats band */}
          <div
            className="mg-game-art-footer"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "grid",
              gridTemplateColumns: `minmax(0, 1fr) ${compareButtonSize}px`,
              background:
                "linear-gradient(to top, rgba(0,0,0,0.84) 0%, rgba(0,0,0,0.76) 48%, rgba(0,0,0,0.0) 100%)",
              pointerEvents: "auto",
            }}
          >
            {/* Left: Hours + Peak / Viewers + Streamers */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                fontSize: footerFontSize,
                color: "#cfd8e3",
                minWidth: 0,
                overflow: "hidden",
                textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              }}
            >
              {isNow ? (
                <>
                  <TwitchStatLine icon={<EyeIcon />}>
                    <span style={{ fontWeight: 800, color: "#fff" }}>
                      {currentViewers.toLocaleString()}
                    </span>
                    {showNowLabels && <span>Viewers</span>}
                  </TwitchStatLine>

                  <TwitchStatLine icon={<StreamerIcon />}>
                    <span style={{ fontWeight: 800, color: "#fff" }}>
                      {activeStreamers.toLocaleString()}
                    </span>
                    {showNowLabels && <span>Streamers</span>}
                  </TwitchStatLine>
                </>
              ) : (
                <>
                  <TwitchStatLine icon={<HoursIcon />}>
                    <span style={{ fontWeight: 800, color: "#fff" }}>
                      {fmtHours(hours)}
                    </span>
                    {showCardLabels && <span>Hours{langSuffix}</span>}
                  </TwitchStatLine>

                  <TwitchStatLine icon={<PeakIcon />}>
                    <span style={{ fontWeight: 800, color: "#fff" }}>
                      {game.displayPeak.toLocaleString()}
                    </span>
                    {showCardLabels && <span>Peak</span>}
                  </TwitchStatLine>
                </>
              )}
            </div>

            {/* Right: + compare button */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                position: "absolute",
                right: 0,
                bottom: 0,
                width: compareButtonSize,
                height: compareButtonSize,
              }}
            >
              <button
                className="mg-game-art-add"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleChart?.(game);
                }}
                style={{
                  width: compareButtonSize,
                  height: compareButtonSize,
                  border: "none",
                  color: "#fff",
                  fontSize: 15,
                  lineHeight: "15px",
                  fontWeight: 900,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s ease, border-color 0.15s ease",
                  boxShadow: "none",
                }}
              >
                {inChart ? "✓" : "+"}
              </button>
            </div>
          </div>
        </div>

      </div>
  );
}


export function TwitchCardGrid({
  games,
  loading,
  onCardClick,
  onRemove,
  selectedLang,
  onToggleChart,
  chartGamesSet,
  isNow = false,
  previewMode,
}) {
  const loaderMode = previewMode || signalLoaderPreview();
  const INITIAL = 60;
  const BATCH = 60;

  const [showCount, setShowCount] = useState(INITIAL);
  useEffect(() => setShowCount(INITIAL), [games]);
  useEffect(() => {
    const onScroll = () => {
      if (
        layoutViewportHeight() + viewportScrollY() + 600 >=
        document.documentElement.offsetHeight
      ) {
        setShowCount((n) => Math.min(n + BATCH, games.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [games.length]);

  const showSkeleton = loading || games.length === 0;
  const slice = showSkeleton ? [] : games.slice(0, showCount);
  const SKELETON_COUNT = 60;

  return (
    <div
      className="mg-game-art-grid"
    >
      {showSkeleton && (
        <style>{`
          @keyframes mgTwitchSkeletonFlip {
            0% { transform: rotateX(90deg); opacity: 0; }
            40% { transform: rotateX(-15deg); opacity: 1; }
            100% { transform: rotateX(0deg); opacity: 1; }
          }
          .mg-twitch-skel-card {
            position: relative;
            width: 100%;
            background: linear-gradient(
              135deg,
              #0d0d0d 0%,
              #141414 50%,
              #0d0d0d 100%
            );
            overflow: hidden;
            transform-origin: top center;
            will-change: transform, opacity;
          }
        `}</style>
      )}

      {showSkeleton
        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div
              key={i}
              className="mg-twitch-skel-card mg-game-art-skeleton"
              style={loaderMode === "baseline" ? {
                animation:
                  "mgTwitchSkeletonFlip 900ms cubic-bezier(.2,.8,.2,1) forwards",
                animationDelay: `${i * 80}ms`,
              } : { willChange: "auto" }}
            >
              {loaderMode !== "baseline" && <MGSignalSkeleton animate={i < 4} />}
            </div>
          ))
        : slice.map((g, i) => {
            // Force a stable-but-unique render key so React never reuses a stale card
            // after filtering/sorting/resetting between very different game sets.
            const cardRenderKey = `${g.key || g.id || normalizeGameName(g.name)}-${i}`;

            return (
              <TwitchCard
                key={cardRenderKey}
                game={g}
                loaderMode={loaderMode}
                index={i}
                selectedLang={selectedLang}
                onClick={onCardClick}
                onRemove={onRemove}
                inChart={chartGamesSet?.has(String(g.id))}
                isNow={isNow}
                isNewRelease={!!g.isNewRelease}
                onToggleChart={onToggleChart}
              />
            );
          })}
      {previewMode === undefined && signalComparisonEnabled() && createPortal(
        <div className="mg-signal-comparison">
          <header><strong>mindGAME Signal Loader · real loading comparison · Twitch purple</strong><a href="?signalLoader=signal">Exit comparison</a></header>
          <div className="mg-signal-comparison-columns">
            {[["baseline", "Current baseline"], ["signal", "Refined · MG Signal"]].map(([mode, label]) =>
              <section key={mode}>
                <h2>{label}</h2>
                <TwitchCardGrid games={games} loading={loading} previewMode={mode} isNow={isNow} />
                <p>Chart placeholder · styling sample</p>
                {mode === "baseline" ? <div className="mg-signal-baseline-chart" style={{ position: "relative", overflow: "hidden" }}><TwitchHistoryChartSkeleton previewMode="baseline" /></div> :
                  <div className="mg-signal-chart-slot"><MGSignalSkeleton kind="chart" /></div>}
                <p>Game-detail hero · styling sample</p>
                <div className="mg-signal-hero-slot">{mode !== "baseline" && <MGSignalSkeleton kind="hero" />}<span>Loading game identity</span></div>
              </section>)}
          </div>
        </div>, document.body)}
    </div>
  );
}

/* ---------- Modal Skeleton ---------- */
function ModalSkeleton() {
  const signal = signalLoaderPreview() !== "baseline";
  return (
    <div
      className={signal ? "mg-signal-panel-preview" : undefined}
      style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}
    >
      <style>{`
        @keyframes mgShimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        .sk {
          background: #151515;
          border: 1px solid #222;
          border-radius: 8px;
          position: relative;
          overflow: hidden;
        }
        .sk::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image: linear-gradient(90deg, transparent, rgba(255,255,255,.06), transparent);
          background-size: 200px 100%;
          animation: mgShimmer 1.2s linear infinite;
          opacity: .7;
        }
      `}</style>

      {/* Language bars skeleton */}
      <div>
        <div
          style={{
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          Viewtime by Language
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "140px 1fr 110px",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div className="sk" style={{ height: 16, borderRadius: 6 }}>{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
              <div className="sk" style={{ height: 26, borderRadius: 6 }}>{signal && <MGSignalSkeleton kind="metric" animate={i < 2} />}</div>
              <div className="sk" style={{ height: 16, borderRadius: 6 }}>{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Streamers skeleton (three columns) */}
      <div>
        <div
          style={{
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          Streamers
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 16,
          }}
        >
          {Array.from({ length: 18 }).map((_, idx) => (
            <div
              key={idx}
              className="sk"
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                background: "#121212",
                border: "1px solid #222",
                borderRadius: 10,
                padding: 10,
                marginBottom: 10,
              }}
            >
              {signal && <MGSignalSkeleton kind="panel" animate={idx < 2} />}
              <div
                className="sk"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  flexShrink: 0,
                }}
              />
              <div style={{ width: "100%" }}>
                <div
                  className="sk"
                  style={{
                    height: 18,
                    borderRadius: 6,
                    marginBottom: 6,
                  }}
                />
                <div
                  className="sk"
                  style={{
                    height: 16,
                    borderRadius: 6,
                    width: "60%",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function ChartSidebarSkeleton({ isNow = false }) {
  const signal = signalLoaderPreview() !== "baseline";
  return (
    <div className={signal ? "mg-signal-panel-preview" : undefined} style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
      <style>{`
        @keyframes mgChartSidebarSkeletonShimmer {
          0% { background-position: -260px 0; }
          100% { background-position: 260px 0; }
        }
        .mg-chart-sidebar-skel-block {
          position: relative;
          overflow: hidden;
          background: rgba(255,255,255,0.075);
          border-radius: 7px;
        }
        .mg-chart-sidebar-skel-block::after {
          content: "";
          position: absolute;
          inset: 0;
          background-image: linear-gradient(
            90deg,
            transparent,
            rgba(255,255,255,0.075),
            transparent
          );
          background-size: 260px 100%;
          animation: mgChartSidebarSkeletonShimmer 1.35s linear infinite;
        }
      `}</style>

      <section
        style={{
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: 12,
          background: "linear-gradient(180deg, rgba(12,12,14,0.44), rgba(5,5,6,0.34))",
          boxShadow: "0 10px 26px rgba(0,0,0,0.22)",
          padding: 10,
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 6,
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div className="mg-chart-sidebar-skel-block" style={{ height: 28, borderRadius: 999 }}>{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
          <div className="mg-chart-sidebar-skel-block" style={{ height: 28, borderRadius: 999 }}>{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
          <div className="mg-chart-sidebar-skel-block" style={{ width: 28, height: 28, borderRadius: 999 }}>{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 8,
          }}
        >
          {Array.from({ length: 24 }).map((_, idx) => (
            <div
              key={idx}
              style={{
                textDecoration: "none",
                color: "#fff",
                background: "rgba(12,12,12,0.48)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 10,
                padding: "10px 10px",
                minHeight: 62,
                minWidth: 0,
                display: "grid",
                gridTemplateColumns: "48px minmax(0, 1fr)",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                className="mg-chart-sidebar-skel-block"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "radial-gradient(circle at 35% 30%, #242424, #101010 68%)",
                }}
              >{signal && <MGSignalSkeleton kind="panel" animate={idx < 2} />}</div>
              <div style={{ minWidth: 0 }}>
                <div
                  className="mg-chart-sidebar-skel-block"
                  style={{ width: `${82 + (idx % 4) * 18}%`, maxWidth: 128, height: 13, marginBottom: 7 }}
                >{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
                <div
                  className="mg-chart-sidebar-skel-block"
                  style={{ width: isNow ? 72 : 58, height: 13, marginBottom: 6 }}
                >{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
                <div
                  className="mg-chart-sidebar-skel-block"
                  style={{ width: 84, height: 10, opacity: 0.8 }}
                >{signal && <MGSignalSkeleton kind="metric" animate={false} />}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function TwitchHistoryChartSkeleton({ previewMode } = {}) {
  if (previewMode) return <LegacyTwitchHistoryChartSkeleton previewMode={previewMode} />;
  return <MindshareChartSkeleton height="100%" message="Loading Twitch history…" />;
}

function LegacyTwitchHistoryChartSkeleton({ previewMode } = {}) {
  const loaderMode = previewMode || signalLoaderPreview();
  const gridRows = 6;
  const gridCols = 9;
  if (loaderMode !== "baseline") return <div className="mg-signal-chart-slot"><MGSignalSkeleton kind="chart" /></div>;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 360,
        borderRadius: 8,
        overflow: "hidden",
        background: "rgba(0,0,0,0.08)",
      }}
    >
      <style>{`
        @keyframes mgTwitchHistoryAreaReveal {
          0% { clip-path: inset(0 100% 0 0); opacity: 0.32; }
          22% { opacity: 0.72; }
          100% { clip-path: inset(0 0 0 0); opacity: 0.72; }
        }
        @keyframes mgTwitchHistorySkeletonPulse {
          0%, 100% { opacity: 0.34; }
          50% { opacity: 0.72; }
        }
        .mg-twitch-chart-skel-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .mg-twitch-chart-skel-area {
          animation: mgTwitchHistoryAreaReveal 1.35s cubic-bezier(.2,.8,.2,1) both,
                     mgTwitchHistorySkeletonPulse 2.4s ease-in-out 1.35s infinite;
          transform-origin: left center;
        }
        .mg-twitch-chart-skel-area-secondary {
          animation-delay: 140ms, 1.5s;
        }
        .mg-twitch-chart-skel-block {
          position: absolute;
          overflow: hidden;
          background: rgba(255,255,255,0.07);
          border-radius: 7px;
          animation: mgTwitchHistorySkeletonPulse 1.45s ease-in-out infinite;
        }
      `}</style>

      {Array.from({ length: gridRows }).map((_, idx) => (
        <div
          key={`h-${idx}`}
          style={{
            position: "absolute",
            left: 112,
            right: 80,
            top: `${12 + (idx * 76) / Math.max(1, gridRows - 1)}%`,
            borderTop: "1px solid rgba(255,255,255,0.075)",
          }}
        />
      ))}
      {Array.from({ length: gridCols }).map((_, idx) => (
        <div
          key={`v-${idx}`}
          style={{
            position: "absolute",
            top: 34,
            bottom: 28,
            left: `${112 + (idx * 74) / Math.max(1, gridCols - 1)}%`,
            borderLeft: "1px solid rgba(255,255,255,0.045)",
            opacity: idx === 0 ? 0 : 1,
          }}
        />
      ))}

      <div className="mg-twitch-chart-skel-block" style={{ left: 18, top: 38, width: 62, height: 13 }} />
      <div className="mg-twitch-chart-skel-block" style={{ left: 18, top: "33%", width: 74, height: 13 }} />
      <div className="mg-twitch-chart-skel-block" style={{ left: 18, top: "58%", width: 54, height: 13 }} />
      <div className="mg-twitch-chart-skel-block" style={{ left: 112, bottom: 10, width: 48, height: 12 }} />
      <div className="mg-twitch-chart-skel-block" style={{ left: "42%", bottom: 10, width: 54, height: 12 }} />
      <div className="mg-twitch-chart-skel-block" style={{ right: 80, bottom: 10, width: 54, height: 12 }} />

      <svg
        className="mg-twitch-chart-skel-svg"
        viewBox="0 0 1200 520"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="mgTwitchSkelAreaFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(63,169,245,0.34)" />
            <stop offset="56%" stopColor="rgba(63,169,245,0.20)" />
            <stop offset="100%" stopColor="rgba(63,169,245,0.05)" />
          </linearGradient>
          <linearGradient id="mgTwitchSkelAreaFill2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(245,230,200,0.26)" />
            <stop offset="62%" stopColor="rgba(245,230,200,0.13)" />
            <stop offset="100%" stopColor="rgba(245,230,200,0.035)" />
          </linearGradient>
        </defs>

        <g transform="translate(112 34)">
          <path
            className="mg-twitch-chart-skel-area"
            d="
              M 0 420
              C 80 372, 145 365, 210 344
              C 292 318, 330 240, 404 228
              C 500 214, 560 308, 642 290
              C 728 270, 760 170, 846 154
              C 930 139, 1000 207, 1088 185
              L 1088 458
              L 0 458
              Z
            "
            fill="url(#mgTwitchSkelAreaFill)"
            stroke="none"
          />
          <path
            className="mg-twitch-chart-skel-area"
            d="
              M 0 420
              C 80 372, 145 365, 210 344
              C 292 318, 330 240, 404 228
              C 500 214, 560 308, 642 290
              C 728 270, 760 170, 846 154
              C 930 139, 1000 207, 1088 185
            "
            fill="none"
            stroke="rgba(63,169,245,0.78)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          <path
            className="mg-twitch-chart-skel-area mg-twitch-chart-skel-area-secondary"
            d="
              M 0 420
              C 72 388, 132 395, 190 374
              C 270 346, 316 326, 386 338
              C 472 353, 522 376, 610 350
              C 700 323, 768 302, 842 318
              C 942 340, 1005 322, 1088 286
              L 1088 458
              L 0 458
              Z
            "
            fill="url(#mgTwitchSkelAreaFill2)"
            stroke="none"
          />
          <path
            className="mg-twitch-chart-skel-area mg-twitch-chart-skel-area-secondary"
            d="
              M 0 420
              C 72 388, 132 395, 190 374
              C 270 346, 316 326, 386 338
              C 472 353, 522 376, 610 350
              C 700 323, 768 302, 842 318
              C 942 340, 1005 322, 1088 286
            "
            fill="none"
            stroke="rgba(245,230,200,0.58)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          <circle cx="846" cy="154" r="4.5" fill="rgba(63,169,245,0.95)" stroke="rgba(0,0,0,0.85)" strokeWidth="2" />
          <rect x="856" y="132" width="112" height="14" rx="7" fill="rgba(63,169,245,0.20)" opacity="0.75" />
        </g>
      </svg>

      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          padding: "8px 12px",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.35)",
          color: "rgba(229,231,235,0.78)",
          fontSize: 12,
          fontWeight: 850,
          letterSpacing: "0.02em",
          backdropFilter: "blur(5px)",
        }}
      >
        Loading Twitch history…
      </div>
    </div>
  );
}

export function ChartGameSidebar({
  game,
  gameImageUrl,
  languages,
  streamers,
  selectedLang,
  loading,
  error,
  onRetry,
  gameMeta,
  period,
  onClose,
}) {
  const STREAMER_PREVIEW_LIMIT = 72;
  const [showAllStreamers, setShowAllStreamers] = useState(false);
  const [activeSidebarPanel, setActiveSidebarPanel] = useState("streamers");

  useEffect(() => {
    setShowAllStreamers(false);
  }, [game?.gameId, selectedLang, period]);

  const gameName = game?.name || "";
  const isNow = period === "now";

  const langsSorted = useMemo(
    () =>
      [...(languages || [])].sort((a, b) =>
        isNow
          ? Number(b.currentViewers || 0) - Number(a.currentViewers || 0)
          : Number(b.viewMinutes || 0) - Number(a.viewMinutes || 0)
      ),
    [languages, isNow]
  );

  const langsTop = useMemo(() => {
    const arr = langsSorted;
    const maxH = Math.max(
      1,
      ...arr.map((l) =>
        isNow ? Number(l.currentViewers || 0) : toHours(l.viewMinutes)
      )
    );
    return { arr, maxH };
  }, [langsSorted, isNow]);

  const streamersSorted = useMemo(
    () =>
      [...(streamers || [])].sort((a, b) =>
        isNow
          ? Number(b.currentViewers || 0) - Number(a.currentViewers || 0)
          : Number(b.viewMinutes || 0) - Number(a.viewMinutes || 0)
      ),
    [streamers, isNow]
  );

  // Streamers_JSON is capped at top 1,001 per game, so show 1,000+ instead
  // of implying the game has exactly 1,001 streamers.
  const streamerCountLabel =
    streamersSorted.length >= 1001
      ? "1,000+"
      : streamersSorted.length.toLocaleString();

  const visibleStreamers = showAllStreamers
    ? streamersSorted
    : streamersSorted.slice(0, STREAMER_PREVIEW_LIMIT);

  const meta = gameMeta || {};
  const releaseRaw =
    meta.release_date ||
    meta.Release_Date ||
    meta.first_release_date ||
    meta.First_Release_Date ||
    "";
  const rd = safeDate(releaseRaw);
  const releaseDate = rd
    ? `${rd.getFullYear()}-${String(rd.getMonth() + 1).padStart(2, "0")}-${String(
        rd.getDate()
      ).padStart(2, "0")}`
    : releaseRaw;

  const publisher = meta.publisher || meta.Publisher || meta.publishers || "";
  const developer = meta.developer || meta.Developer || meta.developers || "";
  const genre = meta.genre || meta.Genre || meta.genres || "";
  const periodSuffix = period === "7d" ? "7D" : period === "now" ? "Now" : "1D";

  const sidebarExportCsv = (rows, label) => {
    if (!rows || !rows.length || !gameName) return;
    const csv = Papa.unparse(rows);
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFilePart(gameName)}_${label}_${periodSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportLanguages = () => {
    const rows = langsSorted.map((l, idx) => ({
      game: gameName,
      rank: idx + 1,
      language_name: langLabel(l.language),
      current_viewers: isNow ? Number(l.currentViewers || 0) : "",
      active_streamers: isNow ? Number(l.activeStreamers || 0) : "",
      hours_watched: isNow ? "" : (toHours(l.viewMinutes) || 0).toFixed(2),
      minutes_watched: isNow ? "" : l.viewMinutes || 0,
      period: periodSuffix,
    }));
    sidebarExportCsv(rows, "Viewtime_by_Language");
  };

  const exportStreamers = () => {
    const rows = streamersSorted.map((s, idx) => {
      const userName = s.user_name || s.display_name || "";
      return {
        game: gameName,
        rank: idx + 1,
        display_name: userName,
        current_viewers: isNow ? Number(s.currentViewers || 0) : "",
        hours_watched: isNow ? "" : (toHours(s.viewMinutes) || 0).toFixed(2),
        minutes_watched: isNow ? "" : s.viewMinutes || 0,
        peak_viewers: isNow ? "" : s.peak ?? "",
        stream_title: isNow ? s.title || "" : "",
        language_filter: selectedLang ? canonicalLang(selectedLang) : "all",
        streamer_url: userName ? `https://www.twitch.tv/${encodeURIComponent(userName)}` : "",
        period: periodSuffix,
      };
    });
    sidebarExportCsv(rows, "Streamers");
  };

  const sectionCardStyle = {
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: 12,
    background: "linear-gradient(180deg, rgba(12,12,14,0.44), rgba(5,5,6,0.34))",
    boxShadow: "0 10px 26px rgba(0,0,0,0.22)",
    padding: 12,
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  };

  const iconBtnStyle = {
    height: 24,
    minWidth: 24,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(0,0,0,0.38)",
    color: "#dbeafe",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const exportIconBtnStyle = (enabled) => ({
    width: 28,
    height: 28,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.22)",
    background: "rgba(0,0,0,0.42)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.45,
    padding: 0,
    flexShrink: 0,
  });

  const ExportIconButton = ({ enabled, onClick, title }) => (
    <CsvExportButton
      type="button"
      onClick={onClick}
      disabled={!enabled}
      title={title}
      aria-label={title}
      style={exportIconBtnStyle(enabled)}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 3v10m0 0l4-4m-4 4l-4-4"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 17v3h14v-3"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </CsvExportButton>
  );

  const PanelHeader = ({ exportEnabled, onExport, exportTitle }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        alignItems: "center",
        marginBottom: 10,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 5,
          padding: 3,
          borderRadius: 999,
          background: "rgba(0,0,0,0.34)",
          border: "1px solid rgba(255,255,255,0.08)",
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setActiveSidebarPanel("streamers")}
          style={{
            height: 28,
            border: "none",
            borderRadius: 999,
            background:
              activeSidebarPanel === "streamers"
                ? "rgba(63,169,245,0.18)"
                : "transparent",
            color: "#fff",
            fontSize: 12,
            fontWeight: 900,
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Streamers · {streamerCountLabel}
        </button>
        <button
          type="button"
          onClick={() => setActiveSidebarPanel("languages")}
          style={{
            height: 28,
            border: "none",
            borderRadius: 999,
            background:
              activeSidebarPanel === "languages"
                ? "rgba(63,169,245,0.18)"
                : "transparent",
            color: "#fff",
            fontSize: 12,
            fontWeight: 900,
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Languages · {langsSorted.length || 0}
        </button>
      </div>
      <ExportIconButton
        enabled={exportEnabled}
        onClick={onExport}
        title={exportTitle}
      />
    </div>
  );

  if (!game) return null;

  return (
    <aside
      style={{
        width: "100%",
        minWidth: 0,
        maxWidth: "none",
        alignSelf: "stretch",
        borderRight: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(0,0,0,0.18)",
        borderRadius: 0,
        overflow: "hidden",
        position: "relative",
        height: "100%",
      }}
    >
      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "16px 16px 18px 16px",
          height: "100%",
          maxHeight: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "relative",
            fontSize: 12,
            color: "rgba(216,224,234,0.84)",
            lineHeight: 1.38,
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 12,
            padding: onClose ? "12px 44px 12px 12px" : 12,
            background: "rgba(0,0,0,0.18)",
            marginBottom: 14,
            display: "grid",
            gridTemplateColumns: gameImageUrl ? "82px minmax(0, 1fr)" : "1fr",
            gap: 13,
            alignItems: "center",
          }}
        >
          {gameImageUrl && (
            <img
              src={gameImageUrl}
              alt={gameName}
              style={{
                width: 82,
                height: 108,
                objectFit: "cover",
                borderRadius: 9,
                border: "1px solid rgba(255,255,255,0.14)",
                boxShadow: "0 10px 22px rgba(0,0,0,0.8)",
                flexShrink: 0,
              }}
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )}

          <div style={{ minWidth: 0 }}>
            <div
              title={gameName}
              style={{
                color: "#fff",
                fontSize: 18,
                lineHeight: 1.08,
                fontWeight: 950,
                marginBottom: 8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {gameName}
            </div>

            {releaseDate && <div><span style={{ opacity: 0.65 }}>Release:</span> <b>{releaseDate}</b></div>}
            {publisher && <div><span style={{ opacity: 0.65 }}>Publisher:</span> <b>{publisher}</b></div>}
            {developer && <div><span style={{ opacity: 0.65 }}>Developer:</span> <b>{developer}</b></div>}
            {genre && <div><span style={{ opacity: 0.65 }}>Genre:</span> <b>{genre}</b></div>}
          </div>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              title="Close drawer"
              aria-label="Close drawer"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 28,
                height: 28,
                borderRadius: 999,
                border: "1px solid rgba(255,255,255,0.22)",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                fontSize: 16,
                fontWeight: 900,
                lineHeight: "26px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>

        {error && (
          <div role="alert" style={{ padding: 12, color: "#ff9b9b", fontSize: 12 }}>
            {error}
            {onRetry && <button type="button" onClick={onRetry}>Retry streamers</button>}
          </div>
        )}
        {loading ? (
          <ChartSidebarSkeleton isNow={isNow} />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              minHeight: 0,
            }}
          >
            {activeSidebarPanel === "languages" ? (
              <section style={{ ...sectionCardStyle, minHeight: 0, padding: 10 }}>
                <PanelHeader
                  exportEnabled={!!langsSorted.length}
                  onExport={exportLanguages}
                  exportTitle="Export languages CSV"
                />

                {langsTop.arr.length === 0 ? (
                  <div style={{ color: "#777", fontSize: 12 }}>No language data.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                    {langsTop.arr.map((l, idx) => {
                      const metricValue = isNow
                        ? Number(l.currentViewers || 0)
                        : toHours(l.viewMinutes);
                      const w = (metricValue / langsTop.maxH) * 100;
                      return (
                        <div
                          key={`${l.language}-${idx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "108px 1fr 110px",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          <div
                            title={langLabel(l.language)}
                            style={{
                              color: "#e5e7eb",
                              fontSize: 12,
                              fontWeight: 800,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {langLabel(l.language)}
                          </div>
                          <div
                            style={{
                              height: 20,
                              background: "rgba(255,255,255,0.06)",
                              border: "1px solid rgba(255,255,255,0.065)",
                              borderRadius: 6,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${w}%`,
                                background: "#3fa9f5",
                                boxShadow: "0 0 12px rgba(63,169,245,0.35)",
                              }}
                            />
                          </div>
                          <div style={{ color: "#fff", fontSize: 11, fontWeight: 900, textAlign: "left", whiteSpace: "nowrap" }}>
                            {isNow
                              ? idx === 0
                                ? `${fmtInt(metricValue)} viewers`
                                : fmtInt(metricValue)
                              : idx === 0
                              ? `${fmtHours(metricValue)} H`
                              : fmtHours(metricValue)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : (
              <section style={{ ...sectionCardStyle, minHeight: 0, padding: 10 }}>
                <PanelHeader
                  exportEnabled={!!streamersSorted.length}
                  onExport={exportStreamers}
                  exportTitle="Export streamers CSV"
                />

                {streamersSorted.length === 0 ? (
                  <div style={{ color: "#777", fontSize: 12 }}>No streamers.</div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                      gap: 8,
                    }}
                  >
                    {visibleStreamers.map((s, idx) => {
                      const userName = s.user_name || s.display_name || "";
                      const rank = idx + 1;
                      const sh = toHours(s.viewMinutes);
                      const currentViewers = Number(s.currentViewers || 0);
                      return (
                        <a
                          key={`${userName}-${rank}`}
                          href={userName ? `https://www.twitch.tv/${encodeURIComponent(userName)}` : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={
                            isNow
                              ? `${rank}. ${userName} — ${fmtInt(currentViewers)} viewers${s.title ? ` — ${s.title}` : ""}`
                              : `${rank}. ${userName} — ${fmtHours(sh)} h`
                          }
                          style={{
                            textDecoration: "none",
                            color: "#fff",
                            background: "rgba(12,12,12,0.48)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 10,
                            padding: "10px 10px",
                            minHeight: 62,
                            minWidth: 0,
                            display: "grid",
                            gridTemplateColumns: "48px minmax(0, 1fr)",
                            gap: 10,
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              overflow: "hidden",
                              background: "#171717",
                              border: "1px solid rgba(255,255,255,0.12)",
                              flexShrink: 0,
                            }}
                          >
                            {getStreamerAvatarUrl(s) && (
                              <img
                                src={getStreamerAvatarUrl(s)}
                                alt={userName}
                                loading="lazy"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            )}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                lineHeight: 1.15,
                                fontWeight: 950,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {rank}. {userName || "Unknown"}
                            </div>
                            <div style={{ color: "#3fa9f5", fontSize: 12, fontWeight: 900, marginTop: 2 }}>
                              {isNow ? `${fmtInt(currentViewers)} viewers` : `${fmtHours(sh)} h`}
                            </div>
                            <div style={{ color: "#9ca3af", fontSize: 11 }}>
                              {isNow
                                ? `${langLabel(s.language || "")} · Live`
                                : `Peak ${fmtInt(s.peak || 0)}`}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}

                {streamersSorted.length > STREAMER_PREVIEW_LIMIT && (
                  <button
                    type="button"
                    onClick={() => setShowAllStreamers((v) => !v)}
                    style={{
                      marginTop: 12,
                      width: "100%",
                      height: 30,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(0,0,0,0.30)",
                      color: "#cfd8e3",
                      fontSize: 11,
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    {showAllStreamers
                      ? "Show top streamers"
                      : `Show all streamers (${streamerCountLabel})`}
                  </button>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}


/* ---------- Modal (using portal for true centering) ---------- */
/* ---------- Modal (using portal for true centering) ---------- */
function StreamersModal({
  gameName,
  gameImageUrl,
  streamers,
  languages,
  onClose,
  selectedLang,
  loading,
  error,
  gameMeta,
  period,
}) {
  const LANG_LIMIT = 10;
  const [showAllLangs, setShowAllLangs] = useState(false);

    const meta = gameMeta || {};

  // Prefer IGDB-style fields but still fall back to any existing ones
  const releaseRaw =
    meta.release_date ||
    meta.Release_Date ||
    meta.first_release_date ||
    meta.First_Release_Date ||
    "";

  // Try to format release date nicely if it's a real date, otherwise use the raw string
  const rd = safeDate(releaseRaw);
  const releaseDate = rd
    ? `${rd.getFullYear()}-${String(rd.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(rd.getDate()).padStart(2, "0")}`
    : releaseRaw;

  const publisher =
    meta.publisher || meta.Publisher || meta.publishers || "";

  const developer =
    meta.developer || meta.Developer || meta.developers || "";

  const genre = meta.genre || meta.Genre || meta.genres || "";

  const hasMeta = releaseDate || publisher || developer || genre;

 // 1D / 7D suffix for export filenames
 const isNow = period === "now";
 const periodSuffix = period === "7d" ? "7D" : period === "now" ? "Now" : "1D";

  const langsSorted = useMemo(
    () =>
      [...languages].sort((a, b) =>
        isNow
          ? Number(b.currentViewers || 0) - Number(a.currentViewers || 0)
          : Number(b.viewMinutes || 0) - Number(a.viewMinutes || 0)
      ),
    [languages, isNow]
  );

  const langsTop = useMemo(() => {
    const arr = langsSorted;
    const maxH = Math.max(
      1,
      ...arr.map((l) =>
        isNow ? Number(l.currentViewers || 0) : toHours(l.viewMinutes)
      )
    );
    return { arr, maxH };
  }, [langsSorted, isNow]);

  const streamersSorted = useMemo(
    () =>
      [...streamers].sort((a, b) =>
        isNow
          ? Number(b.currentViewers || 0) - Number(a.currentViewers || 0)
          : Number(b.viewMinutes || 0) - Number(a.viewMinutes || 0)
      ),
    [streamers, isNow]
  );

  const PAGE_SIZE = 100;
  const [streamerPage, setStreamerPage] = useState(1);

  // reset page when game/language/streamers change
  React.useEffect(() => {
    setStreamerPage(1);
  }, [gameName, selectedLang, streamers]);

  const visibleStreamers = useMemo(
    () => streamersSorted.slice(0, streamerPage * PAGE_SIZE),
    [streamersSorted, streamerPage]
  );

  // ---------- CSV helpers (with BOM) ----------
  const slugifyForFile = (name, fallback = "Game") => {
    const base = (name || fallback)
      .toString()
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return base || fallback;
  };

  const downloadCsv = (rows, dataLabel) => {
    if (!rows || !rows.length) return;

    const csv = Papa.unparse(rows);
    // BOM so Excel respects UTF-8 (Japanese, Chinese, etc.)
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const safeGame = slugifyForFile(gameName);
    const safeLabel = dataLabel.replace(/[^A-Za-z0-9]+/g, "_");
    const fileName = `${safeGame}_${safeLabel}.csv`;

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

    const handleExportLanguagesCsv = () => {
    if (!langsSorted.length) return;

    const rows = langsSorted.map((l, idx) => ({
      game: gameName,
      rank: idx + 1,
      language_name: langLabel(l.language), // ✅ no language_code
      current_viewers: isNow ? Number(l.currentViewers || 0) : "",
      active_streamers: isNow ? Number(l.activeStreamers || 0) : "",
      hours_watched: isNow ? "" : (toHours(l.viewMinutes) || 0).toFixed(2),
      minutes_watched: isNow ? "" : l.viewMinutes || 0,
    }));

    // Game_Name_Viewtime_by_Language_1D or _7D
    downloadCsv(rows, `Viewtime_by_Language_${periodSuffix}`);
  };


    const handleExportStreamersCsv = () => {
    if (!streamersSorted.length) return;

    const rows = streamersSorted.map((s, idx) => {
      const userName = s.user_name || s.display_name || "";
      return {
        game: gameName,
        rank: idx + 1,
        display_name: s.display_name || s.user_name, // ✅ primary identity field
        current_viewers: isNow ? Number(s.currentViewers || 0) : "",
        hours_watched: isNow ? "" : (toHours(s.viewMinutes) || 0).toFixed(2),
        minutes_watched: isNow ? "" : s.viewMinutes || 0,
        peak_viewers: isNow ? "" : s.peak ?? "",
        language_filter: selectedLang ? canonicalLang(selectedLang) : "all",
        // 🔹 same URL as the UI link
        streamer_url: `https://www.twitch.tv/${encodeURIComponent(userName)}`,
      };
    });

    // Game_Name_Streamers_1D or _7D
    downloadCsv(rows, `Streamers_${periodSuffix}`);
  };


  const overlay = (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.65)",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        padding: 20,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(1200px, calc(var(--mg-vw) * 95))",
          maxHeight: "calc(var(--mg-vh) * 85)",
          position: "relative",
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,.75)",
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        {/* global blurred bg */}
        {gameImageUrl && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${gameImageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(12px)",
              transform: "scale(1.12)",
              opacity: 0.80,
            }}
          />
        )}

        {/* dark overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(130% 160% at 50% 0%, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.92) 100%)",
          }}
        />

        {/* scrollable content */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            maxHeight: "calc(var(--mg-vh) * 85)",
            overflow: "auto",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12,
            background:
              "linear-gradient(180deg, rgba(5,5,5,0.45), rgba(5,5,5,0.65))",
          }}
        >
                   {/* HEADER */}
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              padding: "14px 18px 10px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              background: "rgba(5,5,5,0.92)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ position: "relative" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",          // <-- vertical center
                  justifyContent: "flex-start",
                  gap: 18,
                  flexWrap: "wrap",
                  paddingRight: 90,              // room for Close button
                  minWidth: 0,
                }}
              >
                {/* Left: cover art + title */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    minWidth: 0,
                  }}
                >
                  {gameImageUrl && (
                    <img
                      src={gameImageUrl}
                      alt={gameName}
                      style={{
                        width: "auto",
                        height: 90,
                        objectFit: "contain",
                        borderRadius: 12,
                        boxShadow: "0 10px 30px rgba(0,0,0,.8)",
                        flexShrink: 0,
                      }}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: "#8fb4ff",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        marginBottom: 4,
                      }}
                    >
                      Languages &amp; Streamers
                    </div>
                    <div
                      style={{
                        color: "#fff",
                        fontSize: 22,
                        fontWeight: 900,
                        lineHeight: 1.1,
                        textShadow: "0 2px 10px rgba(0,0,0,.8)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={gameName}
                    >
                      {gameName}
                      {selectedLang ? ` — ${langLabel(selectedLang)}` : ""}
                    </div>
                  </div>
                </div>

                {/* Right: metadata, left-aligned and vertically centered */}
                {hasMeta && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",   // <-- left align
                      justifyContent: "center",
                      gap: 2,
                      fontSize: 12,
                      color: "#d0d7e5",
                      lineHeight: 1.3,
                      minWidth: 200,
                    }}
                  >
                    {releaseDate && (
                      <div>
                        <span style={{ opacity: 0.65 }}>Release:</span>{" "}
                        <span style={{ fontWeight: 600 }}>{releaseDate}</span>
                      </div>
                    )}
                    {publisher && (
                      <div>
                        <span style={{ opacity: 0.65 }}>Publisher:</span>{" "}
                        <span style={{ fontWeight: 600 }}>{publisher}</span>
                      </div>
                    )}
                    {developer && (
                      <div>
                        <span style={{ opacity: 0.65 }}>Developer:</span>{" "}
                        <span style={{ fontWeight: 600 }}>{developer}</span>
                      </div>
                    )}
                    {genre && (
                      <div>
                        <span style={{ opacity: 0.65 }}>Genre:</span>{" "}
                        <span style={{ fontWeight: 600 }}>{genre}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Close button – back to top-right corner */}
              <button
                onClick={onClose}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  background: "rgba(0,0,0,0.55)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "6px 14px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                Close
              </button>
            </div>
          </div>


          {error && <div role="alert" style={{ padding: 16, color: "#ff9b9b" }}>{error}</div>}
          {/* BODY */}
          {loading ? (
            <ModalSkeleton />
          ) : (
            <div
              style={{
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {/* Languages */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginBottom: 10,
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    Viewtime by Language
                  </div>

                    <CsvExportButton
    type="button"
    onClick={handleExportLanguagesCsv}
    disabled={loading || !langsSorted.length}
    title="Export Viewtime by Language as CSV"
    style={{
      padding: "2px 8px",
      borderRadius: 999,
      border: "none",
      background: "#ffffff",
      color: "#000000",
      fontSize: 11,
      fontWeight: 600,
      cursor: loading || !langsSorted.length ? "default" : "pointer",
      opacity: loading || !langsSorted.length ? 0.5 : 1,
      whiteSpace: "nowrap",
    }}
  >
    Export
  </CsvExportButton>
</div>


                {langsTop.arr.length === 0 ? (
                  <div style={{ color: "#888" }}>No language data.</div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {langsTop.arr.map((l, idx) => {
                      const metricValue = isNow
                        ? Number(l.currentViewers || 0)
                        : toHours(l.viewMinutes);
                      const w = (metricValue / langsTop.maxH) * 100;

                      return (
                        <div
                          key={`${l.language}-${idx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "140px 1fr 110px",
                            gap: 12,
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              color: "#ddd",
                              fontSize: 13,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {langLabel(l.language)}
                          </div>
                          <div
                            style={{
                              position: "relative",
                              height: 26,
                              background: "rgba(15,15,15,0.55)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: 6,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${w}%`,
                                background: "#3fa9f5",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              color: "#fff",
                              fontSize: 13,
                              textAlign: "left",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isNow
                              ? idx === 0
                                ? `${fmtInt(metricValue)} viewers`
                                : fmtInt(metricValue)
                              : idx === 0
                              ? `${fmtHours(metricValue)} H`
                              : fmtHours(metricValue)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {langsSorted.length > 10 && (
                  <div
                    style={{
                      marginTop: 14,
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => setShowAllLangs((v) => !v)}
                      style={{
                        background: "#222",
                        border: "1px solid #555",
                        color: "#fff",
                        borderRadius: 999,
                        padding: "8px 18px",
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {showAllLangs
                        ? "Show top 10 languages"
                        : `Show all languages (${langsSorted.length})`}
                    </button>
                  </div>
                )}
              </div>

              {/* Streamers */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginBottom: 10,
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 700,
                    }}
                  >
                    Streamers{" "}
                    {selectedLang ? `(${langLabel(selectedLang)})` : ""}
                  </div>

                   <CsvExportButton
    type="button"
    onClick={handleExportStreamersCsv}
    disabled={loading || !streamersSorted.length}
    title="Export Streamers as CSV"
    style={{
      padding: "2px 8px",
      borderRadius: 999,
      border: "none",
      background: "#ffffff",
      color: "#000000",
      fontSize: 11,
      fontWeight: 600,
      cursor: loading || !streamersSorted.length ? "default" : "pointer",
      opacity: loading || !streamersSorted.length ? 0.5 : 1,
      whiteSpace: "nowrap",
    }}
  >
    Export
  </CsvExportButton>
</div>

                {streamersSorted.length === 0 ? (
                  <div style={{ color: "#888" }}>
                    No streamers
                    {selectedLang ? ` for ${langLabel(selectedLang)}` : ""}.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: 16,
                      }}
                    >
                      {visibleStreamers.map((s, idx) => {
                        const rank = idx + 1;
                        const sh = toHours(s.viewMinutes);
                        const currentViewers = Number(s.currentViewers || 0);

                        return (
                          <div
                            key={`${s.user_name}-${rank}`}
                            style={{
                              display: "flex",
                              gap: 12,
                              alignItems: "center",
                              background: "rgba(10,10,10,0.6)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: 10,
                              padding: 10,
                              marginBottom: 0,
                            }}
                          >
                            {s.profile_image_url ? (
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                  border: "1px solid #222",
                                }}
                              >
                                <img
                                  src={s.profile_image_url}
                                  alt={s.user_name}
                                  onError={(e) =>
                                    (e.currentTarget.style.display = "none")
                                  }
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  background: "#1b1b1b",
                                  border: "1px solid #222",
                                  flexShrink: 0,
                                }}
                              />
                            )}

                            <div style={{ minWidth: 0 }}>
                              <a
                                href={`https://www.twitch.tv/${encodeURIComponent(
                                  s.user_name
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#fff",
                                  fontWeight: 800,
                                  fontSize: 15,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "inline-block",
                                  maxWidth: 250,
                                  textDecoration: "none",
                                }}
                              >
                                {rank}. {s.user_name}
                              </a>
                              <div
                                style={{
                                  color: "#3fa9f5",
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                {isNow ? `${fmtInt(currentViewers)} viewers` : `${fmtHours(sh)} h`}
                                {!isNow && (
                                  <>
                                    {" · "}
                                    <span style={{ opacity: 0.9 }}>
                                      Peak {s.peak.toLocaleString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {visibleStreamers.length < streamersSorted.length && (
                      <div style={{ marginTop: 12, textAlign: "center" }}>
                        <button
                          onClick={() => setStreamerPage((p) => p + 1)}
                          style={{
                            background: "#222",
                            border: "1px solid #555",
                            color: "#fff",
                            borderRadius: 999,
                            padding: "8px 18px",
                            cursor: "pointer",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          Show next{" "}
                          {Math.min(
                            PAGE_SIZE,
                            streamersSorted.length -
                              visibleStreamers.length
                          )}{" "}
                          streamers
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}



export function StreamersTable({
  streamers,
  loading,
  error,
  loadingMore,
  detailsLoading,
  detailsError,
  onRetry,
  period,
  onExport,
  gameArtByName,
}) {
  const isNow = period === "now";
  const INITIAL = 120;
  const BATCH = 120;
  const [showCount, setShowCount] = useState(INITIAL);
  const [tableSort, setTableSort] = useState({ key: "hours", dir: "desc" });
  const [streamerNameSearch, setStreamerNameSearch] = useState("");

  const toggleTableSort = (key) => {
    if (isNow) return;
    setTableSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "desc" ? "asc" : "desc" };
      }
      return { key, dir: "desc" };
    });
  };

  const sortArrow = (key) =>
    isNow ? "" : tableSort.key === key ? (tableSort.dir === "desc" ? " ↓" : " ↑") : "";

  useEffect(() => {
    setShowCount(INITIAL);
  }, [streamers, period, streamerNameSearch]);

  const sortedStreamers = useMemo(() => {
    const q = streamerNameSearch.trim().toLowerCase();
    const copy = (Array.isArray(streamers) ? streamers : []).filter((s) => {
      if (!q) return true;
      return String(s.user_name || s.display_name || "").toLowerCase().includes(q);
    });
    if (isNow) {
      return copy;
    }

    const dir = tableSort.dir === "asc" ? 1 : -1;

    copy.sort((a, b) => {
      let av = 0;
      let bv = 0;

      if (tableSort.key === "peak") {
        av = Number(a.peak_viewers || a.peak || 0);
        bv = Number(b.peak_viewers || b.peak || 0);
      } else if (tableSort.key === "games") {
        av = Number(a.games_streamed || a.games || 0);
        bv = Number(b.games_streamed || b.games || 0);
      } else {
        av = Number(a.hours_watched || 0);
        bv = Number(b.hours_watched || 0);
      }

      if (av === bv) {
        return String(a.user_name || a.display_name || "").localeCompare(
          String(b.user_name || b.display_name || ""),
          undefined,
          { sensitivity: "base" }
        );
      }
      return (av - bv) * dir;
    });

    return copy;
  }, [streamers, tableSort, streamerNameSearch, isNow]);

  const visible = loading ? [] : sortedStreamers.slice(0, showCount);

  return (
    <div
      style={{
        border: "1px solid #222",
        borderRadius: 10,
        overflow: "visible",
        background: "#070707",
        boxShadow: "0 10px 28px rgba(0,0,0,0.45)",
      }}
    >
      <style>{`
        .mg-twitch-streamers-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          overflow: visible;
        }
        .mg-twitch-streamers-table th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #0d0d0d;
          color: rgba(187,187,187,0.78);
          font-size: 11px;
          line-height: 1.2;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          font-weight: 800;
          text-align: left;
          padding: 10px 10px;
          border-bottom: 1px solid #222;
          white-space: nowrap;
        }
        .mg-twitch-streamers-table td {
          height: 82px;
          padding: 0 10px;
          border-bottom: 1px solid rgba(255,255,255,0.055);
          color: #d8e0ea;
          font-size: 13px;
          vertical-align: middle;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .mg-twitch-streamers-table tbody tr {
          position: relative;
          z-index: 1;
        }
        .mg-twitch-streamers-table tbody tr:hover {
          z-index: 2000;
        }
        .mg-twitch-streamers-table tr:hover td {
          background: rgba(63,169,245,0.065);
        }
        .mg-twitch-streamers-table a {
          color: #fff;
          text-decoration: none;
        }
        .mg-twitch-streamers-table a:hover {
          color: #3fa9f5;
        }
        .mg-rank-avatar-cell {
          position: relative;
          overflow: hidden !important;
          padding-left: 92px !important;
        }
        .mg-streamer-avatar-wrap {
          position: absolute;
          left: -15px;
          top: -11px;
          width: 104px;
          height: 98px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          pointer-events: none;
        }
        .mg-streamer-avatar {
          width: 105px;
          height: 105px;
          border-radius: 50%;
          object-fit: cover;
          background: #181818;
          border: 1px solid rgba(255,255,255,0.12);
          display: block;
          box-shadow: 0 0 20px rgba(0,0,0,0.9);
          filter: saturate(1.1) contrast(1.05);
        }
        .mg-streamer-avatar-fallback {
          width: 98px;
          height: 98px;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 30%, #2a2a2a, #111 68%);
          border: 1px solid rgba(255,255,255,0.12);
          box-shadow: 0 0 20px rgba(0,0,0,0.9);
        }
        .mg-rank-text {
          position: relative;
          z-index: 1;
          color: #aaa;
          font-weight: 850;
          font-size: 13px;
          margin-left: 6px;
          text-shadow: 0 1px 4px rgba(0,0,0,0.9);
        }
        .mg-games-art-cell {
          overflow: visible !important;
          position: relative;
          z-index: 5;
        }
        .mg-game-art-strip {
          display: flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
          height: 82px;
          overflow: visible;
          position: relative;
          z-index: 10;
        }
        .mg-top-game-art {
          width: 62px;
          height: 82px;
          object-fit: cover;
          border-radius: 0;
          background: #181818;
          border: none;
          flex-shrink: 0;
          display: block;
          transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease;
          transform-origin: center center;
          position: relative;
          z-index: 1;
        }
        .mg-top-game-art:hover {
          transform: scale(2.15) translateY(-10px);
          z-index: 5000;
          border-color: rgba(255,255,255,0.55);
          box-shadow: 0 18px 45px rgba(0,0,0,0.88), 0 0 18px rgba(63,169,245,0.22);
        }
        .mg-streamer-table-search {
          font-family: inherit;
          transition: border-color 0.15s ease;
        }
        .mg-streamer-table-search::placeholder {
          color: #bbb;
          opacity: 0.72;
        }
        .mg-streamer-table-search:hover,
        .mg-streamer-table-search:focus {
          border-color: rgba(255,255,255,0.82) !important;
          outline: none;
        }
      `}</style>

      {detailsLoading && <div role="status" style={{ padding: 12, color: "#aab6c4", fontSize: 12 }}>Loading complete game details. Available streamer totals are shown below.</div>}
      {detailsError && <div role="alert" style={{ padding: 12, color: "#ff9b9b", fontSize: 12 }}>{detailsError}{onRetry && <button type="button" onClick={onRetry}>Retry streamers</button>}</div>}
      {error && streamers.length > 0 && <div role="alert" style={{ padding: 12, color: "#ff9b9b", fontSize: 12 }}>{error}{onRetry && <button type="button" onClick={onRetry}>Retry streamers</button>}</div>}
      {error && !streamers.length ? (
        <div role="alert" style={{ padding: 18, color: "#ff9b9b", fontSize: 13 }}>
          {error}
          {onRetry && <button type="button" onClick={onRetry}>Retry streamers</button>}
        </div>
      ) : loading ? (
  <>
    <style>{`
      @keyframes mgStreamerTableShimmer {
        0% { background-position: -240px 0; }
        100% { background-position: 240px 0; }
      }

      .mg-streamer-table-skel {
        height: 82px;
        border-bottom: 1px solid rgba(255,255,255,0.055);
      }

      .mg-streamer-skel-block {
        position: relative;
        overflow: hidden;
        background: #141414;
        border-radius: 6px;
      }

      .mg-streamer-skel-block::after {
        content: "";
        position: absolute;
        inset: 0;
        background-image: linear-gradient(
          90deg,
          transparent,
          rgba(255,255,255,0.07),
          transparent
        );
        background-size: 240px 100%;
        animation: mgStreamerTableShimmer 1.25s linear infinite;
      }

      .mg-streamer-skel-avatar {
        width: 105px;
        height: 105px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 30%, #242424, #101010 68%);
        border: 1px solid rgba(255,255,255,0.08);
        position: absolute;
        left: -15px;
        top: -11px;
      }
    `}</style>

    <div className={signalLoaderPreview() !== "baseline" ? "mg-signal-panel-preview" : undefined} style={{ overflow: "visible", position: "relative", zIndex: 1 }}>
      <table className="mg-twitch-streamers-table">
        <thead>
          <tr>
            <th style={{ width: 150 }}>Rank</th>
            <th style={{ width: 320 }}>Streamer</th>
            <th style={{ width: 138, textAlign: "right" }}>
              {isNow ? "Current Viewers" : "Hours Watched"}
            </th>
            {!isNow && (
              <th style={{ width: 122, textAlign: "right" }}>
                Peak Viewers
              </th>
            )}
            <th style={{ width: 142, textAlign: "center" }}>
              Games Streamed
            </th>
            <th style={{ width: 620 }}>
              {isNow ? "Currently Streaming" : "Top Games"}
            </th>
            <th style={{ width: 42 }} />
          </tr>
        </thead>

        <tbody>
          {Array.from({ length: 14 }).map((_, idx) => (
            <tr key={idx} className="mg-streamer-table-skel">
              <td className="mg-rank-avatar-cell">
                <div className="mg-streamer-skel-avatar" />
                <div
                  className="mg-streamer-skel-block"
                  style={{ width: 42, height: 14, marginLeft: 6 }}
                />
              </td>

              <td>
                <div
                  className="mg-streamer-skel-block"
                  style={{ width: `${150 + (idx % 4) * 24}px`, height: 16 }}
                />
              </td>

              <td>
                <div
                  className="mg-streamer-skel-block"
                  style={{ width: 72, height: 16, marginLeft: "auto" }}
                />
              </td>

              {!isNow && (
                <td>
                  <div
                    className="mg-streamer-skel-block"
                    style={{ width: 62, height: 16, marginLeft: "auto" }}
                  />
                </td>
              )}

              <td>
                <div
                  className="mg-streamer-skel-block"
                  style={{ width: 38, height: 16, margin: "0 auto" }}
                />
              </td>

              <td className="mg-games-art-cell">
                <div className="mg-game-art-strip">
                  {Array.from({ length: 8 }).map((__, gameIdx) => (
                    <div
                      key={gameIdx}
                      className="mg-streamer-skel-block"
                      style={{
                        width: 62,
                        height: 82,
                        borderRadius: 0,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </div>
              </td>

              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
) : !sortedStreamers.length ? (
        <div style={{ padding: 18, color: "#999", fontSize: 13 }}>
          No streamers match the current search.
        </div>
      ) : (
        <>
          <div style={{ overflow: "visible", position: "relative", zIndex: 1 }}>
            <table className="mg-twitch-streamers-table">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Rank</th>
                  <th style={{ width: 320 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span>Streamer</span>
                      <input
                        type="text"
                        value={streamerNameSearch}
                        onChange={(e) => setStreamerNameSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search Streamers..."
                        className="mg-streamer-table-search"
                        style={{
                          width: 210,
                          height: 28,
                          background: "#111",
                          border: "1px solid #444",
                          borderRadius: 6,
                          padding: "0 9px",
                          boxSizing: "border-box",
                          color: "#bbb",
                          fontSize: 12,
                          fontWeight: 700,
                          textTransform: "none",
                          letterSpacing: 0,
                        }}
                      />
                    </div>
                  </th>
                  <th
                    onClick={() => toggleTableSort("hours")}
                    style={{ width: 138, textAlign: "right", cursor: isNow ? "default" : "pointer" }}
                    title={isNow ? "Current Viewers" : "Sort by Hours Watched"}
                  >
                    {isNow ? "Current Viewers" : "Hours Watched"}{sortArrow("hours")}
                  </th>
                  {!isNow && (
                    <th
                      onClick={() => toggleTableSort("peak")}
                      style={{ width: 122, textAlign: "right", cursor: "pointer" }}
                      title="Sort by Peak Viewers"
                    >
                      Peak Viewers{sortArrow("peak")}
                    </th>
                  )}
                  <th
                    onClick={() => toggleTableSort("games")}
                    style={{ width: 142, textAlign: "center", cursor: isNow ? "default" : "pointer" }}
                    title={isNow ? "Games Streamed" : "Sort by Games Streamed"}
                  >
                    Games Streamed{sortArrow("games")}
                  </th>
                  <th style={{ width: 620 }}>{isNow ? "Currently Streaming" : "Top Games"}</th>
                  <th style={{ width: 42, textAlign: "right" }}>
                    <CsvExportButton
                      type="button"
                      onClick={onExport}
                      disabled={loading || loadingMore || !streamers.length}
                      title="Export visible streamers CSV"
                      aria-label="Export visible streamers CSV"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "1px solid rgba(255,255,255,0.22)",
                        background: "rgba(0,0,0,0.42)",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: loading || loadingMore || !streamers.length ? "default" : "pointer",
                        opacity: loading || loadingMore || !streamers.length ? 0.45 : 1,
                        padding: 0,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M12 3v10m0 0l4-4m-4 4l-4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M5 17v3h14v-3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </CsvExportButton>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s, idx) => {
                  const userName = s.user_name || s.display_name || "";
                  const rank = Number(s.rank || idx + 1);
                  const topGame = getTopGameName(s);
                  const topGames = (Array.isArray(s.top_games) && s.top_games.length
                    ? s.top_games
                    : topGame
                    ? [{ game_name: topGame, box_art_url: getTopGameArt(s, gameArtByName) }]
                    : []
                  ).slice(0, 10);
                  const url =
                    s.twitch_url ||
                    (userName
                      ? `https://www.twitch.tv/${encodeURIComponent(userName)}`
                      : "");

                  return (
                    <tr key={`${userName}-${rank}-${idx}`}>
                      <td className="mg-rank-avatar-cell">
                        <div className="mg-streamer-avatar-wrap">
                          {s.profile_image_url ? (
                            <img
                              className="mg-streamer-avatar"
                              src={s.profile_image_url}
                              alt={userName}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fallback = e.currentTarget.parentElement?.querySelector(".mg-streamer-avatar-fallback");
                                if (fallback) fallback.style.display = "block";
                              }}
                            />
                          ) : null}
                          <div
                            className="mg-streamer-avatar-fallback"
                            style={{ display: getStreamerAvatarUrl(s) ? "none" : "block" }}
                          />
                        </div>
                        <span className="mg-rank-text">#{rank.toLocaleString()}</span>
                      </td>
                      <td title={userName}>
                        <a
                          href={url || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: 850 }}
                        >
                          {userName || "Unknown"}
                        </a>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "#fff",
                          fontWeight: 850,
                        }}
                      >
                        {isNow
                          ? fmtInt(s.current_viewers || s.currentViewers || s.hours_watched || 0)
                          : fmtHours(Number(s.hours_watched || 0))}
                      </td>
                      {!isNow && (
                        <td style={{ textAlign: "right", color: "#dbeafe" }}>
                          {fmtInt(s.peak_viewers || s.peak || 0)}
                        </td>
                      )}
                      <td style={{ textAlign: "center", fontWeight: 850 }}>
                        {fmtInt(s.games_streamed || s.games || 0)}
                      </td>
                      <td
                        className="mg-games-art-cell"
                        title={topGames.map((g) => g.game_name || g.name).filter(Boolean).join(", ")}
                      >
                        <div className="mg-game-art-strip">
                          {topGames.map((g, gameIdx) => {
                            const gameName = g.game_name || g.name || "";
                            const art =
                              normalizeBoxArt(g.box_art_url || g.image_url || "") ||
                              (gameName ? gameArtByName.get(normalizeGameName(gameName)) : "") ||
                              "";
                            return art ? (
                              <img
                                key={`${gameName}-${gameIdx}`}
                                className="mg-top-game-art"
                                src={art}
                                alt={gameName}
                                title={gameName}
                                loading="lazy"
                                onError={(e) =>
                                  (e.currentTarget.style.display = "none")
                                }
                              />
                            ) : (
                              <div key={`${gameName}-${gameIdx}`} className="mg-top-game-art" title={gameName} />
                            );
                          })}
                        </div>
                      </td>
                      <td />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {loadingMore && (
            <div role="status" style={{ padding: 12, color: "#aab6c4", fontSize: 12, textAlign: "center" }}>
              Loading more streamer rows...
            </div>
          )}

          {visible.length < streamers.length && (
            <div style={{ padding: 14, textAlign: "center" }}>
              <button
                type="button"
                onClick={() =>
                  setShowCount((n) => Math.min(n + BATCH, streamers.length))
                }
                style={{
                  height: 34,
                  padding: "0 16px",
                  borderRadius: 7,
                  border: "1px solid #444",
                  background: "#111",
                  color: "#cfd8e3",
                  fontWeight: 800,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Show next {Math.min(BATCH, streamers.length - visible.length)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HeaderMiniDropdown({
  value,
  options,
  onChange,
  active = false,
  width = 160,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handleMouseDown);
    return () => window.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          height: 32,
          borderRadius: 7,
          border: active ? "1px solid #3fa9f5" : "1px solid #444",
          background: active ? "rgba(63,169,245,0.10)" : "#111",
          color: active ? "#3fa9f5" : "#cfd8e3",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "0 9px 0 10px",
          fontSize: 12,
          fontWeight: 800,
          boxShadow: active
            ? "0 0 0 1px rgba(63,169,245,0.12), 0 0 12px rgba(63,169,245,0.10)"
            : "none",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            minWidth: 0,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selected?.label}
          </span>
        </span>
        <span
          style={{
            color: active ? "#3fa9f5" : "#aaa",
            fontSize: 12,
            lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: 36,
            left: 0,
            width: "100%",
            zIndex: 1000,
            background: "#151515",
            border: "1px solid #444",
            borderRadius: 7,
            overflow: "hidden",
            boxShadow: "0 14px 35px rgba(0,0,0,0.75)",
          }}
        >
          {options.map((opt) => {
            const selectedOpt = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: selectedOpt
                    ? "rgba(63,169,245,0.18)"
                    : "#151515",
                  color: selectedOpt ? "#3fa9f5" : "#ddd",
                  padding: "8px 10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (!selectedOpt) e.currentTarget.style.background = "#222";
                }}
                onMouseLeave={(e) => {
                  if (!selectedOpt) e.currentTarget.style.background = "#151515";
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    minWidth: 0,
                  }}
                >
                  <span>{opt.label}</span>
                </span>
                {selectedOpt && <span style={{ fontWeight: 900 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ========================== PAGE ========================== */
export default function Twitch() {
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "market" | "streamers"
  const [hasResolvedInitialDashboardLoad, setHasResolvedInitialDashboardLoad] = useState(false);
  const [sortBy, setSortBy] = useState("hours");
  const [marketShape, setMarketShape] = useState("squares");
  const [marketFavorites, setMarketFavorites] = useState([]);
  const [marketSplitView, setMarketSplitView] = useState(false);
  const [selectedLang, setSelectedLang] = useState("");
const chartExportRef = useRef(null);
const marketMindExportRef = useRef(null);
const [exportingChart, setExportingChart] = useState(false);
const [exportingMarketMind, setExportingMarketMind] = useState(false);
const marketDrawerWidth = 760; // locked wide drawer for 3-column streamer layout
const downloadDataUrl = (dataUrl, fileName) => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = fileName;
  a.click();
};

async function exportChartPanel({ longEdgePx, fileName }) {
  const node = chartExportRef.current;
  if (!node) return;

  setExportingChart(true);
  try {
    const rect = layoutRect(node);
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);

    // scale so the LONG edge becomes longEdgePx (keeps aspect ratio)
    const scale = longEdgePx / Math.max(w, h);

    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: Math.max(1, scale), // <- key: higher-res without changing layout

      // optional: ensure a solid background if anything is transparent
      backgroundColor: "#000",

      filter: (el) => {
        if (!el) return true;
        if (el.getAttribute?.("data-export-hide") === "true") return false;
        if (el.classList?.contains("recharts-tooltip-wrapper")) return false;
        return true;
      },
    });

    downloadDataUrl(dataUrl, fileName);
  } catch (e) {
    console.error("Export failed:", e);
  } finally {
    setExportingChart(false);
  }
}


async function exportMarketMindPanel({ longEdgePx = 3840, fileName }) {
  const node = marketMindExportRef.current;
  if (!node) return;

  setExportingMarketMind(true);
  try {
    const rect = layoutRect(node);
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    const scale = longEdgePx / Math.max(w, h);

    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: Math.max(1, scale),
      backgroundColor: "#000",
      filter: (el) => {
        if (!el) return true;
        if (el.getAttribute?.("data-export-hide") === "true") return false;
        return true;
      },
    });

    downloadDataUrl(dataUrl, fileName || `Twitch Market Mind - ${marketMetricLabel}.png`);
  } catch (e) {
    console.error("Market Mind export failed:", e);
  } finally {
    setExportingMarketMind(false);
  }
}


const handleExportAllGamesCsv = () => {
  if (viewMode === "streamers") {
    handleExportStreamersTableCsv();
    return;
  }

  if (!displayGames.length) return;

  const metricLabel = sortBy === "peak" ? "Peak Viewers" : "Hours Watched";
  const periodLabel = period === "7d" ? "7D" : period === "now" ? "Now" : "1D";

  const rows = displayGames.map((g, idx) => ({
    rank: idx + 1,
    game: g.name,
    hours_watched: (toHours(g.displayMinutes || 0)).toFixed(2),
    minutes_watched: g.displayMinutes || 0,
    peak_viewers: g.displayPeak || 0,
    language_filter: selectedLang || "all",
    period: periodLabel,
  }));

  const csv = Papa.unparse(rows);
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `mindGAME - Twitch ${metricLabel} - ${periodLabel}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

  // --- Twitch compare chart state (like Steam Player History) ---
  const [chartGames, setChartGames] = useState([]); 
  // [{ name, gameId, history: [{ dt, viewers }], loading: bool }]
  const [twitchHistoryGranularity, setTwitchHistoryGranularity] = useState("daily"); // "daily" | "monthly"
  const [pendingSavedChartState, setPendingSavedChartState] = useState(null);
  const [selectedChartGameId, setSelectedChartGameId] = useState(null);
  const [chartSidebarLoading, setChartSidebarLoading] = useState(false);
  const [chartSidebarError, setChartSidebarError] = useState("");
  const loadedSidebarKey = useRef(null);
  const [streamerRetryNonce, setStreamerRetryNonce] = useState(0);

  const chartGamesSet = useMemo(
  () => new Set(chartGames.map((g) => g.gameId)),
  [chartGames]
);

  const sortedChartGames = useMemo(() => {
    if (chartGames.length <= 1) return chartGames;
    return [...chartGames].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [chartGames]);

  const selectedChartGame = useMemo(() => {
    if (!chartGames.length) return null;
    return (
      chartGames.find((g) => String(g.gameId) === String(selectedChartGameId)) ||
      chartGames[0]
    );
  }, [chartGames, selectedChartGameId]);

  const [twitchValueMode, setTwitchValueMode] = useState("hours"); 
    // "hours" | "peak" | "channels"
  const [twitchCumulative, setTwitchCumulative] = useState(false); // hours: daily vs cumulative
  const [twitchChartHeight, setTwitchChartHeight] = useState(760);
  const [chartSidebarPct, setChartSidebarPct] = useState(33);
  const chartSplitRef = useRef(null);

  const handleChartResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const shell = chartSplitRef.current;
    if (!shell) return;

    const onMove = (moveEvent) => {
      const rect = layoutRect(shell);
      if (!rect.width) return;

      const x = layoutCoordinate(moveEvent.clientX) - rect.left;
      const next = Math.max(24, Math.min(48, (x / rect.width) * 100));
      setChartSidebarPct(next);
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleChartHeightResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const panel = chartExportRef.current;
    if (!panel) return;

    const rect = layoutRect(panel);
    const startY = layoutCoordinate(e.clientY);
    const startHeight = rect.height || twitchChartHeight;
    const minHeight = Math.min(760, Math.max(520, layoutViewportHeight() - rect.top - 16));

    const onMove = (moveEvent) => {
      const maxHeight = Math.max(minHeight, layoutViewportHeight() - rect.top - 16);
      const next = startHeight + (layoutCoordinate(moveEvent.clientY) - startY);
      setTwitchChartHeight(Math.max(minHeight, Math.min(maxHeight, next)));
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };


  // --- Custom tooltip: sort games by value desc for that period (Steam-style) ---
  const renderTwitchTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Sort series by value DESC for that time point
    const sorted = [...payload]
      .filter((p) => p && Number.isFinite(Number(p.value)))
      .sort((a, b) => Number(b.value) - Number(a.value));

    const dateLabel = fmtDateShort(label);
    const hasEstimate = sorted.some((p) =>
      String(p.dataKey || "").endsWith("__estimate") ||
      String(p.name || "").toLowerCase().includes("estimate")
    );

    return (
      <div
        style={{
          background: "#050608",
          border: "1px solid #333",
          borderRadius: 6,
          padding: "6px 10px 8px",
          fontSize: 12,
          color: "#f9fafb",
          minWidth: 180,
        }}
      >
        <div
          style={{
            fontWeight: 700,
            color: "#e5e7eb",
            marginBottom: hasEstimate ? 1 : 4,
          }}
        >
          {dateLabel}
        </div>
        {hasEstimate && (
          <div style={{ color: "#9ca3af", fontSize: 11, marginBottom: 5 }}>
            Current-month full-month estimate
          </div>
        )}

        {sorted.map((entry) => {
          const color = entry.color || entry.stroke || "#3fa9f5";
          const isEstimate = String(entry.dataKey || "").endsWith("__estimate") ||
            String(entry.name || "").toLowerCase().includes("estimate");
          const displayName = isEstimate
            ? String(entry.name || "").replace(/\s+estimate$/i, "")
            : entry.name;
          return (
            <div
              key={`${entry.dataKey || entry.name}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: isEstimate ? "transparent" : color,
                    border: isEstimate ? `1.5px solid ${color}` : "none",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displayName}{isEstimate ? " (Estimated)" : ""}
                </span>
              </div>
              <span
                style={{
                  fontWeight: 700,
                  marginLeft: 4,
                  flexShrink: 0,
                }}
              >
                {formatTwitchValue(entry.value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Period window for the chart (Steam-style slider)
  const [twWinStart, setTwWinStart] = useState(0);
  const [twWinEnd, setTwWinEnd] = useState(0);
  const twHasInitWindow = useRef(false);

  // Series highlight state (for multi-select)
  const [twHighlightedSeries, setTwHighlightedSeries] = useState(null); // string | string[] | null
  const [twMultiKeyDown, setTwMultiKeyDown] = useState(false);

useEffect(() => {
  if (twitchValueMode !== "hours" && twitchCumulative) {
    setTwitchCumulative(false);
  }
}, [twitchValueMode, twitchCumulative]);

const toggleTwitchSeriesHighlight = (seriesName) => {
  if (!seriesName) return;
  setTwHighlightedSeries((prev) => {
    const prevArr = Array.isArray(prev) ? prev : prev ? [prev] : [];
    const already = prevArr.includes(seriesName);

    if (twMultiKeyDown) {
      if (already) {
        const next = prevArr.filter((n) => n !== seriesName);
        return next.length ? next : null;
      }
      return [...prevArr, seriesName];
    }

    if (already && prevArr.length === 1) return null;
    return [seriesName];
  });
};

  // Track Ctrl/Cmd for multi-select behavior
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Control" || e.key === "Meta") {
        setTwMultiKeyDown(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === "Control" || e.key === "Meta") {
        setTwMultiKeyDown(false);
      }
    };
    const handleBlur = () => setTwMultiKeyDown(false);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // period toggle '1d' vs '7d'
  const [period, setPeriod] = useState("now"); // "now" | "1d" | "7d"
  const [liveGeneratedAt, setLiveGeneratedAt] = useState("");
  const [cardsRevision, setCardsRevision] = useState("");
  const [liveRefreshNonce, setLiveRefreshNonce] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarContentVisible, setSidebarContentVisible] = useState(true);
  const [sidebarButtonVisible, setSidebarButtonVisible] = useState(true);

const viewport = useViewportSize();

const twitchLayout = useMemo(() => {
  const sidebarWidth = viewport.width < 1280 ? 232 : viewport.width < 1536 ? 252 : 280;
  const mainPadX = 30;
  const mainPadY = viewport.width < 1280 ? 14 : viewport.width < 1536 ? 16 : 18;
  return {
    sidebarWidth,
    mainPadding: viewMode === "market"
      ? `${mainPadY}px 18px 0 18px`
      : `4px ${mainPadX}px ${Math.max(22, mainPadY + 8)}px ${mainPadX}px`,
    statFontSize: viewport.width < 1280 ? "1.06rem" : viewport.width < 1536 ? "1.16rem" : "1.30rem",
    headerGap: viewport.width < 1280 ? 7 : 9,
    logoWidth: viewport.width < 1280 ? 128 : viewport.width < 1536 ? 142 : 154,
    headerDropdownWidth: viewport.width < 1280 ? 132 : 152,
  };
}, [viewport.width, sidebarCollapsed, viewMode]);


  useEffect(() => {
    let contentTimer;
    let buttonTimer;

    // Hide the toggle completely while the sidebar width/layout is changing.
    // This prevents the button from being visible while React moves it between
    // the expanded footer position and the collapsed top position.
    setSidebarButtonVisible(false);

    if (sidebarCollapsed) {
      setSidebarContentVisible(false);

      // Wait until the rail transition is fully settled, then fade in the
      // compact expand button at its final top position.
      buttonTimer = window.setTimeout(() => setSidebarButtonVisible(true), 420);
    } else {
      // Let the rail expand first, then fade/slide the content in.
      contentTimer = window.setTimeout(() => setSidebarContentVisible(true), 210);

      // Fade the collapse button in only after the content/layout has settled.
      buttonTimer = window.setTimeout(() => setSidebarButtonVisible(true), 520);
    }

    return () => {
      if (contentTimer) window.clearTimeout(contentTimer);
      if (buttonTimer) window.clearTimeout(buttonTimer);
    };
  }, [sidebarCollapsed]);

  // Check the tiny manifest in every period; immutable game files are reused.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setLiveRefreshNonce((n) => n + 1);
    }, 60000);
    return () => window.clearInterval(timer);
  }, []);

  const [loading, setLoading] = useState(true);
  const [cardsError, setCardsError] = useState("");
  const hasLoadedGamesRef = useRef(false);
  const hasLoadedLiveDetailsRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hidden, setHidden] = useState(new Set());

  const [streamerSummary1d, setStreamerSummary1d] = useState(null);
  const [streamerSummary7d, setStreamerSummary7d] = useState(null);
  const [streamerSummaryComplete1d, setStreamerSummaryComplete1d] = useState(false);
  const [streamerSummaryComplete7d, setStreamerSummaryComplete7d] = useState(false);
  const [streamerSummaryError, setStreamerSummaryError] = useState("");

  const [streamerGames1d, setStreamerGames1d] = useState(null);
  const [streamerGames7d, setStreamerGames7d] = useState(null);
  const [streamerGamesComplete1d, setStreamerGamesComplete1d] = useState(false);
  const [streamerGamesComplete7d, setStreamerGamesComplete7d] = useState(false);
  const [streamerGamesLoading, setStreamerGamesLoading] = useState(false);
  const [streamerGamesError, setStreamerGamesError] = useState("");

  // Live / Now streamer table rows, loaded from Twitch_Live_JSON/latest_streamers.json.
  const [liveStreamerRows, setLiveStreamerRows] = useState([]);
  const [liveStreamerLoading, setLiveStreamerLoading] = useState(false);
  const [liveStreamerError, setLiveStreamerError] = useState("");
  const liveStreamerRowsRef = useRef([]);

  useEffect(() => {
    liveStreamerRowsRef.current = liveStreamerRows;
  }, [liveStreamerRows]);

  const streamerAvatarLookup = useMemo(
    () => buildStreamerAvatarLookup(streamerSummary1d, streamerSummary7d),
    [streamerSummary1d, streamerSummary7d]
  );


  const [games, setGames] = useState([]);
  const [streamersByGame, setStreamersByGame] = useState(new Map());
  const [streamersByGameLang, setStreamersByGameLang] = useState(new Map());
  const [languagesByGame, setLanguagesByGame] = useState(new Map());
  const [languagesTotals, setLanguagesTotals] = useState([]);
  const [langsReady, setLangsReady] = useState(false);
  const [languageLoadRequested, setLanguageLoadRequested] = useState(false);

  const [selectedGame, setSelectedGame] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [selectedGameImage, setSelectedGameImage] = useState("");

  const mainRef = useRef(null);
  const [selectedBubble, setSelectedBubble] = useState(null);

     // 🔹 Period-specific filter metadata state
const [igdbMetaById, setIgdbMetaById] = useState(new Map()); // igdb_id -> compact metadata row
const [twitchMetaById, setTwitchMetaById] = useState(new Map()); // twitch game_id -> compact metadata row
const [selectedPublishers, setSelectedPublishers] = useState(new Set());
const [selectedDevelopers, setSelectedDevelopers] = useState(new Set());
const [selectedGenres, setSelectedGenres] = useState(new Set());
const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
const [releaseDateRange, setReleaseDateRange] = useState([null, null]); // [min,max]
const [selectedDateRange, setSelectedDateRange] = useState([null, null]); // [from,to]
const [igdbReady, setIgdbReady] = useState(false);
const [selectedKeywords, setSelectedKeywords] = useState(new Set());
const [selectedFranchises, setSelectedFranchises] = useState(new Set());

// 🔹 Top filter popover state (same pattern as Steam)
const [filtersOpen, setFiltersOpen] = useState(false);
const filterCloseTimerRef = useRef(null);
// 🔹 Expanding header search (same pattern as mindSHARE)
const [headerControlsTarget, setHeaderControlsTarget] = useState(null);
const [headerStatsTarget, setHeaderStatsTarget] = useState(null);
const [headerToolsTarget, setHeaderToolsTarget] = useState(null);

// 🔹 Popular filter: only show new releases (the gold-highlighted cards)
const [newReleasesOnly, setNewReleasesOnly] = useState(false);

const [publisherStats, setPublisherStats] = useState([]);
const [developerStats, setDeveloperStats] = useState([]);
const [genreStats, setGenreStats] = useState([]);
const [platformStats, setPlatformStats] = useState([]);
const [keywordStats, setKeywordStats] = useState([]);
const [franchiseStats, setFranchiseStats] = useState([]);


useEffect(() => {
  setHeaderControlsTarget(document.getElementById("twitch-header-controls"));
  setHeaderStatsTarget(document.getElementById("twitch-header-stats"));
  const findToolsTarget = () => setHeaderToolsTarget(document.getElementById("twitch-header-saved-controls"));
  findToolsTarget();
  const observer = new MutationObserver(findToolsTarget);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
  };
}, []);

 useEffect(() => {
  if (selectedLang) {
    setLanguageLoadRequested(true);
  }
}, [selectedLang]);

const handleResetGames = () => {
  setMarketShape("squares");
  setMarketFavorites([]);
  setMarketSplitView(false);
  setHidden(new Set());
  setSearchTerm("");
  setSelectedLang("");
  setSelectedGame(null);
  setSelectedBubble(null);
  setNewReleasesOnly(false);

  setSelectedPublishers(new Set());
  setSelectedDevelopers(new Set());
  setSelectedGenres(new Set());
  setSelectedPlatforms(new Set());
  setSelectedKeywords(new Set());
  setSelectedFranchises(new Set());
  setSelectedDateRange([null, null]);
  setChartGames([]);
  setSelectedChartGameId(null);
  setTwHighlightedSeries(null);
  twHasInitWindow.current = false;
  setTwWinStart(0);
  setTwWinEnd(0);
};


  const handleAddToChart = async (game) => {
  if (!game?.name || !game?.id) return;

  const name = game.name;
  const gameId = String(game.id);

  if (chartGames.some((g) => g.gameId === gameId)) {
    setSelectedChartGameId(gameId);
    setTwHighlightedSeries([name]);
    return;
  }

  // 🔹 Look up compact metadata for this game, and pull wide_art_url
  let wideArtUrl = "";
  const meta = getGameMeta(game, igdbMetaById, twitchMetaById);

  if (meta) {
    wideArtUrl =
      meta.wide_art_url ||
      meta.wide_art ||
      meta.hero_art_url ||
      "";
  }

  // 🔹 Fallbacks if IGDB hero art is missing
  if (!wideArtUrl) {
    wideArtUrl = game.imageUrl || "";
  }

  // optimistic placeholder with hero art
  setChartGames((prev) => [
    ...prev,
    {
      name,
      gameId,
      wideArtUrl,
      imageUrl: game.imageUrl || "",
      igdbId: game.igdbId || "",
      history: [],
      loading: true,
    },
  ]);

  setSelectedChartGameId((prev) => prev || gameId);
  setTwHighlightedSeries([name]);

  try {
    const history = await loadTwitchChartHistoryByGameId(gameId, twitchHistoryGranularity);
    setChartGames((prev) =>
      prev.map((g) =>
        g.gameId === gameId ? { ...g, history, loading: false } : g
      )
    );
  } catch (e) {
    console.error("Twitch history load failed:", e);
    setChartGames((prev) =>
      prev.map((g) =>
        g.gameId === gameId ? { ...g, loading: false } : g
      )
    );
  }
};



  const handleRemoveFromChart = (gameIdOrName) => {
  setChartGames((prev) => {
    const next = prev.filter((g) => g.gameId !== gameIdOrName && g.name !== gameIdOrName);
    const removedSelected =
      String(selectedChartGameId || "") === String(gameIdOrName || "") ||
      prev.find((g) => g.name === gameIdOrName && String(g.gameId) === String(selectedChartGameId || ""));

    if (removedSelected) {
      const fallback = next[0] || null;
      setSelectedChartGameId(fallback ? fallback.gameId : null);
      setTwHighlightedSeries(fallback ? [fallback.name] : null);
    }

    return next;
  });
};

  const handleToggleChartGame = (game) => {
    if (!game?.name) return;
    if (chartGamesSet.has(String(game.id))) {
      // already in chart → remove
      handleRemoveFromChart(String(game.id));
    } else {
      // not in chart → add
      handleAddToChart(game);
    }
  };


  const TWITCH_CHART_COLORS = [
    "#3fa9f5",
    "#F5E6C8",
    "#ff9b70",
    "#33c3eb",
    "#f1726a",
    "#66CCFF",
  ];


  const twitchChartColorById = useMemo(() => {
    const map = new Map();
    chartGames.forEach((g, idx) => {
      map.set(String(g.gameId), TWITCH_CHART_COLORS[idx % TWITCH_CHART_COLORS.length]);
    });
    return map;
  }, [chartGames]);

  const getTwitchChartColor = (gameId, fallbackIndex = 0) =>
    twitchChartColorById.get(String(gameId)) ||
    TWITCH_CHART_COLORS[fallbackIndex % TWITCH_CHART_COLORS.length];


const chartPillStyle = (active) => ({
  height: 32,
  padding: "0 12px",
  border: "none",
  borderRadius: 7,
  background: active ? "rgba(63,169,245,0.18)" : "#111",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 12,
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: active ? "inset 0 0 0 1px rgba(63,169,245,0.20)" : "none",
});

const chartChipStyle = {
  height: 28,
  borderRadius: 7,
  border: "1px solid #444",
  background: "#111",
  color: "#cfd8e3",
  padding: "0 10px",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  maxWidth: 200,
};

const twitchChartData = useMemo(() => {
  if (!chartGames.length) return [];

  const bucket = new Map(); // dt -> row

  chartGames.forEach((g) => {
    (g.history || []).forEach((pt) => {
      const rawKey = getHistoryPointKey(pt);
      if (!rawKey) return;

      // Monthly history export stores closed months as YYYY-MM-01 while the
      // current-month estimate uses YYYY-MM. Normalize all monthly chart x
      // values to YYYY-MM so May actual and May/June estimate anchors share
      // one continuous monthly axis instead of creating duplicate date buckets.
      const key = twitchHistoryGranularity === "monthly"
        ? (normalizeMonthKey(rawKey) || rawKey)
        : rawKey;

      let row = bucket.get(key);
      if (!row) {
        row = { x: key };
        bucket.set(key, row);
      }

      const minutes = Number(
        pt.total_minutes ??
          pt.viewtime_minutes ??
          pt.minutes ??
          (pt.hours_watched != null ? Number(pt.hours_watched) * 60 : 0)
      );
      const peak = Number(pt.max_viewers ?? pt.peak_viewers ?? pt.viewers ?? 0);
      const channels = Number(
        pt.channels_streamed ??
          pt.channels ??
          pt.streamers ??
          pt.active_streamers ??
          0
      );

      const value =
        twitchValueMode === "peak"
          ? peak
          : twitchValueMode === "channels"
          ? channels
          : minutes / 60; // daily hours watched (NOT cumulative)

      const isEstimate = !!pt.is_current_month_estimate;
      const isZeroAnchor = !!pt.is_zero_anchor;
      if ((isEstimate || isZeroAnchor) && twitchHistoryGranularity === "monthly" && twitchValueMode === "hours") {
        // Estimates render as a separate dotted series. For brand-new games,
        // the previous-month zero anchor belongs to that dotted estimate line,
        // not the filled actual Area series.
        row[`${g.gameId}__estimate`] = Number.isFinite(value) && value >= 0 ? value : 0;
        row[g.gameId] = null;
      } else {
        row[g.gameId] = Number.isFinite(value) && value > 0 ? value : 0;
      }
    });
  });

  const rows = Array.from(bucket.values()).sort((a, b) => {
    const ad = parseHistoryDate(a.x);
    const bd = parseHistoryDate(b.x);
    return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
  });

  if (twitchHistoryGranularity === "monthly" && twitchValueMode === "hours") {
    sortedChartGames.forEach((g) => {
      const actualKey = g.gameId;
      const estimateKey = `${g.gameId}__estimate`;
      const estimateIndex = rows.findIndex((row) => Number(row[estimateKey] || 0) > 0);
      if (estimateIndex > 0) {
        for (let i = estimateIndex - 1; i >= 0; i -= 1) {
          const prevActual = Number(rows[i][actualKey]);
          if (rows[i][actualKey] != null && Number.isFinite(prevActual) && prevActual >= 0) {
            rows[i][estimateKey] = prevActual;
            break;
          }
        }
      }
    });
  }

  // ❌ no cumulative transform here – leave rows as daily values
  return rows;
}, [chartGames, twitchValueMode, twitchHistoryGranularity, sortedChartGames]);


// Windowed data for the chart (Steam-style slider)
const twitchRenderData = useMemo(() => {
  const len = twitchChartData.length;
  if (!len) return [];

  const s = Math.max(0, Math.min(twWinStart, len - 1));
  const e = Math.max(s, Math.min(twWinEnd, len - 1));

  // Base slice = raw daily values for the current window
  const windowRows = twitchChartData.slice(s, e + 1);
  if (!windowRows.length) return [];

  // If not in cumulative-hours mode, just use the raw window
  if (!(twitchValueMode === "hours" && twitchCumulative)) {
    return windowRows;
  }

  // 🔹 Cumulative (Hours Watched) over the *window only*
  const acc = Object.create(null); // gameId -> running total

  return windowRows.map((row) => {
    const out = { ...row };
    sortedChartGames.forEach((g) => {
      const key = g.gameId;
      const estimateKey = `${g.gameId}__estimate`;
      const rawActual = Number(row[key]);
      const rawEstimate = Number(row[estimateKey]);

      if (!acc[key]) acc[key] = 0;

      if (Number.isFinite(rawActual) && rawActual > 0) {
        acc[key] += rawActual;
        out[key] = acc[key];
      } else if (row[key] == null) {
        out[key] = null;
      } else {
        out[key] = acc[key];
      }

      // Monthly estimate line:
      // - on the prior closed month, connect from the cumulative actual value
      // - on the current estimated month, connect to cumulative actual + estimated month
      if (
        twitchHistoryGranularity === "monthly" &&
        Number.isFinite(rawEstimate) &&
        rawEstimate > 0
      ) {
        out[estimateKey] =
          Number.isFinite(rawActual) && rawActual > 0
            ? acc[key]
            : acc[key] + rawEstimate;
      }
    });
    return out;
  });
}, [
  twitchChartData,
  twWinStart,
  twWinEnd,
  twitchValueMode,
  twitchCumulative,
  sortedChartGames,
]);

useEffect(() => {
  const len = twitchChartData.length;

  if (!len) {
    // No data yet → reset window + flag
    twHasInitWindow.current = false;
    setTwWinStart(0);
    setTwWinEnd(0);
    return;
  }

  if (!twHasInitWindow.current) {
    // First time we get data → show the full window
    setTwWinStart(0);
    setTwWinEnd(len - 1);
    twHasInitWindow.current = true;
  } else {
    // Data length changed (e.g. extra days added) → clamp existing window
    setTwWinStart((prev) =>
      Math.max(0, Math.min(prev, Math.max(0, len - 2)))
    );
    setTwWinEnd((prev) =>
      Math.max(1, Math.min(prev, Math.max(1, len - 1)))
    );
  }
}, [twitchChartData.length]);

const labelForTwIdx = (idx) => {
  const len = twitchChartData.length;
  if (!len) return "";
  const safeIdx = Math.min(Math.max(idx, 0), len - 1);
  const x = twitchChartData[safeIdx].x;
  return fmtDateShort(x);
};

  // 🔹 Chart background art: show wide_art_url when exactly one game is selected
  const twitchChartBgArt =
    chartGames.length === 1
      ? chartGames[0].wideArtUrl || ""
      : "";


  const fmtDateShort = (dt) => {
  const s = String(dt || "");
  const dayMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayMatch) {
    const [, yyyy, mm, dd] = dayMatch;
    return `${mm}/${dd}/${yyyy.slice(-2)}`;
  }

  const monthMatch = s.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    const [, yyyy, mm] = monthMatch;
    const d = new Date(Number(yyyy), Number(mm) - 1, 1);
    const mon = d.toLocaleString("en-US", { month: "short" });
    return `${mon} '${yyyy.slice(-2)}`;
  }

  return s;
};

const formatTwitchValue = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num)) return "-";

  if (twitchValueMode === "peak") {
    return `${new Intl.NumberFormat("en-US").format(Math.round(num))} viewers`;
  }

  if (twitchValueMode === "channels") {
    return `${new Intl.NumberFormat("en-US").format(Math.round(num))} channels`;
  }

  // hours watched
  const h = num < 10 ? num.toFixed(1) : new Intl.NumberFormat("en-US").format(Math.round(num));
  return `${h} H`;
};

const formatTwitchAxisValue = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num)) return "-";
  return new Intl.NumberFormat("en-US").format(Math.round(num));
};


  // simple pill base style for header toggles
  const pillBase = {
    border: "none",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    transition: "background 0.15s ease",
  };


  useEffect(() => {
    if (viewMode === "streamers" && period === "now" && sortBy !== "hours") {
      setSortBy("hours");
    }
  }, [viewMode, period, sortBy]);

const handleViewCards = () => {
  if (viewMode === "cards") return;
  setViewMode("cards");
  setSelectedBubble(null);
};

const handleViewMarket = () => {
  if (viewMode === "market") return;
  setViewMode("market");
  setSelectedBubble(null);
};

const handleViewStreamers = () => {
  if (viewMode === "streamers") return;
  setViewMode("streamers");
  setSelectedBubble(null);
  setChartGames([]);
  setSelectedChartGameId(null);
  setTwHighlightedSeries(null);
};


  // reset period-specific caches/state when period changes.
  // Do NOT clear `hidden` here: games removed with the card X should stay removed
  // when switching between Cards and Market Mind, until the user clicks Reset.
  useEffect(() => {
    hasLoadedGamesRef.current = false;
    hasLoadedLiveDetailsRef.current = false;
    setSelectedLang("");
    setLanguagesByGame(new Map());
    setLanguagesTotals([]);
    setLangsReady(false);
    setLanguageLoadRequested(false);
    setStreamersByGame(new Map());
    setStreamersByGameLang(new Map());

    setSelectedBubble(null);
  }, [period]);

  // Keep active card filters/removals when entering Market Mind.
  useEffect(() => {
    if (viewMode !== "market") return;
    setSelectedBubble(null);
  }, [viewMode]);

 useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const silentLiveRefresh = hasLoadedGamesRef.current;
      if (!silentLiveRefresh) setLoading(true);
      const snapshot = await loadTwitchCards(period);
      if (cancelled) return;
      setCardsError("");
      setLiveGeneratedAt(snapshot.generatedAt);
      setCardsRevision(snapshot.revision);
      setGames(previous => {
        const next = snapshot.games.map(game => ({ ...game, imageUrl: normalizeBoxArt(game.imageUrl) }));
        return previous.length === next.length && previous.every((game, i) =>
          game.id === next[i].id && game.viewMinutes === next[i].viewMinutes &&
          game.peak === next[i].peak && game.activeStreamers === next[i].activeStreamers &&
          game.imageUrl === next[i].imageUrl && game.name === next[i].name &&
          game.igdbId === next[i].igdbId && game.releaseDate === next[i].releaseDate
        ) ? previous : next;
      });
    } catch (error) {
      if (!cancelled) {
        console.error("Compact Twitch cards failed:", error);
        setCardsError("Twitch cards could not be refreshed. Please retry.");
      }
    } finally {
      if (!cancelled) {
        hasLoadedGamesRef.current = true;
        setLoading(false);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [period, liveRefreshNonce]);

     // 3) Period-specific filter metadata: compact JSON, not full IGDB CSV
useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      setIgdbReady(false);

      const payload = await loadFilterMetadata(period);
      const normalized = normalizeFilterMetadataPayload(payload);

      if (cancelled) return;

      setIgdbMetaById(normalized.metaMap);
      setTwitchMetaById(normalized.twitchMetaMap);

      setPublisherStats(normalized.publisherStats);
      setDeveloperStats(normalized.developerStats);
      setGenreStats(normalized.genreStats);
      setPlatformStats(normalized.platformStats);
      setKeywordStats(normalized.keywordStats);
      setFranchiseStats(normalized.franchiseStats);

      if (normalized.minDate && normalized.maxDate) {
        setReleaseDateRange([normalized.minDate, normalized.maxDate]);
      } else {
        setReleaseDateRange([null, null]);
      }

      setIgdbReady(true);

      console.log("[Twitch] filter metadata loaded", {
        period,
        metaByIgdb: normalized.metaMap.size,
        metaByTwitch: normalized.twitchMetaMap.size,
        publishers: normalized.publisherStats.length,
        developers: normalized.developerStats.length,
        genres: normalized.genreStats.length,
        platforms: normalized.platformStats.length,
        keywords: normalized.keywordStats.length,
        franchises: normalized.franchiseStats.length,
      });
    } catch (e) {
      if (!cancelled) {
        console.error("Twitch filter metadata load failed:", e);
        setIgdbMetaById(new Map());
        setTwitchMetaById(new Map());
        setPublisherStats([]);
        setDeveloperStats([]);
        setGenreStats([]);
        setPlatformStats([]);
        setKeywordStats([]);
        setFranchiseStats([]);
        setReleaseDateRange([null, null]);
        setIgdbReady(false);
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [period, cardsRevision]);


  // Rebuild filter rollups from the compact per-game metadata joined to the currently
  // loaded Twitch period. This preserves the old dropdown behavior:
  // "Publisher — Hours • games" instead of a plain alphabetical metadata list.
  useEffect(() => {
    if (!games.length || (!igdbMetaById.size && !twitchMetaById.size)) {
      setPublisherStats([]);
      setDeveloperStats([]);
      setGenreStats([]);
      setPlatformStats([]);
      setKeywordStats([]);
      setFranchiseStats([]);
      setReleaseDateRange([null, null]);
      return;
    }

    const makeAcc = () => new Map();
    const pubs = makeAcc();
    const devs = makeAcc();
    const genres = makeAcc();
    const plats = makeAcc();
    const keywords = makeAcc();
    const franchises = makeAcc();

    let minDate = null;
    let maxDate = null;

    const addValues = (acc, values, metricValue) => {
      values.forEach((name) => {
        if (!name || String(name).toLowerCase() === "nan") return;
        const prev = acc.get(name) || { name, minutes: 0, count: 0 };
        prev.minutes += Number(metricValue || 0);
        prev.count += 1;
        acc.set(name, prev);
      });
    };

    for (const g of games) {
      const meta = getGameMeta(g, igdbMetaById, twitchMetaById);
      if (!meta) continue;

      // For 1D/7D this is viewtime minutes. For Now this is current viewers;
      // keep it in the same numeric bucket and format labels based on period below.
      const metricValue =
        period === "now"
          ? Number(g.currentViewers ?? g.viewMinutes ?? 0) || 0
          : Number(g.viewMinutes || 0) || 0;

      addValues(pubs, splitMulti(meta.publishers || meta.publisher || ""), metricValue);
      addValues(devs, splitMulti(meta.developers || meta.developer || ""), metricValue);
      addValues(genres, splitMulti(meta.genres || meta.genre || ""), metricValue);
      addValues(plats, splitMulti(meta.platforms || meta.platform || ""), metricValue);
      addValues(keywords, splitMulti(meta.keywords || meta.keyword || ""), metricValue);
      addValues(franchises, splitMulti(meta.franchises || meta.franchise || ""), metricValue);

      const rd = safeDate(meta.first_release_date) || safeDate(meta.release_date);
      if (rd) {
        if (!minDate || rd < minDate) minDate = rd;
        if (!maxDate || rd > maxDate) maxDate = rd;
      }
    }

    const sortStats = (acc) =>
      Array.from(acc.values()).sort(
        (a, b) =>
          Number(b.minutes || 0) - Number(a.minutes || 0) ||
          Number(b.count || 0) - Number(a.count || 0) ||
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );

    setPublisherStats(sortStats(pubs));
    setDeveloperStats(sortStats(devs));
    setGenreStats(sortStats(genres));
    setPlatformStats(sortStats(plats));
    setKeywordStats(sortStats(keywords));
    setFranchiseStats(sortStats(franchises));
    setReleaseDateRange(minDate && maxDate ? [minDate, maxDate] : [null, null]);
  }, [games, igdbMetaById, twitchMetaById, period]);

  // ---- Filter dropdown options from compact metadata export ----

  const genreOptions = useMemo(() => {
    const nf = new Intl.NumberFormat("en-US");
    return genreStats.map(({ name, count }) => ({
      label: count ? `${name} — ${nf.format(count)} games` : name,
      value: name,
    }));
  }, [genreStats]);

  const publisherOptions = useMemo(() => {
    const nf = new Intl.NumberFormat("en-US");
    return publisherStats.map(({ name, minutes, count }) => ({
      value: name,
      label:
        period === "now"
          ? `${name} — ${fmtInt(minutes)} Viewers • ${nf.format(count)} games`
          : `${name} — ${fmtHours(toHours(minutes))} Hours • ${nf.format(count)} games`,
    }));
  }, [publisherStats, period]);

  const developerOptions = useMemo(() => {
    const nf = new Intl.NumberFormat("en-US");
    return developerStats.map(({ name, minutes, count }) => ({
      value: name,
      label:
        period === "now"
          ? `${name} — ${fmtInt(minutes)} Viewers • ${nf.format(count)} games`
          : `${name} — ${fmtHours(toHours(minutes))} Hours • ${nf.format(count)} games`,
    }));
  }, [developerStats, period]);

  const platformOptions = useMemo(() => {
    const nf = new Intl.NumberFormat("en-US");
    return platformStats.map(({ name, minutes, count }) => ({
      value: name,
      label:
        period === "now"
          ? `${name} — ${fmtInt(minutes)} Viewers • ${nf.format(count)} games`
          : `${name} — ${fmtHours(toHours(minutes))} Hours • ${nf.format(count)} games`,
    }));
  }, [platformStats, period]);

  const languageOptions = useMemo(
    () =>
      languagesTotals.slice(0, 100).map((l) => ({
        value: canonicalLang(l.language),
        label:
          period === "now"
            ? `${langLabel(l.language)} — ${fmtInt(l.minutes)} viewers`
            : `${langLabel(l.language)} — ${fmtHours(toHours(l.minutes))} h`,
      })),
    [languagesTotals, period]
  );

const keywordOptions = useMemo(() => {
  const nf = new Intl.NumberFormat("en-US");
  return keywordStats.map(({ name, count }) => ({
    value: name,
    label: count ? `${name} — ${nf.format(count)} games` : name,
  }));
}, [keywordStats]);

const franchiseOptions = useMemo(
  () =>
    franchiseStats.map((f) => ({
      value: f.name,
      label: `${f.name}${f.count ? ` — ${f.count.toLocaleString()} games` : ""}`,
    })),
  [franchiseStats]
);

  // 2) Language data now comes from precomputed JSON exports:
  // Twitch_Language_JSON/{now|1d|7d}/language_summary.json.gz
  // This replaces browser-side parsing/grouping of latest_languages.json and
  // daily/weekly language CSVs for filters and game language details.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLangsReady(false);

        const payload = await loadLanguageSummary(period, {
          forceFresh: period === "now",
        });
        if (cancelled) return;

        const { byGameMap, totalsArr } = normalizeLanguageSummaryPayload(payload);
        setLanguagesByGame(byGameMap);
        setLanguagesTotals(totalsArr);
        setLangsReady(true);
      } catch (e) {
        if (!cancelled) {
          console.error("Twitch language summary load failed:", e);
          setLanguagesByGame(new Map());
          setLanguagesTotals([]);
          setLangsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [period, liveRefreshNonce]);

  // Keep live streamer pages separate from language loading so the first page
  // can render before the remaining pages arrive.
  const needsLiveStreamerData = period === "now" && viewMode === "streamers";

  useEffect(() => {
    if (!needsLiveStreamerData) return;

    let cancelled = false;
    const hadLiveRows = liveStreamerRowsRef.current.length > 0;
    const refreshedRows = [];

    (async () => {
      try {
        setLiveStreamerLoading(true);
        setLiveStreamerError("");
        await loadTwitchLiveStreamers(rows => {
          if (cancelled) return;
          const liveRows = [];
          for (const r of rows) {
            const gameName = String(r.game_name || "").trim();
            if (!gameName) continue;

            const langTok = canonicalLang(r.language) || "unknown";
            const currentViewers = Number(r.current_viewers || 0) || 0;
            const userName = String(r.user_name || "").trim();
            const liveRawRow = {
              ...r,
              user_id: String(r.user_id || ""),
              user_name: userName,
              display_name: userName,
            };
            const profileImageUrl = getStreamerAvatarUrl(
              hydrateStreamerAvatar(liveRawRow, streamerAvatarLookup)
            );
            const row = {
              user_id: String(r.user_id || ""),
              user_name: userName,
              display_name: userName,
              profile_image_url: profileImageUrl,
              currentViewers: currentViewers,
              viewMinutes: 0,
              peak: currentViewers,
              title: r.title || "",
              language: langTok,
              game_id: String(r.game_id || ""),
              game_name: gameName,
              twitch_url: r.twitch_url ||
                (userName ? `https://www.twitch.tv/${encodeURIComponent(userName)}` : ""),
            };
            liveRows.push({
              ...row,
              rank: 0,
              current_viewers: currentViewers,
              hours_watched: currentViewers,
              peak_viewers: currentViewers,
              games_streamed: 1,
              top_game_name: gameName,
              top_games: [{
                game_id: String(r.game_id || ""),
                game_name: gameName,
                hours_watched: currentViewers,
                peak_viewers: currentViewers,
              }],
            });
          }
          refreshedRows.push(...liveRows);
          if (!hadLiveRows && liveRows.length) {
            setLiveStreamerRows(previous => [...previous, ...liveRows]);
          }
        }, { shouldContinue: () => !cancelled });
        if (cancelled) return;
        if (hadLiveRows) setLiveStreamerRows(refreshedRows);
        hasLoadedLiveDetailsRef.current = true;
      } catch (e) {
        if (!cancelled) {
          console.error("Live streamer load failed:", e);
          setLiveStreamerError("Live streamers could not be loaded. Please retry.");
        }
      } finally {
        if (!cancelled) setLiveStreamerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    period,
    needsLiveStreamerData,
    streamerAvatarLookup,
    liveRefreshNonce,
    streamerRetryNonce,
  ]);


  const gameArtByName = useMemo(() => {
    const map = new Map();
    for (const g of games) {
      const key = normalizeGameName(g.name);
      if (key && g.imageUrl) map.set(key, g.imageUrl);
    }
    return map;
  }, [games]);

  const streamerAllowedGameNames = useMemo(() => {
    const hasGameFilter =
      hidden.size > 0 ||
      selectedPublishers.size > 0 ||
      selectedDevelopers.size > 0 ||
      selectedGenres.size > 0 ||
      selectedPlatforms.size > 0 ||
      selectedKeywords.size > 0 ||
      selectedFranchises.size > 0 ||
      (selectedDateRange[0] != null && selectedDateRange[1] != null);

    if (!hasGameFilter) return null;

    const allowed = new Set();

    for (const g of games) {
      if (hidden.has(g.name)) continue;

      const meta = getGameMeta(g, igdbMetaById, twitchMetaById);

      if (
        selectedPublishers.size > 0 ||
        selectedDevelopers.size > 0 ||
        selectedGenres.size > 0 ||
        selectedPlatforms.size > 0 ||
        selectedKeywords.size > 0 ||
        selectedFranchises.size > 0 ||
        (selectedDateRange[0] != null && selectedDateRange[1] != null)
      ) {
        if (!meta) continue;

        const pubs = splitMulti(meta.publishers || "");
        const devs = splitMulti(meta.developers || "");
        const genres = splitMulti(meta.genres || "");
        const plats = splitMulti(meta.platforms || "");
        const keywords = splitMulti(meta.keywords || "");
        const franchise = (meta.franchises || "").trim();
        const rd = safeDate(meta.first_release_date) || safeDate(meta.release_date);

        if (selectedPublishers.size > 0 && !pubs.some((p) => selectedPublishers.has(p))) continue;
        if (selectedDevelopers.size > 0 && !devs.some((d) => selectedDevelopers.has(d))) continue;
        if (selectedGenres.size > 0 && !genres.some((gn) => selectedGenres.has(gn))) continue;
        if (selectedPlatforms.size > 0 && !plats.some((pl) => selectedPlatforms.has(pl))) continue;
        if (selectedKeywords.size > 0 && !keywords.some((kw) => selectedKeywords.has(kw))) continue;
        if (selectedFranchises.size > 0 && (!franchise || !selectedFranchises.has(franchise))) continue;
        if (selectedDateRange[0] != null && selectedDateRange[1] != null) {
          const [from, to] = selectedDateRange;
          if (!rd || rd < from || rd > to) continue;
        }
      }

      const key = normalizeGameName(g.name);
      if (key) allowed.add(key);
    }

    return allowed;
  }, [
    games,
    hidden,
    selectedPublishers,
    selectedDevelopers,
    selectedGenres,
    selectedPlatforms,
    selectedKeywords,
    selectedFranchises,
    selectedDateRange,
    igdbMetaById,
    twitchMetaById,
    twitchHistoryGranularity,
  ]);


  const activeStreamerSummary = period === "7d" ? streamerSummary7d : streamerSummary1d;
  const activeStreamerGameRows = period === "7d" ? streamerGames7d : streamerGames1d;
  const activeStreamerSummaryComplete = period === "7d" ? streamerSummaryComplete7d : streamerSummaryComplete1d;
  const activeStreamerGameRowsComplete = period === "7d" ? streamerGamesComplete7d : streamerGamesComplete1d;

  const streamerRequiresFilteredDetails = !!streamerAllowedGameNames || !!searchTerm.trim();
  const streamerNeedsGameRows = useMemo(
    () => viewMode === "streamers" && period !== "now" && streamerRequiresFilteredDetails,
    [viewMode, period, streamerRequiresFilteredDetails]
  );

  useEffect(() => {
    if (viewMode !== "streamers") return;
    if (period === "now") return;

    const cached = period === "7d" ? streamerSummary7d : streamerSummary1d;
    const complete = period === "7d" ? streamerSummaryComplete7d : streamerSummaryComplete1d;
    if (cached && complete) return;

    let cancelled = false;
    const setRows = period === "7d" ? setStreamerSummary7d : setStreamerSummary1d;
    const setComplete = period === "7d" ? setStreamerSummaryComplete7d : setStreamerSummaryComplete1d;

    (async () => {
      try {
        setStreamerSummaryError("");
        setRows(null);
        setComplete(false);
        const result = await loadTwitchTablePages(
          `leaderboards/${period}/summary`,
          rows => {
            if (!cancelled) setRows(previous => [...(previous || []), ...rows]);
          },
          { shouldContinue: () => !cancelled }
        );
        if (!cancelled) setComplete(result.complete);
      } catch (err) {
        if (!cancelled) {
          console.error("Streamer summary load failed:", err);
          setStreamerSummaryError(err.message || "Failed to load streamer table.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewMode, period, streamerRetryNonce]);

  useEffect(() => {
    if (!streamerNeedsGameRows) return;
    if (period === "now") return;

    const cached = period === "7d" ? streamerGames7d : streamerGames1d;
    const complete = period === "7d" ? streamerGamesComplete7d : streamerGamesComplete1d;
    if (cached && complete) return;

    let cancelled = false;
    const setRows = period === "7d" ? setStreamerGames7d : setStreamerGames1d;
    const setComplete = period === "7d" ? setStreamerGamesComplete7d : setStreamerGamesComplete1d;

    (async () => {
      try {
        setStreamerGamesLoading(true);
        setStreamerGamesError("");
        setRows(null);
        setComplete(false);
        const result = await loadTwitchTablePages(
          `leaderboards/${period}/games`,
          rows => {
            if (!cancelled) setRows(previous => [...(previous || []), ...rows]);
          },
          { shouldContinue: () => !cancelled }
        );
        if (!cancelled) setComplete(result.complete);
      } catch (err) {
        if (!cancelled) {
          console.error("Streamer-game table load failed:", err);
          setStreamerGamesError(err.message || "Failed to load streamer-game table.");
        }
      } finally {
        if (!cancelled) {
          setStreamerGamesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [streamerNeedsGameRows, period, streamerRetryNonce]);

  const displayStreamers = useMemo(() => {
    const summaryRows = Array.isArray(activeStreamerSummary)
      ? activeStreamerSummary
      : [];

    const q = searchTerm.trim().toLowerCase();

    if (period === "now") {
      const rows = (Array.isArray(liveStreamerRows) ? liveStreamerRows : [])
        .filter((s) => {
          const userName = String(s.user_name || s.display_name || "").toLowerCase();
          const gameName = String(s.game_name || s.top_game_name || "").toLowerCase();
          const gameKey = normalizeGameName(s.game_name || s.top_game_name || "");

          if (streamerAllowedGameNames) {
            if (!gameKey || !streamerAllowedGameNames.has(gameKey)) return false;
          }

          if (!q) return true;
          return userName.includes(q) || gameName.includes(q);
        })
        .map((s) => {
          s = hydrateStreamerAvatar(s, streamerAvatarLookup);
          const gameName = s.game_name || s.top_game_name || "";
          const art = gameName ? gameArtByName.get(normalizeGameName(gameName)) || "" : "";
          return {
            ...s,
            hours_watched: Number(s.current_viewers || s.currentViewers || 0),
            peak_viewers: Number(s.current_viewers || s.currentViewers || 0),
            games_streamed: 1,
            top_game_name: gameName,
            top_games: [
              {
                game_id: s.game_id || "",
                game_name: gameName,
                box_art_url: art,
                hours_watched: Number(s.current_viewers || s.currentViewers || 0),
                peak_viewers: Number(s.current_viewers || s.currentViewers || 0),
              },
            ],
          };
        });

      rows.sort((a, b) => {
        const av =
          sortBy === "peak"
            ? Number(a.peak_viewers || 0)
            : Number(a.current_viewers || a.hours_watched || 0);
        const bv =
          sortBy === "peak"
            ? Number(b.peak_viewers || 0)
            : Number(b.current_viewers || b.hours_watched || 0);

        if (av === bv) {
          return String(a.user_name || a.display_name || "").localeCompare(
            String(b.user_name || b.display_name || ""),
            undefined,
            { sensitivity: "base" }
          );
        }
        return bv - av;
      });

      return rows.map((row, idx) => ({
        ...row,
        rank: idx + 1,
      }));
    }

    // No game-level filtering/search required: use compact summary export.
    if (!streamerNeedsGameRows || !Array.isArray(activeStreamerGameRows)) {
      if (streamerRequiresFilteredDetails && !Array.isArray(activeStreamerGameRows)) return [];
      const filteredRows = summaryRows.filter((s) => {
        const topGameName = getTopGameName(s);
        const topGamesText = Array.isArray(s.top_games)
          ? s.top_games
              .map((g) => String(g.game_name || g.name || ""))
              .join(" ")
          : "";

        if (!q) return true;

        const userName = String(s.user_name || s.display_name || "").toLowerCase();
        const topGame = String(topGameName || "").toLowerCase();
        const topGames = String(topGamesText || "").toLowerCase();

        return (
          userName.includes(q) ||
          topGame.includes(q) ||
          topGames.includes(q)
        );
      });

      filteredRows.sort((a, b) => {
        if (sortBy === "peak") {
          return Number(b.peak_viewers || b.peak || 0) - Number(a.peak_viewers || a.peak || 0);
        }
        return Number(b.hours_watched || 0) - Number(a.hours_watched || 0);
      });

      return filteredRows;
    }

    // Game-level mode: recalculate Hours/Peak/Games from matching streamer-game rows.
    // This makes filters like "NBA" show NBA-only hours/peak/games, not all-game totals.
    const summaryByUser = new Map();
    for (const s of summaryRows) {
      const userKey = String(s.user_id || s.user_name || s.display_name || "").toLowerCase();
      if (userKey) summaryByUser.set(userKey, s);
    }

    const acc = new Map();

    for (const row of activeStreamerGameRows) {
      const gameName = getStreamerGameName(row);
      const gameKey = normalizeGameName(gameName);
      const userName = String(row.user_name || row.display_name || "").trim();
      const userKey = String(row.user_id || userName).toLowerCase();
      if (!userKey) continue;

      if (streamerAllowedGameNames) {
        if (!gameKey || !streamerAllowedGameNames.has(gameKey)) continue;
      }

      if (q) {
        const gameHit = String(gameName || "").toLowerCase().includes(q);
        const userHit = userName.toLowerCase().includes(q);
        if (!gameHit && !userHit) continue;
      }

      const hours = getStreamerRowHours(row);
      const peak = getStreamerRowPeak(row);
      if (hours <= 0 && peak <= 0) continue;

      let item = acc.get(userKey);
      if (!item) {
        const summary = summaryByUser.get(userKey) || summaryByUser.get(userName.toLowerCase()) || {};
        item = {
          rank: summary.rank || row.rank || 0,
          user_id: row.user_id || summary.user_id || "",
          user_name: userName || summary.user_name || summary.display_name || "",
          display_name: row.display_name || summary.display_name || userName || "",
          profile_image_url: row.profile_image_url || summary.profile_image_url || "",
          twitch_url:
            row.twitch_url ||
            summary.twitch_url ||
            (userName ? `https://www.twitch.tv/${encodeURIComponent(userName)}` : ""),
          hours_watched: 0,
          peak_viewers: 0,
          games_streamed: 0,
          top_games: [],
          _games: new Map(),
        };
        acc.set(userKey, item);
      }

      item.hours_watched += hours;
      item.peak_viewers = Math.max(item.peak_viewers || 0, peak);

      if (gameKey) {
        const prev = item._games.get(gameKey) || {
          game_id: row.game_id || "",
          game_name: gameName,
          box_art_url: getStreamerGameArt(row, gameArtByName),
          hours_watched: 0,
          peak_viewers: 0,
        };
        prev.hours_watched += hours;
        prev.peak_viewers = Math.max(prev.peak_viewers || 0, peak);
        if (!prev.box_art_url) prev.box_art_url = getStreamerGameArt(row, gameArtByName);
        item._games.set(gameKey, prev);
      }
    }

    const rows = Array.from(acc.values()).map((item) => {
      const topGames = Array.from(item._games.values()).sort(
        (a, b) =>
          Number(b.hours_watched || 0) - Number(a.hours_watched || 0) ||
          String(a.game_name || "").localeCompare(String(b.game_name || ""), undefined, { sensitivity: "base" })
      );

      return {
        ...item,
        games_streamed: topGames.length,
        top_game_name: topGames[0]?.game_name || "",
        top_game_box_art_url: topGames[0]?.box_art_url || "",
        top_games: topGames.slice(0, 10),
        _games: undefined,
      };
    });

    rows.sort((a, b) => {
      if (sortBy === "peak") {
        return Number(b.peak_viewers || 0) - Number(a.peak_viewers || 0);
      }
      return Number(b.hours_watched || 0) - Number(a.hours_watched || 0);
    });

    return rows.map((row, idx) => ({
      ...row,
      rank: idx + 1,
    }));
  }, [
    activeStreamerSummary,
    activeStreamerGameRows,
    liveStreamerRows,
    period,
    searchTerm,
    sortBy,
    streamerAllowedGameNames,
    streamerRequiresFilteredDetails,
    streamerNeedsGameRows,
    gameArtByName,
    streamerAvatarLookup,
  ]);

  const streamerTableLoading = period === "now"
    ? liveStreamerLoading && !liveStreamerRows.length
    : !activeStreamerSummary || (streamerRequiresFilteredDetails && !activeStreamerGameRows);
  const streamerTableLoadingMore = period === "now"
    ? liveStreamerLoading && liveStreamerRows.length > 0
    : (!streamerSummaryError && Array.isArray(activeStreamerSummary) && !activeStreamerSummaryComplete) ||
      (!streamerGamesError && streamerNeedsGameRows && Array.isArray(activeStreamerGameRows) && !activeStreamerGameRowsComplete);
  const streamerTableError = period === "now"
    ? liveStreamerError
    : streamerSummaryError || (streamerNeedsGameRows ? streamerGamesError : "");
  const initialDashboardLoading = viewMode === "streamers"
    ? streamerTableLoading && !displayStreamers.length && !streamerTableError
    : loading && !games.length && !cardsError;
  useEffect(() => {
    if (!initialDashboardLoading) setHasResolvedInitialDashboardLoad(true);
  }, [initialDashboardLoading]);

  const handleExportStreamersTableCsv = () => {
    if (!displayStreamers.length) return;

    const periodLabel = period === "7d" ? "7D" : period === "now" ? "Now" : "1D";
    const rows = displayStreamers.map((s, idx) => {
      const userName = s.user_name || s.display_name || "";
      const url =
        s.twitch_url ||
        (userName ? `https://www.twitch.tv/${encodeURIComponent(userName)}` : "");
      return {
        rank: s.rank || idx + 1,
        streamer: userName,
        current_viewers: period === "now" ? Number(s.current_viewers || s.currentViewers || s.hours_watched || 0) : "",
        hours_watched: period === "now" ? "" : Number(s.hours_watched || 0).toFixed(2),
        peak_viewers: s.peak_viewers || s.peak || 0,
        games_streamed: s.games_streamed || s.games || 0,
        top_game: getTopGameName(s),
        twitch_url: url,
        period: periodLabel,
      };
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `mindGAME - Twitch Streamers - ${periodLabel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Disable page scroll when in Market Mind view (full-screen bubbles)
  useEffect(() => {
    const lock = viewMode === "market";
    const { body, documentElement } = document;
    if (lock) {
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
      body.style.height = "calc(var(--mg-vh) * 100)";
      documentElement.style.height = "calc(var(--mg-vh) * 100)";
    } else {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    }
    return () => {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    };
  }, [viewMode]);

  // --- JSON streamer partitions loader (used for BOTH 1d and 7d) ---
  async function fetchStreamerDetailsForGame(periodKey, gameId) {
    return loadTwitchStreamerDetails(gameId, periodKey);
  }

  // open modal for cards/bubbles
  async function openGameModal(gameOrName) {
    const name =
      typeof gameOrName === "string"
        ? gameOrName
        : gameOrName?.name || "";
    if (!name) return;

    // Language detail is loaded lazily so period/view changes stay fast.
    setLanguageLoadRequested(true);

    // Find the game object so we have id + image
    const gameObj =
      typeof gameOrName === "string"
        ? games.find((g) => g.name === gameOrName)
        : gameOrName;

    const gameId = gameObj?.id; // Twitch game_id from the cards

    setSelectedGame(name);
    setSelectedGameImage(gameObj?.imageUrl || "");
    setModalLoading(true);
    setModalError("");

    try {
      if (gameId) {
        const rec = await fetchStreamerDetailsForGame(
          period,
          gameId.toString()
        );
        if (rec && Array.isArray(rec.streamers)) {
          const normalised = rec.streamers.map((s) => ({
            ...s,
            viewMinutes: s.viewMinutes ?? s.view_minutes ?? 0,
            peak: s.peak ?? s.peak_viewer_count ?? 0,
          }));

          // store per-game streamers
          setStreamersByGame(previous => new Map(previous).set(name, normalised));
        } else {
          setStreamersByGame(previous => new Map(previous).set(name, []));
        }
      } else {
        throw new Error("This game has no Twitch game ID.");
      }
    } catch (e) {
      console.error("Error loading streamer JSON", e);
      setModalError("Streamers could not be loaded. Close and reopen to retry.");
    } finally {
      setModalLoading(false);
    }
  }


  async function openMarketDrawer(gameOrName) {
    const name =
      typeof gameOrName === "string"
        ? gameOrName
        : gameOrName?.name || "";
    if (!name) return;

    // Language detail is loaded lazily so period/view changes stay fast.
    setLanguageLoadRequested(true);

    const gameObj =
      typeof gameOrName === "string"
        ? games.find((g) => g.name === gameOrName)
        : gameOrName;

    const gameId = gameObj?.id;

    setSelectedGame(null);
    setSelectedGameImage(gameObj?.imageUrl || "");
    setModalLoading(true);
    setModalError("");

    try {
      if (gameId) {
        const rec = await fetchStreamerDetailsForGame(period, gameId.toString());
        const normalised =
          rec && Array.isArray(rec.streamers)
            ? rec.streamers.map((s) => ({
                ...s,
                viewMinutes: s.viewMinutes ?? s.view_minutes ?? 0,
                peak: s.peak ?? s.peak_viewer_count ?? 0,
              }))
            : [];

        setStreamersByGame((prev) => {
          const next = new Map(prev);
          next.set(name, normalised);
          return next;
        });
      } else {
        throw new Error("This game has no Twitch game ID.");
      }
    } catch (e) {
      console.error("Error loading Market Mind drawer streamer JSON", e);
      setModalError("Streamers could not be loaded. Close and reopen to retry.");
    } finally {
      setModalLoading(false);
    }
  }

  // Minutes/viewers + peak/streamers per language (used for filtering)
  const minutesFor = (g) => {
    if (!selectedLang) return g.viewMinutes || 0;
    const arr = languagesByGame.get(g.name) || [];
    const hit = arr.find(
      (l) => canonicalLang(l.language) === selectedLang
    );
    if (!hit) return 0;
    return period === "now"
      ? Number(hit.currentViewers || 0)
      : Number(hit.viewMinutes || 0);
  };
  const peakFor = (g) => {
    if (!selectedLang) return g.peak || 0;
    const arr = languagesByGame.get(g.name) || [];
    const hit = arr.find(
      (l) => canonicalLang(l.language) === selectedLang
    );
    if (!hit) return 0;
    return period === "now"
      ? Number(hit.activeStreamers || 0)
      : Number(hit.peak || 0);
  };

  // filtering & sorting
     const filtered = useMemo(() => {
  const q = searchTerm.toLowerCase();

  const hasPublisherFilter = selectedPublishers.size > 0;
  const hasDevFilter = selectedDevelopers.size > 0;
  const hasGenreFilter = selectedGenres.size > 0;
  const hasPlatformFilter = selectedPlatforms.size > 0;
 const hasDateFilter =
  selectedDateRange[0] != null && selectedDateRange[1] != null;
 const hasKeywordFilter = selectedKeywords.size > 0;
 const hasFranchiseFilter = selectedFranchises.size > 0;


  return games.filter((g) => {
    if (hidden.has(g.name)) return false;

    if (q && !g.name.toLowerCase().includes(q)) return false;

    // If any IGDB filter is active, look up that game's IGDB metadata once
    if (
  hasPublisherFilter ||
  hasDevFilter ||
  hasGenreFilter ||
  hasPlatformFilter ||
  hasDateFilter ||
  hasKeywordFilter ||
  hasFranchiseFilter
) {
  const meta = getGameMeta(g, igdbMetaById, twitchMetaById);
  if (!meta) return false;

  const pubs = splitMulti(meta.publishers || "");
  const devs = splitMulti(meta.developers || "");
  const genres = splitMulti(meta.genres || "");
  const plats = splitMulti(meta.platforms || "");
  const keywords = splitMulti(meta.keywords || "");
  const franchise = (meta.franchises || "").trim();
  const rd =
    safeDate(meta.first_release_date) || safeDate(meta.release_date);

  if (hasPublisherFilter) {
    if (!pubs.some((p) => selectedPublishers.has(p))) return false;
  }
  if (hasDevFilter) {
    if (!devs.some((d) => selectedDevelopers.has(d))) return false;
  }
  if (hasGenreFilter) {
    if (!genres.some((gn) => selectedGenres.has(gn))) return false;
  }
  if (hasPlatformFilter) {
    if (!plats.some((pl) => selectedPlatforms.has(pl))) return false;
  }
  if (hasKeywordFilter) {
    if (!keywords.some((kw) => selectedKeywords.has(kw))) return false;
  }
  if (hasFranchiseFilter) {
    if (!franchise || !selectedFranchises.has(franchise)) return false;
  }
  if (hasDateFilter) {
    const [from, to] = selectedDateRange;
    if (!rd || rd < from || rd > to) return false;
  }
}


    return true;
  });
}, [
  games,
  searchTerm,
  hidden,
  selectedPublishers,
  selectedDevelopers,
  selectedGenres,
  selectedPlatforms,
  selectedDateRange,
  selectedKeywords,
  selectedFranchises,
  igdbMetaById,
  twitchMetaById,
]);


const displayGames = useMemo(() => {
  const copy = filtered.map((g) => {
    const meta = getGameMeta(g, igdbMetaById, twitchMetaById);
    const releaseRaw = meta?.first_release_date || meta?.release_date || "";
    const isNewRelease = isReleasedInLast7Days(releaseRaw);

    if (period === "now") {
      const langViewers = selectedLang ? minutesFor(g) : Number(g.currentViewers || 0);
      const langStreamers = selectedLang ? peakFor(g) : Number(g.activeStreamers || 0);
      return {
        ...g,
        isNewRelease,
        displayCurrentViewers: langViewers,
        displayStreamers: langStreamers,
        displayMinutes: langViewers,
        // In Now mode, the secondary sort is active streamers, not historical peak.
        displayPeak: langStreamers,
      };
    }

    return {
      ...g,
      isNewRelease,
      displayMinutes: minutesFor(g),
      displayPeak: peakFor(g),
    };
  });

  const visible = newReleasesOnly ? copy.filter((g) => g.isNewRelease) : copy;

  visible.sort((a, b) =>
    sortBy === "peak"
      ? b.displayPeak - a.displayPeak
      : b.displayMinutes - a.displayMinutes
  );

  return visible;
}, [filtered, sortBy, selectedLang, languagesByGame, period, igdbMetaById, twitchMetaById, newReleasesOnly]);


    // total minutes for visible games (after filters) and baseline across all games
  const { totalMinutes, baselineMinutes } = useMemo(() => {
    const visibleTotal = displayGames.reduce(
      (sum, g) => sum + (g.displayMinutes || 0),
      0
    );

    const baselineTotal = games.reduce(
      (sum, g) => sum + (g.viewMinutes || 0),
      0
    );

    return { totalMinutes: visibleTotal, baselineMinutes: baselineTotal };
  }, [displayGames, games]);

  const totalHours = useMemo(
    () =>
      period === "now"
        ? Number(totalMinutes || 0).toLocaleString()
        : fmtHours(toHours(totalMinutes)),
    [totalMinutes, period]
  );

  const shareOfHours = useMemo(() => {
    if (!baselineMinutes || baselineMinutes <= 0) return "0.00";
    const pct = (totalMinutes / baselineMinutes) * 100;

    // Show exactly 100, otherwise XX.XX
    if (Math.abs(pct - 100) < 0.005) return "100";
    return pct.toFixed(2);
  }, [totalMinutes, baselineMinutes]);


  const modalStreamers = useMemo(() => {
  const name = selectedGame || selectedBubble?.data?.name || null;
  if (!name) return [];
  return streamersByGame.get(name) || [];
}, [selectedGame, selectedBubble, streamersByGame]);

  const selectedLanguages = useMemo(() => {
    const name = selectedGame || selectedBubble?.data?.name || null;
    if (!name) return [];
    return languagesByGame.get(name) || [];
  }, [selectedGame, selectedBubble, languagesByGame]);

  const selectedChartLanguages = useMemo(() => {
    const name = selectedChartGame?.name || null;
    if (!name) return [];
    return languagesByGame.get(name) || [];
  }, [selectedChartGame, languagesByGame]);

  const selectedChartStreamers = useMemo(() => {
    const name = selectedChartGame?.name || null;
    if (!name) return [];
    return streamersByGame.get(name) || [];
  }, [selectedChartGame, streamersByGame]);

  const selectedChartMeta = useMemo(() => {
    return getGameMeta(selectedChartGame, igdbMetaById, twitchMetaById);
  }, [selectedChartGame, igdbMetaById, twitchMetaById]);

useEffect(() => {
  if (selectedChartGame?.name) {
    setLanguageLoadRequested(true);
  }
}, [selectedChartGame?.name]);

  useEffect(() => {
    if (!pendingSavedChartState) return;
    if (loading || !games.length) return;

    const savedChartGames = Array.isArray(pendingSavedChartState.chartGames)
      ? pendingSavedChartState.chartGames
      : [];

    if (!savedChartGames.length || viewMode !== "cards") {
      setPendingSavedChartState(null);
      return;
    }

    let cancelled = false;

    (async () => {
      const restoredBase = [];

      for (const saved of savedChartGames) {
        const savedGameId = String(saved.gameId || "");
        const savedName = String(saved.name || "");
        const match = games.find((g) => {
          if (savedGameId && String(g.id) === savedGameId) return true;
          if (savedName && normalizeGameName(g.name) === normalizeGameName(savedName)) return true;
          return false;
        });

        if (!match?.id || !match?.name) continue;

        const gameId = String(match.id);
        const meta = getGameMeta(match, igdbMetaById, twitchMetaById);
        const wideArtUrl =
          meta?.wide_art_url ||
          meta?.wide_art ||
          meta?.hero_art_url ||
          match.imageUrl ||
          "";

        restoredBase.push({
          name: match.name,
          gameId,
          wideArtUrl,
          imageUrl: match.imageUrl || "",
          igdbId: match.igdbId || saved.igdbId || "",
          history: [],
          loading: true,
        });
      }

      if (cancelled) return;

      if (!restoredBase.length) {
        setChartGames([]);
        setSelectedChartGameId(null);
        setPendingSavedChartState(null);
        return;
      }

      setChartGames(restoredBase);

      const restoredWithHistory = await Promise.all(
        restoredBase.map(async (g) => {
          try {
            const history = await loadTwitchChartHistoryByGameId(g.gameId, twitchHistoryGranularity);
            return { ...g, history, loading: false };
          } catch (e) {
            console.error("Saved View Twitch history restore failed:", e);
            return { ...g, loading: false };
          }
        })
      );

      if (cancelled) return;

      setChartGames(restoredWithHistory);

      const savedSelected = pendingSavedChartState.selectedChartGameId
        ? String(pendingSavedChartState.selectedChartGameId)
        : "";
      const selectedExists = restoredWithHistory.some(
        (g) => String(g.gameId) === savedSelected
      );
      const nextSelected = selectedExists
        ? savedSelected
        : restoredWithHistory[0]?.gameId || null;

      setSelectedChartGameId(nextSelected);

      const incomingHighlighted = pendingSavedChartState.twHighlightedSeries;
      const restoredHighlighted = Array.isArray(incomingHighlighted)
        ? incomingHighlighted
        : incomingHighlighted
        ? [incomingHighlighted]
        : null;
      setTwHighlightedSeries(
        restoredHighlighted || (restoredWithHistory[0] ? [restoredWithHistory[0].name] : null)
      );

      const start = Number(pendingSavedChartState.twWinStart);
      const end = Number(pendingSavedChartState.twWinEnd);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        twHasInitWindow.current = true;
        setTwWinStart(Math.max(0, Math.floor(start)));
        setTwWinEnd(Math.max(0, Math.floor(end)));
      } else {
        twHasInitWindow.current = false;
      }

      setPendingSavedChartState(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pendingSavedChartState,
    loading,
    games,
    viewMode,
    igdbMetaById,
    twitchMetaById,
  ]);

  useEffect(() => {
    if (!chartGames.length) {
      if (selectedChartGameId !== null) setSelectedChartGameId(null);
      return;
    }

    const exists = chartGames.some((g) => String(g.gameId) === String(selectedChartGameId));
    if (!selectedChartGameId || !exists) {
      setSelectedChartGameId(chartGames[0].gameId);
      setTwHighlightedSeries([chartGames[0].name]);
    }
  }, [chartGames, selectedChartGameId]);

  useEffect(() => {
    if (!chartGames.length) return;

    let cancelled = false;

    setChartGames((prev) =>
      prev.map((g) => ({
        ...g,
        history: [],
        loading: true,
      }))
    );

    (async () => {
      const refreshed = await Promise.all(
        chartGames.map(async (g) => {
          try {
            const history = await loadTwitchChartHistoryByGameId(
              g.gameId,
              twitchHistoryGranularity
            );
            return { gameId: g.gameId, history, loading: false };
          } catch (e) {
            console.error("Twitch chart history reload failed:", e);
            return { gameId: g.gameId, history: [], loading: false };
          }
        })
      );

      if (cancelled) return;

      const byId = new Map(refreshed.map((g) => [String(g.gameId), g]));
      setChartGames((prev) =>
        prev.map((g) => {
          const next = byId.get(String(g.gameId));
          return next ? { ...g, history: next.history, loading: next.loading } : g;
        })
      );

      twHasInitWindow.current = false;
    })();

    return () => {
      cancelled = true;
    };
  }, [twitchHistoryGranularity]);

  useEffect(() => {
    setChartSidebarError("");
    setChartSidebarLoading(false);
    if (!selectedChartGame?.name || !selectedChartGame?.gameId) {
      loadedSidebarKey.current = null;
      return;
    }
    const sidebarKey = `${period}:${selectedChartGame.gameId}`;
    let cancelled = false;

    (async () => {
      try {
        setChartSidebarLoading(loadedSidebarKey.current !== sidebarKey);

        const rec = await fetchStreamerDetailsForGame(period, String(selectedChartGame.gameId));
        const normalised =
          rec && Array.isArray(rec.streamers)
            ? rec.streamers.map((s) => ({
                ...s,
                viewMinutes: s.viewMinutes ?? s.view_minutes ?? 0,
                peak: s.peak ?? s.peak_viewer_count ?? 0,
              }))
            : [];

        if (cancelled) return;

        loadedSidebarKey.current = sidebarKey;
        setStreamersByGame((prev) => {
          const next = new Map(prev);
          next.set(selectedChartGame.name, normalised);
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Error loading chart sidebar streamer JSON", e);
          setChartSidebarError("Streamers could not be loaded. The chart is still available.");
        }
      } finally {
        if (!cancelled) setChartSidebarLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedChartGame?.gameId, selectedChartGame?.name, period, liveRefreshNonce, streamerRetryNonce]);

  // color scale & bubbles
  // Market Mind should respect the selected sort metric:
  // - Sort by Hours/Viewers: bubble size + color use displayMinutes
  //   (hours watched for 1D/7D, current viewers for Now)
  // - Sort by Peak/Streamers: bubble size + color use displayPeak
  //   (peak viewers for 1D/7D, active streamers for Now)
  const marketMetricLabel = twitchMarketMetricLabel(period, sortBy);

  const marketGames = useMemo(() => games.filter(game => !hidden.has(game.name)).map(game => {
    const meta = getGameMeta(game, igdbMetaById, twitchMetaById);
    return {
      ...game,
      release_date: meta?.first_release_date || meta?.release_date || "",
      publisher: meta?.publishers || "",
      genres: splitMulti(meta?.genres || ""),
    };
  }), [games, hidden, igdbMetaById, twitchMetaById]);
  const hasActiveFilters = !!(searchTerm.trim() || selectedLang || newReleasesOnly ||
    selectedPublishers.size || selectedDevelopers.size || selectedGenres.size || selectedPlatforms.size ||
    selectedKeywords.size || selectedFranchises.size || selectedDateRange[0] || selectedDateRange[1]);
  const handlePeriodChange = nextPeriod => {
    if (nextPeriod === period) return;
    setGames([]);
    setLoading(true);
    setSelectedBubble(null);
    setPeriod(nextPeriod);
  };


  const statNumberStyle = {
    color: "#fff",
    fontWeight: 900,
    fontSize: twitchLayout.statFontSize,
    lineHeight: 1,
  };

  const sidebarActionBtnStyle = {
    flex: 1,
    height: 36,
    minHeight: 36,
    background: "#050505",
    color: "rgba(187,187,187,0.75)",
    border: "1px solid #444",
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 400,
    fontFamily: "inherit",
    lineHeight: 1.3,
    textAlign: "center",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    transition: "border-color 0.15s ease, color 0.15s ease",
  };

  return (
    <div
      className="mg-jura-dashboard mg-twitch-page"
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      {filtersOpen && (
        <div
          className="mg-steam-filter-backdrop"
          onMouseEnter={() => {
            if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
          }}
          onClick={() => setFiltersOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Twitch filter workspace (top popover, same pattern as Steam) */}
      <div
        className="mg-twitch-filter-popover"
        onMouseEnter={() => {
          if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
          setFiltersOpen(true);
        }}
        onMouseLeave={() => setFiltersOpen(false)}
        style={{
          position: "fixed",
          top: "calc(var(--mg-vh) * 18)",
          left: "50%",
          height: "auto",
          maxHeight: "min(calc(var(--mg-vh) * 72), 760px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "40px 24px 24px",
          background: "rgba(8,10,12,0.97)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 18,
          width: "min(980px, calc(calc(var(--mg-vw) * 100) - 48px))",
          minWidth: 0,
          flexShrink: 0,
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: "12px 16px",
          zIndex: 10000,
          opacity: filtersOpen ? 1 : 0,
          visibility: filtersOpen ? "visible" : "hidden",
          pointerEvents: filtersOpen ? "auto" : "none",
          transform: filtersOpen ? "translate(-50%, 0)" : "translate(-50%, -6px)",
          boxShadow: "0 22px 60px rgba(0,0,0,0.82)",
          transition: "opacity 150ms ease, transform 150ms ease, visibility 150ms ease",
        }}
      >
        <div style={{ display: "contents" }}>
{/* Search games */}
<style>
  {`
    .mg-twitch-search {
      font-size: 16px;               /* match dropdown label size */
      line-height: 1.3;
      font-family: inherit;
      color: #bbb;                /* same text color as dropdown */
      transition: border-color 0.15s ease;
    }
    .mg-twitch-search::placeholder {
      color: #bbb;
      opacity: 0.75;                 /* same softness as dropdown placeholder */
    }
    .mg-twitch-search:hover,
    .mg-twitch-search:focus {
      border-color: rgba(255,255,255,0.82) !important;
      outline: none;
    }
    .mg-twitch-sidebar-action:hover:not(:disabled) {
      border-color: rgba(255,255,255,0.82) !important;
      color: rgba(187,187,187,0.75) !important;
      cursor: pointer !important;
    }
    .mg-twitch-saved-wrap button:hover {
      border-color: rgba(255,255,255,0.82) !important;
    }
    .mg-twitch-saved-wrap,
    .mg-twitch-saved-wrap * {
      cursor: pointer !important;
    }
    .mg-twitch-filter-wrap,
    .mg-twitch-filter-wrap * {
      cursor: pointer !important;
    }
    .mg-twitch-filter-wrap:hover * {
      border-color: rgba(255,255,255,0.82) !important;
    }
    .mg-twitch-date-filter {
      color: rgba(187,187,187,0.75);
      cursor: pointer !important;
    }
    .mg-twitch-date-filter * {
      font-family: inherit;
      cursor: pointer !important;
      border-radius: 6px !important;
    }
    .mg-twitch-date-filter:hover,
    .mg-twitch-date-filter:hover * {
      border-color: rgba(255,255,255,0.82) !important;
    }
  `}
</style>

{/* Search now lives in the header (Steam-style expanding search) */}

{/* Saved Views – same formatting/behavior as Steam */}
<div className="mg-twitch-saved-wrap" style={{ gridColumn: 3, gridRow: 1 }}>
<SavedViewsManager
  type="twitch"
  currentFilters={{
    searchTerm,
    selectedLang,
    publishers: Array.from(selectedPublishers),
    developers: Array.from(selectedDevelopers),
    genres: Array.from(selectedGenres),
    platforms: Array.from(selectedPlatforms),
    keywords: Array.from(selectedKeywords),
    franchises: Array.from(selectedFranchises),
    dateRange: selectedDateRange,
    sortBy,
    marketShape,
    marketFavorites,
    marketSplitView,
    period,
    viewMode,
    newReleases: newReleasesOnly,

    // Persist the full workspace, not just the top-level filters.
    hiddenGames: Array.from(hidden),
    chartGames: chartGames.map((g) => ({
      gameId: g.gameId,
      name: g.name,
      igdbId: g.igdbId || "",
    })),
    selectedChartGameId,
    twitchValueMode,
    twitchCumulative,
    twitchHistoryGranularity,
    twWinStart,
    twWinEnd,
    twHighlightedSeries,
    chartSidebarPct,
    sidebarCollapsed,
  }}
  onApplyFilters={(filters) => {
    const nextViewMode = filters.viewMode || "cards";
    const nextPeriod = filters.period || "now";

    setSearchTerm(filters.searchTerm ?? "");
    setSelectedLang(filters.selectedLang ?? "");
    setNewReleasesOnly(!!filters.newReleases);

    setSelectedPublishers(new Set(filters.publishers || []));
    setSelectedDevelopers(new Set(filters.developers || []));
    setSelectedGenres(new Set(filters.genres || []));
    setSelectedPlatforms(new Set(filters.platforms || []));
    setSelectedKeywords(new Set(filters.keywords || []));
    setSelectedFranchises(new Set(filters.franchises || []));
    setHidden(new Set(filters.hiddenGames || []));

    // Date range: normalize possible string dates into real Date objects
    const incomingRange = filters.dateRange || [null, null];
    const normalizedRange = [...incomingRange];
    if (normalizedRange[0] && typeof normalizedRange[0] === "string") {
      normalizedRange[0] = new Date(normalizedRange[0]);
    }
    if (normalizedRange[1] && typeof normalizedRange[1] === "string") {
      normalizedRange[1] = new Date(normalizedRange[1]);
    }
    setSelectedDateRange(normalizedRange);

    setSortBy(filters.sortBy || "hours");
    setMarketShape(filters.marketShape === "bubbles" ? "bubbles" : "squares");
    setMarketFavorites((filters.marketFavorites || []).filter(value => ["upcoming", "recent"].includes(value)));
    setMarketSplitView(!!filters.marketSplitView);
    setTwitchValueMode(filters.twitchValueMode || "hours");
    setTwitchCumulative(!!filters.twitchCumulative);
    setTwitchHistoryGranularity(filters.twitchHistoryGranularity === "monthly" ? "monthly" : "daily");
    setChartSidebarPct(
      Number.isFinite(Number(filters.chartSidebarPct))
        ? Math.max(24, Math.min(48, Number(filters.chartSidebarPct)))
        : 33
    );
    setSidebarCollapsed(!!filters.sidebarCollapsed);

    const incomingHighlighted = filters.twHighlightedSeries;
    setTwHighlightedSeries(
      Array.isArray(incomingHighlighted)
        ? incomingHighlighted
        : incomingHighlighted
        ? [incomingHighlighted]
        : null
    );

    const savedChartGames = Array.isArray(filters.chartGames)
      ? filters.chartGames
      : [];

    // Queue chart restoration so it runs after the correct period's games load.
    setPendingSavedChartState({
      chartGames: savedChartGames,
      selectedChartGameId: filters.selectedChartGameId || null,
      twWinStart: filters.twWinStart,
      twWinEnd: filters.twWinEnd,
      twHighlightedSeries: incomingHighlighted,
    });

    if (nextViewMode === "market") {
      handleViewMarket();
    } else if (nextViewMode === "streamers") {
      handleViewStreamers();
    } else {
      handleViewCards();
    }

    if (!savedChartGames.length || nextViewMode !== "cards") {
      setChartGames([]);
      setSelectedChartGameId(null);
    }

    // Changing period should re-fetch data just like clicking the pill.
    if (nextPeriod !== period) {
      setGames([]);
      setLoading(true);
      hasLoadedGamesRef.current = false;
      hasLoadedLiveDetailsRef.current = false;
      if (nextViewMode === "market") {
        setSelectedBubble(null);
      }
      setPeriod(nextPeriod);
    }
  }}
  onClearFilters={() => {
    // Match Steam behavior: full reset of filters + chart/workspace.
    handleResetGames();
    setPendingSavedChartState(null);
    setSidebarCollapsed(false);
    setChartSidebarPct(33);
    setTwitchValueMode("hours");
    setTwitchCumulative(false);
    setTwitchHistoryGranularity("daily");
    setPeriod("now");
    setSortBy("hours");
  }}
/>
</div>

  <button
    className="mg-twitch-sidebar-action"
    onClick={handleResetGames}
    style={{ ...sidebarActionBtnStyle, gridColumn: 3, gridRow: 2, borderRadius: 8 }}
  >
    ↻ Reset All
  </button>

  <CsvExportButton
    className="mg-twitch-sidebar-action"
    onClick={handleExportAllGamesCsv}
    disabled={viewMode === "streamers" ? (streamerTableLoading || streamerTableLoadingMore || !displayStreamers.length) : (loading || !displayGames.length)}
    title={viewMode === "streamers" ? "Export visible streamers to CSV" : "Export all visible games to CSV"}
    style={{
      ...sidebarActionBtnStyle,
      gridColumn: 3,
      gridRow: 3,
      borderRadius: 8,
      cursor: viewMode === "streamers" ? (streamerTableLoading || streamerTableLoadingMore || !displayStreamers.length ? "default" : "pointer") : (loading || !displayGames.length ? "default" : "pointer"),
      opacity: viewMode === "streamers" ? (streamerTableLoading || streamerTableLoadingMore || !displayStreamers.length ? 0.5 : 1) : (loading || !displayGames.length ? 0.5 : 1),
    }}
  >
    ⬇ Export CSV
  </CsvExportButton>

{/* New Releases (gold-highlighted games, same rule as the cards) */}
<button
  type="button"
  onClick={() => setNewReleasesOnly((v) => !v)}
  aria-pressed={newReleasesOnly}
  style={{
    gridColumn: 1,
    gridRow: 1,
    width: "100%",
    minHeight: 36,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid",
    boxSizing: "border-box",
    background: newReleasesOnly ? "#3fa9f5" : "#050505",
    borderColor: newReleasesOnly ? "#3fa9f5" : "#444",
    color: newReleasesOnly ? "#000" : "rgba(187,187,187,0.75)",
    cursor: "pointer",
    lineHeight: 1.3,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  New Releases
</button>

{/* Filter divider */}
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "12px 0 6px 0",
  }}
>
  <div
    style={{
      flex: 1,
      height: 1,
      background: "rgba(255,255,255,0.12)",
    }}
  />

  <div
    style={{
      color: "#777",
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    }}
  >
    Filters
  </div>

  <div
    style={{
      flex: 1,
      height: 1,
      background: "rgba(255,255,255,0.12)",
    }}
  />
</div>

                       {/* Language filter */}
        <div
          className="mg-twitch-filter-wrap"
          style={{ gridColumn: 2, gridRow: 1 }}
          onMouseDown={() => setLanguageLoadRequested(true)}
          onFocus={() => setLanguageLoadRequested(true)}
        >
          <VirtualizedDropdown
            placeholder="All Languages"
            options={languageOptions}
            isMulti={false}
            isClearable
            closeMenuOnSelect
            blurInputOnSelect
            maxMenuWidth={340}
            isDisabled={false}
            value={
              selectedLang
                ? languageOptions.find(
                    (opt) => opt.value === selectedLang
                  ) || null
                : null
            }
            onChange={(opt) => {
              setSelectedLang(opt ? opt.value : "");
            }}
          />
        </div>



                           {/* Publisher filter (IGDB) */}
        <div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 2 }}>
          <VirtualizedDropdown
            placeholder="All Publishers"
            options={publisherOptions}
            isMulti
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            hideSelectedOptions={false}
            maxMenuWidth={340}
            isDisabled={!igdbReady || !publisherOptions.length}
            value={Array.from(selectedPublishers).map((p) => ({
              label: p,
              value: p,
            }))}
            onChange={(opts) => {
              const values = (opts || []).map((o) => o.value);
              setSelectedPublishers(new Set(values));
            }}
          />
        </div>



               {/* Developer filter (IGDB) */}
        <div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 3 }}>
          <VirtualizedDropdown
            placeholder="All Developers"
            options={developerOptions}
            isMulti
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            hideSelectedOptions={false}
            maxMenuWidth={340}
            isDisabled={!igdbReady || !developerOptions.length}
            value={Array.from(selectedDevelopers).map((d) => ({
              label: d,
              value: d,
            }))}
            onChange={(opts) => {
              const values = (opts || []).map((o) => o.value);
              setSelectedDevelopers(new Set(values));
            }}
          />
        </div>

{/* Franchise filter (IGDB) */}
<div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 4 }}>
  <VirtualizedDropdown
    placeholder="All Franchises"
    options={franchiseOptions}
    isMulti
    closeMenuOnSelect={false}
    blurInputOnSelect={false}
    hideSelectedOptions={false}
    maxMenuWidth={340}
    isDisabled={!igdbReady || !franchiseOptions.length}
    value={Array.from(selectedFranchises).map((f) => ({
      label: f,
      value: f,
    }))}
    onChange={(opts) => {
      const values = (opts || []).map((o) => o.value);
      setSelectedFranchises(new Set(values));
    }}
  />
</div>

{/* Keyword filter (IGDB) */}
<div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 5 }}>
  <VirtualizedDropdown
    placeholder="All Keywords"
    options={keywordOptions}
    isMulti
    closeMenuOnSelect={false}
    blurInputOnSelect={false}
    hideSelectedOptions={false}
    maxMenuWidth={340}
    isDisabled={!igdbReady || !keywordOptions.length}
    value={Array.from(selectedKeywords).map((k) => ({
      label: k,
      value: k,
    }))}
    onChange={(opts) => {
      const values = (opts || []).map((o) => o.value);
      setSelectedKeywords(new Set(values));
    }}
  />
</div>

               {/* Genre filter (IGDB) */}
        <div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 6 }}>
          <VirtualizedDropdown
            placeholder="All Genres"
            options={genreOptions}
            isMulti
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            hideSelectedOptions={false}
            maxMenuWidth={340}
            isDisabled={!igdbReady || !genreOptions.length}
            value={Array.from(selectedGenres).map((g) => ({
              label: g,
              value: g,
            }))}
            onChange={(opts) => {
              const values = (opts || []).map((o) => o.value);
              setSelectedGenres(new Set(values));
            }}
          />
        </div>


              {/* Platform filter (IGDB) */}
        <div className="mg-twitch-filter-wrap" style={{ gridColumn: 2, gridRow: 7 }}>
          <VirtualizedDropdown
            placeholder="All Platforms"
            options={platformOptions}
            isMulti
            closeMenuOnSelect={false}
            blurInputOnSelect={false}
            hideSelectedOptions={false}
            maxMenuWidth={340}
            isDisabled={!igdbReady || !platformOptions.length}
            value={Array.from(selectedPlatforms).map((p) => ({
              label: p,
              value: p,
            }))}
            onChange={(opts) => {
              const values = (opts || []).map((o) => o.value);
              setSelectedPlatforms(new Set(values));
            }}
          />
        </div>


{/* Release date (IGDB first_release_date) */}
<div className="mg-twitch-date-filter" style={{ gridColumn: 2, gridRow: 8 }}>
  <DateSlider
    range={releaseDateRange}
    selected={selectedDateRange}
    onChange={setSelectedDateRange}
    onClear={() => setSelectedDateRange([null, null])}
    forceClose={!filtersOpen}
  />
</div>

          </div>

  <div className="steam-filter-divider steam-filter-divider-popular" aria-hidden="true">Popular</div>
  <div className="steam-filter-divider steam-filter-divider-filters" aria-hidden="true">Filters</div>
  <div className="steam-filter-divider steam-filter-divider-tools" aria-hidden="true">Tools</div>
      </div>

      {/* Main */}
      <MindshareHeaderControls visible targetId="twitch-header-controls" savedControlsId="twitch-header-saved-controls"
        centerSearchTargetId="twitch-header-search" searchAriaLabel="Search Twitch games"
        ariaLabel="Twitch views and filters" viewMode={viewMode}
        onViewChange={next => {
          if (next === "cards") handleViewCards();
          if (next === "market") handleViewMarket();
          if (next === "streamers") handleViewStreamers();
        }}
        views={[
          { mode: "cards", label: "Cards", Icon: GalleryHorizontalEnd },
          { mode: "market", label: "Market Mind", Icon: MarketMindIcon },
          { mode: "streamers", label: "Streamers", Icon: Users },
        ]}
        filtersOpen={filtersOpen} onOpenFilters={() => setFiltersOpen(open => !open)}
        filtersActive={hasActiveFilters} toolsExpanded={hasActiveFilters}
        searchTerm={searchTerm} onSearchChange={setSearchTerm} />
      {headerToolsTarget && createPortal(
        <button type="button" className="ms-header-icon" onClick={handleResetGames} aria-label="Reset all" data-tooltip="Reset all"><RotateCcw size={16} /></button>,
        headerToolsTarget
      )}
      {headerStatsTarget && !initialDashboardLoading && createPortal(
          <div className="tw-header-stats mg-dashboard-content" role="group" aria-label="Twitch statistics">
            <span className="tw-header-stat">
              <strong>
                {viewMode === "streamers"
                  ? displayStreamers.length.toLocaleString()
                  : displayGames.length.toLocaleString()}
              </strong>
              <span>{viewMode === "streamers" ? "Streamers" : "Games Streamed"}</span>
            </span>
            <span className="tw-header-stat">
              <strong>
                {viewMode === "streamers"
                  ? (period === "now"
                      ? fmtInt(displayStreamers.reduce(
                          (sum, s) => sum + Number(s.current_viewers || s.currentViewers || s.hours_watched || 0),
                          0
                        ))
                      : fmtHours(displayStreamers.reduce(
                          (sum, s) => sum + Number(s.hours_watched || 0),
                          0
                        )))
                  : `${totalHours}`}
              </strong>
              <span>{period === "now" ? "Current Viewers" : "Total Hours Watched"}</span>
            </span>
            <span className="tw-header-stat tw-header-share">
              <strong>
                {viewMode === "streamers" ? (period === "now" ? "Live" : "Top 75k") : `${shareOfHours}%`}
              </strong>
              <span>
                {viewMode === "streamers" ? "Streamer Export" : (period === "now"
                  ? "of All Live Viewers"
                  : "of All Hours Watched")}
              </span>
            </span>
          </div>,
        headerStatsTarget
      )}
      <div
        ref={mainRef}
        style={{
          flex: 1,
          padding: twitchLayout.mainPadding,
          position: "relative",
          height: viewMode === "market" ? "calc(var(--mg-vh) * 100)" : "auto",
          overflow: viewMode === "market" ? "hidden" : "visible",
        }}
      >
    {cardsError && <div role="alert" style={{ position: "relative", zIndex: 10, padding: 12, color: "#ff9b9b", background: "#111" }}>
      {cardsError} <button type="button" onClick={() => setLiveRefreshNonce(n => n + 1)}>Retry cards</button>
    </div>}

        {/* Twitch compare chart (daily or monthly history) */}
      {viewMode === "cards" && chartGames.length > 0 && (
  <div
  ref={chartExportRef}
  style={{
    marginBottom: 20,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #222",
    background: "#050505",
    padding: 0,
    position: "relative",
    height: `min(${twitchChartHeight}px, calc(calc(var(--mg-vh) * 100) - 108px))`,
    minHeight: 520,
    display: "flex",
    flexDirection: "column",
  }}
>

<div
  data-export-hide="true"
  style={{
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,
    display: "flex",
    gap: 8,
  }}
>
  <button
    type="button"
    disabled={exportingChart}
  onClick={() => {
  const gameLabel =
    chartGames.length === 1
      ? safeFilePart(chartGames[0]?.name, "Game")
      : "Multi-Game";

  exportChartPanel({
    longEdgePx: 3840,
    fileName: `Twitch ${gameLabel} ${twitchHistoryGranularity === "monthly" ? "Monthly" : "Daily"} Chart.png`,
  });
}}

    title="Export chart image"
    aria-label="Export chart image (4K)"
    style={{
      width: 34,
      height: 34,
      borderRadius: 999,
      border: "1px solid rgba(255,255,255,0.25)",
      background: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(6px)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: exportingChart ? "default" : "pointer",
      opacity: exportingChart ? 0.55 : 1,
      padding: 0,
    }}
  >
    {/* little “download/export” icon */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3v10m0 0l4-4m-4 4l-4-4"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 17v3h14v-3"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
</div>

    {/* 🔹 Background wide art (only when exactly one game in chart) */}
    {twitchChartBgArt && (
      <>
            <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${twitchChartBgArt})`,
        // 🔹 1920×1080 hero art: fill container, light zoom
        backgroundSize: "cover",
        backgroundPosition: "50% 10%",
        backgroundRepeat: "no-repeat",
        transformOrigin: "center center",
        transform: "scale(1.02)", 
        filter: "saturate(115%)",
        opacity: 0.22,
        zIndex: 0,
      }}
    />

            <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background:
          // 1) cyan-ish radial glow at the very top
          "radial-gradient(120% 140% at 50% -20%, rgba(63,169,245,0.18), transparent 45%)," +
          // 2) vertical fade: bottom darkest → top lightest
          "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.65) 55%, rgba(0,0,0,0.35) 100%)",
        // IMPORTANT: remove multiply so it doesn't re-darken everything
        mixBlendMode: "normal",
        zIndex: 0,
      }}
    />

      </>
    )}

    {/* Top-to-bottom brightness fade for chart background */}
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        // bottom darkest → top brightest (still fairly dark)
        background:
          "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.4) 100%)",
        zIndex: 0,
      }}
    />

    {/* Foreground content (chips + chart) */}
    <div style={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        ref={chartSplitRef}
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "hidden",
          background: "rgba(0,0,0,0.10)",
        }}
      >
        <div
          style={{
            width: `${chartSidebarPct}%`,
            minWidth: 320,
            maxWidth: "52%",
            flex: "0 0 auto",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <ChartGameSidebar
            game={selectedChartGame}
            gameImageUrl={selectedChartGame?.imageUrl || ""}
            streamers={selectedChartStreamers}
            languages={selectedChartLanguages}
            selectedLang={selectedLang}
            loading={chartSidebarLoading}
            error={chartSidebarError}
            onRetry={() => setStreamerRetryNonce(n => n + 1)}
            gameMeta={selectedChartMeta}
            period={period}
          />
        </div>

        <div
          data-export-hide="true"
          onPointerDown={handleChartResizeStart}
          title="Drag to resize"
          style={{
            width: 14,
            flex: "0 0 14px",
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))",
            borderLeft: "1px solid rgba(255,255,255,0.06)",
            borderRight: "1px solid rgba(255,255,255,0.06)",
            zIndex: 3,
          }}
        >
          <div
            style={{
              width: 4,
              height: 54,
              borderRadius: 999,
              background: "rgba(63,169,245,0.55)",
              boxShadow: "0 0 14px rgba(63,169,245,0.25)",
            }}
          />
        </div>

        <div style={{ flex: "1 1 0", minWidth: 0, padding: "12px 12px 12px 12px", display: "flex", flexDirection: "column", minHeight: 0 }}>
              {/* Chart controls live above the chart only, not above the sidebar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          padding: "0 48px 10px 0",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "transparent",
          flex: "0 0 auto",
        }}
      >
        {/* Daily frontend JSON / full monthly history toggle */}
        <div
          style={{
            display: "inline-flex",
            border: "1px solid #2a2a2a",
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            onClick={() => setTwitchHistoryGranularity("daily")}
            style={{
              ...chartPillStyle(twitchHistoryGranularity === "daily"),
              borderRadius: 0,
              borderTop: "none",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "1px solid #2a2a2a",
            }}
          >
            Daily History
          </button>
          <button
            type="button"
            onClick={() => setTwitchHistoryGranularity("monthly")}
            style={{
              ...chartPillStyle(twitchHistoryGranularity === "monthly"),
              borderRadius: 0,
              border: "none",
            }}
          >
            Monthly History
          </button>
        </div>

        {/* Hours Watched / Peak Viewers / Channels Streamed toggle */}
        <div
          style={{
            display: "inline-flex",
            border: "1px solid #2a2a2a",
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          <button
            type="button"
            onClick={() => setTwitchValueMode("hours")}
            style={{
              ...chartPillStyle(twitchValueMode === "hours"),
              borderRadius: 0,
              borderTop: "none",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "1px solid #2a2a2a",
            }}
          >
            Hours Watched
          </button>
          <button
            type="button"
            onClick={() => setTwitchValueMode("peak")}
            style={{
              ...chartPillStyle(twitchValueMode === "peak"),
              borderRadius: 0,
              borderTop: "none",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "1px solid #2a2a2a",
            }}
          >
            Peak Viewers
          </button>
          <button
            type="button"
            onClick={() => setTwitchValueMode("channels")}
            style={{
              ...chartPillStyle(twitchValueMode === "channels"),
              borderRadius: 0,
              border: "none",
            }}
          >
            Channels Streamed
          </button>
        </div>

        {/* Daily / Cumulative toggle (Hours only) */}
        {twitchValueMode === "hours" && (
          <div
            style={{
              display: "inline-flex",
              border: "1px solid #2a2a2a",
              borderRadius: 7,
              overflow: "hidden",
              marginLeft: 2,
            }}
          >
            <button
              type="button"
              onClick={() => setTwitchCumulative(false)}
              style={{
                ...chartPillStyle(!twitchCumulative),
                height: 30,
                padding: "0 10px",
                borderRadius: 0,
                borderTop: "none",
                borderBottom: "none",
                borderLeft: "none",
                borderRight: "1px solid #2a2a2a",
                fontSize: 11,
              }}
            >
              Daily
            </button>
            <button
              type="button"
              onClick={() => setTwitchCumulative(true)}
              style={{
                ...chartPillStyle(twitchCumulative),
                height: 30,
                padding: "0 10px",
                borderRadius: 0,
                border: "none",
                fontSize: 11,
              }}
            >
              Cumulative
            </button>
          </div>
        )}

      </div>






      {/* Chart body */}
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 360,
            overflow: "hidden",
          }}
        >
        {twitchChartData.length === 0 && chartGames.some(game => game.loading) ? (
          <TwitchHistoryChartSkeleton />
        ) : twitchChartData.length === 0 ? (
          <div role="status" style={{ height: "100%", minHeight: 360, display: "grid", placeItems: "center", color: "#b7c3ca", fontSize: 13 }}>
            {twitchHistoryGranularity === "daily"
              ? "No daily history available in the clean date window (February 1, 2026 onward, up to the last 12 months)."
              : "No monthly history available for the selected games."}
          </div>
        ) : (
      <ResponsiveContainer width="100%" height="100%">
  <AreaChart
    data={twitchRenderData}
    margin={{ top: 34, right: 80, left: 8, bottom: 0 }}
  >

    <CartesianGrid stroke="#222" />
    <XAxis
      dataKey="x"
      stroke="#ddd"
      tick={{ fontSize: 11 }}
      tickFormatter={fmtDateShort}
    />
    <YAxis
      stroke="#ddd"
      width={112}
      tick={{ fontSize: 12 }}
      tickFormatter={(v) => formatTwitchAxisValue(v)}
    />
        {/* Steam-style tooltip: games sorted by value desc for that date */}
    <Tooltip content={renderTwitchTooltip} />


    {/* Area series – Ctrl/Cmd-click multi-select like Steam */}
    {sortedChartGames.map((g, idx) => {
      const color = getTwitchChartColor(g.gameId, idx);

      const selectedNames = Array.isArray(twHighlightedSeries)
        ? twHighlightedSeries
        : twHighlightedSeries
        ? [twHighlightedSeries]
        : [];

      const isDimmed =
        selectedNames.length > 0 && !selectedNames.includes(g.name);

      return (
        <Area
          key={g.gameId}
          type="monotone"
          dataKey={g.gameId}
          stroke={color}
          fill={color}
          fillOpacity={isDimmed ? 0.08 : 0.25}
          strokeWidth={isDimmed ? 1.4 : 2}
          strokeOpacity={isDimmed ? 0.4 : 1}
          dot={false}
          name={g.name}
          isAnimationActive={false}
          style={{ cursor: "pointer" }}
          onClick={() => toggleTwitchSeriesHighlight(g.name)}
        />
      );
    })}



    {/* Dotted current-month estimate line for Monthly Hours */}
    {twitchHistoryGranularity === "monthly" && twitchValueMode === "hours" &&
      sortedChartGames.map((g, idx) => {
        const color = getTwitchChartColor(g.gameId, idx);
        const selectedNames = Array.isArray(twHighlightedSeries)
          ? twHighlightedSeries
          : twHighlightedSeries
          ? [twHighlightedSeries]
          : [];
        const isDimmed = selectedNames.length > 0 && !selectedNames.includes(g.name);
        return (
          <Area
            key={`${g.gameId}-estimate-line`}
            type="monotone"
            dataKey={`${g.gameId}__estimate`}
            stroke={color}
            fill={color}
            fillOpacity={0}
            strokeWidth={isDimmed ? 1.4 : 2.4}
            strokeOpacity={isDimmed ? 0.45 : 1}
            strokeDasharray="6 6"
            dot={false}
            activeDot={false}
            connectNulls={false}
            name={`${g.name} estimate`}
            isAnimationActive={false}
            style={{ cursor: "pointer" }}
            onClick={() => toggleTwitchSeriesHighlight(g.name)}
          />
        );
      })}

    {/* Peak labels at top-most point, Steam-style */}
   {(() => {
  if (!twitchRenderData.length || !sortedChartGames.length) return null;

  const peaks = {};
  for (const row of twitchRenderData) {
    for (const key of Object.keys(row)) {
      if (key === "x") continue;
      const baseKey = key.endsWith("__estimate")
        ? key.replace(/__estimate$/, "")
        : key;
      const v = Number(row[key]);
      if (!Number.isFinite(v)) continue;
      if (!peaks[baseKey] || v > peaks[baseKey].y) {
        peaks[baseKey] = { x: row.x, y: v };
      }
    }
  }

  const selectedNames = Array.isArray(twHighlightedSeries)
    ? twHighlightedSeries
    : twHighlightedSeries
    ? [twHighlightedSeries]
    : [];

  return sortedChartGames
    .filter((g) => peaks[g.gameId])
    .map((g, seriesIndex) => {
      const { x, y } = peaks[g.gameId];
      const labelName = g.name;
      const color = getTwitchChartColor(g.gameId, seriesIndex);

      const isDimmed =
        selectedNames.length > 0 && !selectedNames.includes(labelName);

      const labelColor = isDimmed ? "#777" : color || "#fff";

      const renderPeakLabel = (props) => {
        const px = Number(props.x ?? props.viewBox?.x ?? 0);
        const py = Number(props.y ?? props.viewBox?.y ?? 0);
        const chartBox = props.viewBox || {};
        const rightEdge = Number(chartBox.x || 0) + Number(chartBox.width || 0);
        const nearRightEdge = rightEdge > 0 && px > rightEdge - 130;
        const labelX = nearRightEdge ? px - 8 : px + 8;
        const labelY = Math.max(14, py - 6);

        return (
          <text
            x={labelX}
            y={labelY}
            textAnchor={nearRightEdge ? "end" : "start"}
            fill={labelColor}
            fontSize={12}
            fontWeight={700}
            style={{
              cursor: "pointer",
              pointerEvents: "auto",
              userSelect: "none",
              paintOrder: "stroke",
              stroke: "rgba(0,0,0,0.75)",
              strokeWidth: 3,
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleTwitchSeriesHighlight(labelName);
            }}
          >
            {labelName}
          </text>
        );
      };

      return (
        <ReferenceDot
          key={`peak-${g.gameId}`}
          x={x}
          y={y}
          r={5}
          ifOverflow="extendDomain"
          fill={isDimmed ? "#444" : color}
          stroke="#000"
          label={renderPeakLabel}
        />
      );
    });
})()}



  </AreaChart>
</ResponsiveContainer>
        )}
        </div>
      </div>

      {/* Steam-style period slider */}
      {twitchChartData.length > 1 && (
        <div style={{ marginTop: 8, marginLeft: 112, marginRight: 80, flex: "0 0 auto" }}>
          <style>{`
            .mg-twitch-range {
              -webkit-appearance: none;
              appearance: none;
            }
            .mg-twitch-range::-webkit-slider-runnable-track {
              height: 18px;
              background: transparent;
              border: none;
            }
            .mg-twitch-range::-moz-range-track {
              height: 18px;
              background: transparent;
              border: none;
            }
            .mg-twitch-range::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 28px;
              height: 14px;
              border-radius: 7px;
              border: 1px solid rgba(63,169,245,0.95);
              background: #3fa9f5;
              cursor: grab;
              box-shadow: 0 0 10px rgba(63,169,245,0.35);
              pointer-events: auto;
              margin-top: 2px;
            }
            .mg-twitch-range::-moz-range-thumb {
              width: 28px;
              height: 14px;
              border-radius: 7px;
              border: 1px solid rgba(63,169,245,0.95);
              background: #3fa9f5;
              cursor: grab;
              box-shadow: 0 0 10px rgba(63,169,245,0.35);
              pointer-events: auto;
            }
          `}</style>


          <div
            style={{
              position: "relative",
              height: 6,
              background: "#1a1a1a",
              borderRadius: 4,
            }}
          >
            <div
              style={{
                position: "absolute",
                left: `${
                  (100 * twWinStart) /
                  (twitchChartData.length - 1)
                }%`,
                width: `${
                  (100 * (twWinEnd - twWinStart)) /
                  Math.max(1, twitchChartData.length - 1)
                }%`,
                top: 0,
                bottom: 0,
                background: "#3fa9f5",
                opacity: 0.35,
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              position: "relative",
              height: 18,
              marginTop: -12,
            }}
          >
            <input
              className="mg-twitch-range"
              type="range"
              min={0}
              max={Math.max(0, twitchChartData.length - 1)}
              value={twWinStart}
              onChange={(e) =>
                setTwWinStart(
                  Math.min(
                    parseInt(e.target.value, 10),
                    twWinEnd - 1
                  )
                )
              }
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: 18,
                background: "transparent",
                margin: 0,
                pointerEvents: "none",
              }}
            />
            <input
              className="mg-twitch-range"
              type="range"
              min={0}
              max={Math.max(0, twitchChartData.length - 1)}
              value={twWinEnd}
              onChange={(e) =>
                setTwWinEnd(
                  Math.max(
                    parseInt(e.target.value, 10),
                    twWinStart + 1
                  )
                )
              }
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: 18,
                background: "transparent",
                margin: 0,
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      )}

      {/* Game chips (legend) - moved below period slider */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginTop: 10,
          marginLeft: 112,
          marginRight: 80,
          paddingBottom: 8,
          flex: "0 0 auto",
        }}
      >
        {sortedChartGames.map((g, idx) => {
          const isSelected = String(g.gameId) === String(selectedChartGame?.gameId || "");
          const chipColor = getTwitchChartColor(g.gameId, idx);
          return (
            <button
              key={g.gameId}
              type="button"
              onClick={() => {
                setSelectedChartGameId(g.gameId);
                setTwHighlightedSeries([g.name]);
              }}
              title="Select this game for Languages & Streamers"
              style={{
                ...chartChipStyle,
                border: `1px solid ${chipColor}`,
                background: isSelected
                  ? `linear-gradient(180deg, ${chipColor}40, rgba(0,0,0,0.52))`
                  : `linear-gradient(180deg, ${chipColor}22, rgba(0,0,0,0.42))`,
                color: "#fff",
                boxShadow: isSelected ? `0 0 14px ${chipColor}3D` : "none",
              }}
            >
              <span
                style={{
                  maxWidth: 160,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {g.name}
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFromChart(g.gameId);
                }}
                style={{
                  opacity: 0.8,
                  paddingLeft: 2,
                  cursor: "pointer",
                }}
              >
                ✕
              </span>
            </button>
          );
        })}
      </div>

        </div>
      </div>
    </div>

    <button
      type="button"
      data-export-hide="true"
      onPointerDown={handleChartHeightResizeStart}
      title="Drag to resize chart height"
      aria-label="Drag to resize chart height"
      style={{
        position: "absolute",
        right: 6,
        bottom: 6,
        zIndex: 6,
        width: 20,
        height: 20,
        border: "none",
        background: "transparent",
        color: "#8a96a3",
        display: "grid",
        placeItems: "center",
        cursor: "ns-resize",
        touchAction: "none",
        padding: 0,
      }}
    >
      <Grip size={14} aria-hidden="true" />
    </button>
  </div>
)}



        {(!initialDashboardLoading || hasResolvedInitialDashboardLoad) && (
          <div className="mg-dashboard-content">
            {viewMode !== "market" && <MGSectionHeader index={chartGames.length && viewMode === "cards" ? "02" : "01"} accent="paper">
              Ranked {viewMode === "streamers" ? "streamers" : "game"}{" "}
              {viewMode === "streamers" && period === "now" ? "viewers" : <MGHeadingToggle
                label="Twitch ranking metric" value={sortBy} onChange={setSortBy} angledSelection
                options={[{ value: "hours", label: twitchMarketMetricLabel(period, "hours") },
                  { value: "peak", label: twitchMarketMetricLabel(period, "peak") }]} />}
              <span className="mg-twitch-ranking-divider" aria-hidden="true">|</span>
              <MGHeadingToggle label="Twitch ranking period" value={period} onChange={handlePeriodChange} angledSelection
                options={[{ value: "now", label: "Live" }, { value: "1d", label: "1 Day" }, { value: "7d", label: "7 Day" }]} />
            </MGSectionHeader>}
          </div>
        )}
        <DashboardContent label="Twitch" loading={initialDashboardLoading}>
        {viewMode === "streamers" ? (
          <StreamersTable
            streamers={displayStreamers}
            loading={streamerTableLoading}
            error={streamerTableError}
            loadingMore={streamerTableLoadingMore}
            onRetry={() => setStreamerRetryNonce(n => n + 1)}
            period={period}
            onExport={handleExportStreamersTableCsv}
            gameArtByName={gameArtByName}
          />
        ) : viewMode === "market" ? (
  <div
    ref={marketMindExportRef}
    className={selectedBubble ? "tw-market-has-drawer" : ""}
    style={{
      position: "absolute",
      inset: 0,
      border: "none",
      borderRadius: 0,
      background:
        "radial-gradient(1200px 600px at 50% -10%, rgba(40,144,195,0.08), transparent 55%)",
    }}
  >
        <TwitchMarketMind games={marketGames} filtered={displayGames} period={period}
          onPeriodChange={handlePeriodChange} sortBy={sortBy} onSortChange={setSortBy}
          shape={marketShape} onShapeChange={setMarketShape} favorites={marketFavorites}
          onFavoritesChange={setMarketFavorites} splitView={marketSplitView} onSplitChange={setMarketSplitView}
          hasActiveFilters={hasActiveFilters} loading={loading} activeId={selectedBubble?.data?.id} onSelect={node => {
            if (selectedBubble?.data?.id === node.data.id) {
              setSelectedBubble(null);
              setSelectedGameImage("");
            } else {
              setSelectedBubble(node);
              openMarketDrawer(node.data.name);
            }
          }} />
        <button
          type="button"
          data-export-hide="true"
          disabled={exportingMarketMind}
          onClick={() =>
            exportMarketMindPanel({
              longEdgePx: 3840,
              fileName: `Twitch Market Mind - ${marketMetricLabel}.png`,
            })
          }
          title="Export Market Mind image"
          aria-label="Export Market Mind image"
          style={{
            position: "absolute",
            right: 16,
            bottom: 24,
            zIndex: 140,
            height: 36,
            minWidth: 116,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.24)",
            background: "rgba(0,0,0,0.58)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: exportingMarketMind ? "default" : "pointer",
            opacity: exportingMarketMind ? 0.55 : 1,
            padding: 0,
            backdropFilter: "blur(7px)",
            boxShadow: "0 10px 26px rgba(0,0,0,0.45)",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden style={{ marginRight: 7 }}>
            <path d="M12 3v10m0 0l4-4m-4 4l-4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 17v3h14v-3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 900 }}>Export PNG</span>
        </button>

        {selectedBubble && (() => {
          const drawerName = selectedBubble?.data?.name || "";
          const drawerGame = games.find((g) => g.name === drawerName) || null;
          const drawerMeta = getGameMeta(drawerGame, igdbMetaById, twitchMetaById);

          return (
            <div
              data-export-hide="true"
              style={{
                position: "absolute",
                top: 78,
                right: 16,
                bottom: 24,
                maxHeight: "calc(calc(var(--mg-vh) * 100) - 116px)",
                width: `min(${marketDrawerWidth}px, calc(100% - 32px))`,
                zIndex: 40,
                border: "1px solid rgba(255,255,255,0.13)",
                borderRadius: 14,
                overflow: "hidden",
                background:
                  "linear-gradient(180deg, rgba(8,10,12,0.96), rgba(3,3,4,0.94))",
                boxShadow: "0 24px 70px rgba(0,0,0,0.72)",
                backdropFilter: "blur(10px)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ flex: "1 1 auto", minHeight: 0, boxSizing: "border-box" }}>
                <ChartGameSidebar
                  game={{
                    name: drawerName,
                    gameId: drawerGame?.id || drawerName,
                    igdbId: drawerGame?.igdbId || "",
                  }}
                  gameImageUrl={selectedGameImage || drawerGame?.imageUrl || ""}
                  streamers={streamersByGame.get(drawerName) || []}
                  languages={languagesByGame.get(drawerName) || []}
                  selectedLang={selectedLang}
                  loading={modalLoading}
                  error={modalError}
                  onRetry={() => openMarketDrawer(drawerName)}
                  gameMeta={drawerMeta}
                  period={period}
                  onClose={() => {
                    setSelectedBubble(null);
                    setSelectedGameImage("");
                  }}
                />
              </div>
            </div>
          );
        })()}
  </div>
      ) : (
       <TwitchCardGrid
  games={displayGames}
  loading={loading}
  selectedLang={selectedLang}
  onCardClick={(g) => openGameModal(g)}
  onRemove={(name) =>
    setHidden((prev) => new Set(prev).add(name))
  }
  onToggleChart={handleToggleChartGame}
  chartGamesSet={chartGamesSet}
  isNow={period === "now"}
/>

      )}

        </DashboardContent>
      </div>

            {selectedGame && (
        <StreamersModal
          gameName={selectedGame || selectedBubble?.data?.name}
          gameImageUrl={selectedGameImage}
          streamers={(() => {
            const name =
              selectedGame || selectedBubble?.data?.name || "";
            return modalStreamers.length
              ? modalStreamers
              : streamersByGame.get(name) || [];
          })()}
          languages={(() => {
            const name =
              selectedGame || selectedBubble?.data?.name || "";
            return languagesByGame.get(name) || [];
          })()}
          selectedLang={selectedLang}
          loading={modalLoading}
          error={modalError}
          gameMeta={(() => {
            const name =
              selectedGame || selectedBubble?.data?.name || "";
            const g = games.find((x) => x.name === name);
            return getGameMeta(g, igdbMetaById, twitchMetaById);
          })()}
          period={period}
          onClose={() => {
            setSelectedGame(null);
            setSelectedBubble(null);
            setSelectedGameImage("");
          }}
        />
      )}

    </div>
  );
}
